/**
 * Entry point: wires core logic to the browser platform and renderer.
 *
 * Note the direction of the dependencies - main.ts knows about everything,
 * but core/ knows about nothing. That asymmetry is the whole architecture.
 */

import { World, GROW_REACH, FORGET_REACH } from "./core/world/world.js";
import {
  PlayerController, PLAYER_WIDTH, PLAYER_HEIGHT, EMPTY_INPUT,
} from "./core/player/controller.js";
import {
  isSolid, isFluid, isLava, isLeaf, isWadeable, isWater, isTargetable, isOpaque, isReplaceable, needsSupport, isDoor, withStoveLit,
  wallTorchId, isWallTorch, wallTorchFacing, type DoorFacing,
  UPRIGHT_LOGS, logForFace,
  isShutter, isBed, isRushnyk, isStove, isChest, rushnykId, stoveId, chestId,
  blueprintBuilding, isNaturalGround,
  TREBUCHET_BASE, trebuchetId, isTrebuchet, trebuchetFacing,
  flagId, PIRATE_FLAG,
  WOOD_STAIRS_BASE, STONE_STAIRS_BASE,
  stairsForFace,
  wallId,
  rollDrops, BlockId,
  isGate, gateId, cellFill, graniteWallId, MAZANKA_SLAB, GRANITE_SLAB,
  isCrop, nextCropStage, doorState,
  type DoorWood, type StoveFinish,
} from "./core/block/registry.js";
import {
  growCrops, growSaplings, spreadGrass, canTill, canPlant, GROWTH_PASS_SECONDS, type FarmAction,
} from "./core/world/growth.js";
import {
  placedBlock, itemLabel, itemUse, doorItemWood, stoveItemFinish,
  shutterItemWood, tamesDogs, PlainItemId,
} from "./core/block/items.js";
import {
  facingFromLook, canPlaceDoor, placeDoor, removeDoor,
  canPlaceShutter, placeShutter, togglePanel, facingFromFace, stairFacingFor,
} from "./core/world/doors.js";
import {
  canPlaceBed, placeBed, removeBed, wakeSpot, sleep, headCell,
} from "./core/world/beds.js";
import { skinById } from "./core/player/skins.js";
import { raycastVoxels, raycastBeside } from "./core/physics/raycast.js";
import { boxOverlapsVoxel } from "./core/physics/voxelCollision.js";
import { Inventory } from "./core/inventory/inventory.js";
import { GRID_SLOTS, noticeNewIngredients } from "./core/crafting/recipes.js";
import { CASTLE_POSTS, CASTLE_THRONE, castleCell } from "./core/world/castle.js";
import { Music, TRACKS, GAP_SHORT } from "./core/audio/music.js";
import { stationsNear } from "./core/crafting/stations.js";
import { RecipeBook } from "./ui/recipeBook.js";
import { RecipeNudge } from "./ui/recipeNudge.js";
import { QuestBook } from "./ui/questBook.js";
import { questsFromSave } from "./core/quest/quests.js";
import { Tooltip } from "./ui/tooltip.js";
import { InventoryUI } from "./ui/inventoryUI.js";
import { VitalsUI } from "./ui/vitalsUI.js";
import { SkinPicker, loadSavedSkin } from "./ui/skinPicker.js";
import { SkinStrip } from "./ui/skinStrip.js";
import { ToneToggle } from "./ui/toneToggle.js";
import { OverlayToggle } from "./ui/overlayToggle.js";
import { OverlaySelect } from "./ui/overlaySelect.js";
import {
  MAPS, SOLO_MAPS, mapById, liveSlotFor, ADVENTURE, DEFAULT_MAP,
} from "./core/world/maps.js";
import { NetworkUI } from "./ui/networkUI.js";
import { probeServer, askAccount, askHosting } from "./platform/netProbe.js";
import { lastKnock } from "./platform/peer.js";
import {
  Connection, takeJoinRequest, joinOnReload, savedName, savedPassword,
  takeHoldRequest, holdOnReload, nameTag,
  type JoinedWorld, type WarState,
} from "./platform/net.js";
import {
  holdWorld, joinHeld, heldAlready, heldStore, forgetHeld, type Holding,
} from "./platform/hosting.js";
import { type OrcState, type CreatureKind } from "./core/net/protocol.js";
import { Trail } from "./core/net/smooth.js";
import {
  explodes, blastCells, blastHurt, CLIMB_SECONDS, GATHER_SECONDS, roundAt,
  KING_MIN_Y,
} from "./core/world/kingHill.js";
import {
  oneBlockDrop, oneBlockCount, oneBlockSaid, oneBlockBirth,
} from "./core/world/oneBlock.js";
import { VOICE, voiceFor, voiceUrl } from "./core/audio/voice.js";
import { spawnForItem } from "./core/entity/spawn.js";
import { Fire } from "./core/world/fire.js";
import { Burning, SCORCH_DAMAGE } from "./core/entity/burning.js";
import {
  makeFirebolt, stepFirebolts, BOLT_SPEED, COLD_SPEED,
  WIND_SECONDS, trebuchetThrow,
  aimedThrow,
  COLD_DAMAGE,
  type Firebolt,
} from "./core/entity/firebolt.js";
import {
  ui as uiText, parseLang, pick, LANG_LABELS, CONTROLS,
  type Lang, type UiKey,
} from "./core/text/lang.js";
import { say, type LineKey } from "./core/text/tone.js";
import {
  makeVillager, mirrorVillager, tickVillagers, faceToward, villagerInCell,
  VILLAGER_HEIGHT, SPEECH_SECONDS, stepVillagers,
  nameForKind, skinForKind, lineForKind, type Villager,
} from "./core/entity/villager.js";
import {
  household, doorCell, porchCell, matCell, roomBox,
} from "./core/world/village.js";
import { CHEST_SLOTS } from "./core/world/containers.js";
import { CAMP_KIT } from "./core/world/campKit.js";
import { walkLine } from "./core/world/walk.js";
import { GATE_MOUTH, ROAD_STOPS_SHORT } from "./core/world/walk.js";
import {
  makeArrow, stepArrows, ARROW_DAMAGE, type Arrow,
} from "./core/entity/arrow.js";
import {
  makeOrc, mirrorOrc, stepOrcs, rollOrcDrops, ORC_BREEDS, WILD_FOES, CASTLE_FOES,
  huntable,
  nightOnly, dayOnly,
  ORC_WIDTH, ORC_HEIGHT, type Orc, type OrcKind,
} from "./core/entity/orc.js";
import {
  RaidClock, LairClock, MAX_ORCS, RAID_INTERVAL,
  HARDCORE_FIRST_RAID, HARDCORE_MAX_ORCS, HARDCORE_PEACE,
} from "./core/world/raid.js";
import {
  waterToScoop, oilToScoop, poolClosesOver,
} from "./core/world/scoop.js";
import { raise, buildingNamed, builtInside } from "./core/world/blueprint.js";
import {
  makeBeast, mirrorBeast, stepBeasts, spook, rollBeastDrop,
  GUARD_REACH, BREEDS, ZMII, type Beast, type BeastKind,
} from "./core/entity/beast.js";
import {
  strayedFromAll, footroom, whatToPutDown,
  LIFE_KEEP, FOES_KEEP,
  type Wood, type Person,
} from "./core/entity/restock.js";
import {
  makeBoat, mirrorBoat, rowBoat, stepAshore, BOAT_DRAFT,
  type Boat, type Water, type Oars,
} from "./core/entity/boat.js";
import {
  makeTank, mirrorTank, driveTank, shellStruck, blastShare,
  TANK_FLOOR, TANK_RELOAD, SHELL_SPEED,
  type Tank, type Levers,
} from "./core/entity/tank.js";
import {
  makeShell, stepShells, type Shell,
} from "./core/entity/shell.js";
import {
  makeFish, mirrorFish, stepFish, startle, KARAS_DROP, type Fish,
} from "./core/entity/fish.js";
import { FishingLine } from "./core/entity/angling.js";
import { pickEntity } from "./core/entity/aim.js";
import {
  makeDragon, stepDragons, hurtDragon, BREATH_DAMAGE, type Dragon,
} from "./core/entity/dragon.js";
import { LAIR_CELL, HALL_DEPTH } from "./core/world/lair.js";
import {
  makePickup, scatterDrops, stepPickups, mergePickups, restingCell,
  PICKUP_LIFETIME, DEATH_LIFETIME, type Pickup,
} from "./core/entity/pickup.js";
import {
  Drone, NO_DRONE_INPUT, DRONE_LOAD, DRONE_SIGHT, type DroneInput,
} from "./core/entity/drone.js";
import { Vitals, PLAYER_MAX_HP, fallDamage, LAVA_DAMAGE } from "./core/entity/vitals.js";
import { STRIKE_SECONDS, strikeProgress } from "./core/player/strike.js";
import { type Hand, HAND_KEY, parseHand } from "./core/player/hand.js";
import { tellPlayed } from "./platform/played.js";
import { NameTags, type NameTag } from "./ui/nameTags.js";

/**
 * How far off a creature will tell you its name.
 *
 * Further than arm's length: "what is that thing over there" is the question
 * a name answers, and one you can only read once you are close enough to hit
 * it has answered too late.
 */
const NAME_RANGE = 24;
import { itemDamage, itemArmour } from "./core/block/items.js";
import {
  ARMOUR_SLOTS, damageAfterArmour, reliefPercent,
} from "./core/entity/armour.js";
import {
  Digging, blowsToBreak, requiredStrength, BLOW_SECONDS,
} from "./core/block/breaking.js";
import { requiredTool, type ToolKind } from "./core/block/tools.js";
import { itemTool, spentForm } from "./core/block/items.js";
import { captureSave, restoreSave } from "./core/save/session.js";
import {
  encodeSave, decodeSave, describeSave, SaveFormatError, WORLDGEN_VERSION,
  restampWorldgen,
} from "./core/save/saveGame.js";
import { downloadSave, pickSaveFile, suggestedFilename } from "./platform/saveFile.js";
import { IndexedDbStore } from "./platform/saveStore.js";
import { SavesUI } from "./ui/savesUI.js";
import {
  slotKey, LIVE_SLOT as SAVE_SLOT, BACKUP_SLOT,
} from "./core/save/slots.js";
import type { SaveStore } from "./platform/saveStore.js";
import type { SaveGame } from "./core/save/saveGame.js";
import { DayNightCycle } from "./core/world/daynight.js";
import { Renderer, CAMERA_MODES } from "./render/scene.js";
import { Input } from "./platform/input.js";
import { Audio } from "./platform/audio.js";
import { soundForBlock } from "./core/audio/materials.js";

/** Simulation runs at a fixed rate so movement feels the same at any fps. */
const TICK_RATE = 60;
const TICK_DT = 1 / TICK_RATE;
/** Cap catch-up steps so a background tab doesn't spiral on resume. */
const MAX_TICKS_PER_FRAME = 5;

/**
 * Who the player is, to a dog, when there is nobody else.
 *
 * Online a person is a client id on a server; alone there is no server and
 * no ids, so a собака that belongs to you belongs to number nought. It is
 * written down rather than spelled `0` in three places because it is the
 * same nought every time and it means something.
 */
const ALONE = 0;

/*
 * The world, in chunks.
 *
 * Wide enough for two islands and the sea between them, and deep enough that
 * what is under the water is worth digging into: 128 tall with the waterline
 * at 53 leaves fifty-odd blocks of rock beneath it.
 */

/** Cycled by the T key. 1x is the real 20-minute day; the rest are for us. */
const TIME_SPEEDS = [1, 20, 100, 0] as const;

/** Fall below this and you are put back at spawn rather than falling forever. */
const VOID_Y = -24;

/** How far you can reach to break or place, in blocks. */
const REACH = 6;

/** How often the game saves itself, in milliseconds. */
const AUTOSAVE_INTERVAL = 30_000;




/** Which line to say when the wrong thing is in hand. */
const TOOL_REFUSAL: Record<ToolKind, LineKey> = {
  pickaxe: "need-pickaxe",
  shovel: "need-shovel",
  sabre: "need-sabre",
  hoe: "need-hoe",
  // Never said: nothing in the world requires a сокира - it makes wood
  // quicker, it is not what makes wood possible. Here because the record
  // covers every tool there is, and a tool with no line would not compile.
  axe: "need-axe",
};

const CAMERA_LABEL: Record<string, UiKey | null> = {
  // "1st" is the same word either way, so it needs no phrase of its own.
  first: null,
  third: "cam-back",
  thirdFront: "cam-front",
};

/** Read the saved game, if there is one and it is readable. */
/**
 * Read the saved game, if there is one, and if it still means anything.
 *
 * A save is the seed plus the differences the player made to the terrain it
 * grew. When the generator changes, those differences are differences against
 * a world that no longer exists: the modified chunks come back as slabs of
 * somewhere else standing in the middle of the new land. It looks like a bug
 * and it is not one - it is what a diff against the wrong base looks like.
 *
 * So a save from an older generator is not loaded. It is kept, moved to the
 * backup slot rather than overwritten, and the game starts fresh.
 */
async function loadSave(
  store: SaveStore, slot: string,
): Promise<{ save: SaveGame | null; staleTerrain: boolean; unreadable: boolean }> {
  let bytes: ArrayBuffer | null = null;
  try {
    bytes = await store.read(slot);
    if (bytes === null) return { save: null, staleTerrain: false, unreadable: false };
    const save = decodeSave(bytes);

    if (save.worldgen !== WORLDGEN_VERSION) {
      console.info(
        `save was grown by terrain ${save.worldgen}, this game grows ` +
        `${WORLDGEN_VERSION}: starting fresh, the old one is in the backup slot`,
      );
      await store.write(BACKUP_SLOT, bytes);
      return { save: null, staleTerrain: true, unreadable: false };
    }
    return { save, staleTerrain: false, unreadable: false };
  } catch (error) {
    /*
     * The save is there and we cannot read it. This is the dangerous case.
     *
     * The comment that used to sit here said the save was kept rather than
     * overwritten - and nothing made that true. The game started a fresh
     * world, and thirty seconds later the autosave wrote it straight over the
     * one that would not open. By the time anybody noticed, the world was
     * gone and the backup slot was empty, because this path - unlike the
     * worldgen one above - never copied anything into it.
     *
     * So now it does both: the bytes go to the backup slot, and the caller is
     * told to stop autosaving. Whatever is on that disk belongs to the player
     * and is not ours to write over while we do not even understand it.
     */
    console.error(
      error instanceof SaveFormatError
        ? `could not read the save: ${error.message}`
        : error,
    );
    if (bytes !== null) {
      try {
        await store.write(BACKUP_SLOT, bytes);
      } catch (writeError) {
        console.error("could not even copy the unreadable save", writeError);
      }
    }
    return { save: null, staleTerrain: false, unreadable: bytes !== null };
  }
}

async function main(): Promise<void> {
  const overlay = document.getElementById("overlay") as HTMLDivElement;

  /*
   * What the screen is currently saying, and until when.
   *
   * Declared first, before anything that could want to say something.
   * `notify` is a function declaration and hoists; these are `let` and do
   * not - so a message sent before this line threw `Cannot access
   * 'saveNotice' before initialization` and took the whole page with it.
   *
   * Which is exactly what happened to the one message that most needs to
   * work: "could not join that server". A failed join is meant to drop you
   * into single player with a line explaining why - the promise at the top
   * of platform/net.ts - and instead it was a blank screen. Being unable to
   * play with somebody is a disappointment; being unable to play at all
   * because of it is a bug, and it was one for as long as the message
   * existed, because until now nothing had ever failed to join in a test.
   */
  let saveNotice = "";
  let saveNoticeUntil = 0;

  // Two tones of voice, one game. The 18+ switch on the pause screen picks
  // which one it speaks in; left alone, it stays kind. Declared this early
  // because everything that talks to the player reads it.
  // Silent until the first click: a browser drops every sound played into an
  // AudioContext the user has not yet interacted with, and says nothing.
  const audio = new Audio();

  /**
   * Whether death costs you what you were carrying.
   *
   * Off by default: losing the bag is what makes a raid a risk, and a game
   * that never takes anything from you has nothing to lose. It is a switch
   * rather than a decision made for everybody because this is somebody's first
   * game and the first hour of it should not be spent walking back.
   */
  const keepInventory = new OverlayToggle<boolean>(
    document.getElementById("keep-toggle") as HTMLInputElement,
    document.getElementById("keep-row") as HTMLLabelElement,
    "vyzhyvannia.keepInventory",
    (raw) => raw === "1",
    (on) => ({ stored: on ? "1" : "0", checked: on }),
    (checked) => checked,
    () => notify(say(
      keepInventory.value ? "keep-on" : "keep-off", tone.tone, lang.value), 2600),
  );

  /**
   * Режим творця: the комора in the bag, and everything in it for nothing.
   *
   * Off by default and kept out of the way, because the game is about digging
   * things up. It is here so a building can be tried without first mining a
   * hillside for it - and because the креслярський камінь is no use to anybody
   * who cannot get one.
   */
  const creative = new OverlayToggle<boolean>(
    document.getElementById("creative-toggle") as HTMLInputElement,
    document.getElementById("creative-row") as HTMLLabelElement,
    "vyzhyvannia.creative",
    (raw) => raw === "1",
    (on) => ({ stored: on ? "1" : "0", checked: on }),
    (checked) => checked,
    () => {
      ui.setCreative(creative.value);
      notify(say(
        creative.value ? "creative-on" : "creative-off", tone.tone, lang.value), 2600);
    },
  );

  /**
   * Hardcore: the кремль map with the gloves off.
   *
   * "Орки будуть йти на другий острів, кількість орків збільшиться вдвічі,
   * орки починають атаку через 5 хвилин після старту." Since then with ten
   * quiet minutes in front of all that - see `HARDCORE_PEACE`. Read once, when the
   * world is built, like the 18+ switch - a raid clock that changed its mind
   * halfway through an evening would be a different game arriving without
   * warning.
   */
  const hardcore = new OverlayToggle<boolean>(
    document.getElementById("hardcore-toggle") as HTMLInputElement,
    document.getElementById("hardcore-row") as HTMLLabelElement,
    "vyzhyvannia.hardcore",
    (raw) => raw === "1",
    (on) => ({ stored: on ? "1" : "0", checked: on }),
    (checked) => checked,
    () => { /* nothing to show until a world is built from it */ },
  );

  /**
   * Which language the game speaks.
   *
   * A list rather than a checkbox. As a tick it read "Language: English"
   * with a box beside it, and nothing said what unticking would do - you had
   * to try it. A list shows both options and which one you are in.
   */
  /**
   * Which map is being played.
   *
   * One so far. Changing it needs a reload, and says so: the world, its
   * chunks, its meshes and the worker pool would all have to be rebuilt in
   * flight otherwise - the same reason loading a save reloads the page.
   */
  const chosenMap = new OverlaySelect<string>(
    document.getElementById("map-select") as HTMLSelectElement,
    document.getElementById("map-row") as HTMLLabelElement,
    "vyzhyvannia.map",
    // Labelled again by applyLang once everything exists; this first pass
    // only has to put the right values in the list.
    // The solo picker only, so a map that is no use alone is not offered
    // to somebody playing alone. See `GameMap.online`.
    SOLO_MAPS.map((m) => ({ value: m.id, label: m.name.uk })),
    (raw) => mapById(raw).id,
    () => { settingsForMap(); sayTheMap(); void refreshHomeButtons(); },
  );

  /**
   * And who is playing, under it.
   *
   * The same question as which map - "what am I about to play" - so the two
   * sit together. It writes the key the in-game picker reads, and the game
   * is built after this screen, so whatever is chosen here is who walks into
   * the world. Changing your mind inside is still the panel's job.
   */
  const homeSkin = new SkinStrip(
    document.getElementById("skin-strip") as HTMLElement,
    document.getElementById("skin-choice") as HTMLElement,
    document.getElementById("skin-name") as HTMLElement,
    document.getElementById("skin-caret") as HTMLElement,
    loadSavedSkin(),
  );

  /**
   * Point the three switches at the map now chosen.
   *
   * They belong to a map rather than to the game: a кремль somebody wants to
   * build in and a hill they want to race on want different answers, and one
   * set for everything meant changing them at every change of map - or, more
   * often, not noticing that you had not. The old single key is the fallback
   * the first time each map is opened, so nobody's switches are turned off
   * by this arriving.
   */
  function pointSettingsAtMap(): void {
    const map = chosenMap.value;
    keepInventory.retarget(
      `vyzhyvannia.keepInventory:${map}`, "vyzhyvannia.keepInventory");
    // No fallback to the old shared switch, unlike the others: a map opened
    // for the first time came up in creative if creative had ever been on
    // anywhere, and «Сучасна війна» broke every block at one blow - "має
    // бути однаково на всіх мапах, крім режиму творчості".
    creative.retarget(`vyzhyvannia.creative:${map}`);
    hardcore.retarget(`vyzhyvannia.hardcore:${map}`, "vyzhyvannia.hardcore");
    tone.retarget(`vyzhyvannia.adult:${map}`, "vyzhyvannia.adult");
  }

  /**
   * And say so on the screen.
   *
   * Split from the pointing because the pointing has to happen before the
   * world is built - 18+ decides whether hemp grows in it - and the screen
   * it would refresh does not exist that early.
   */
  function settingsForMap(): void {
    pointSettingsAtMap();
    ui.setCreative(creative.value);
    /*
     * Hardcore belongs to one map, so it is only on the screen for that one.
     *
     * A switch offered where it does nothing is a switch that teaches the
     * player it does nothing.
     */
    const hardcoreRow =
      document.getElementById("hardcore-row") as HTMLLabelElement | null;
    if (hardcoreRow !== null) {
      // The кремль map names no terrain: two islands is what a world is
      // unless it says otherwise.
      hardcoreRow.hidden =
        (mapById(chosenMap.value).terrain ?? "islands") !== "islands";
    }
    /*
     * And the режим творця has no place on «Один блок».
     *
     * The map is one block that hands out the world a piece at a time; a
     * комора with everything in it is that map with the point taken out.
     * Hidden, and unticked when hidden, so a tick left from another map
     * does not quietly start this one in creative.
     */
    const creativeRow =
      document.getElementById("creative-row") as HTMLLabelElement | null;
    const creativeBox =
      document.getElementById("creative-toggle") as HTMLInputElement | null;
    if (creativeRow !== null && creativeBox !== null) {
      const noCreative = mapById(chosenMap.value).terrain === "one";
      creativeRow.hidden = noCreative;
      if (noCreative && creativeBox.checked) {
        creativeBox.checked = false;
        creativeBox.dispatchEvent(new Event("change"));
      }
    }
    applyTone();
  }

  /**
   * The map this session was built for.
   *
   * The world, its chunks, its meshes and the worker pool are all made from
   * it at startup, so changing the map mid-flight would mean rebuilding all
   * of them - the same reason loading a save reloads the page. What changed
   * is whose job the reload is: the screen used to tell the player to do it
   * and then leave a "Продовжити" button that walked them back into the old
   * map. Now the button remembers what was asked for and reloads itself.
   */
  const playing = mapById(chosenMap.value);

  /**
   * What was pressed on the home screen just before a reload, if anything.
   *
   * Read once and cleared, the same as a join request and for the same
   * reason: a page that kept trying to start a world on every reload would
   * be a player who cannot get back to their own home screen.
   */
  const BEGIN_KEY = "vyzhyvannia.begin";
  /**
   * The seed the next new world is grown from.
   *
   * Written by «Почати пригоду» and read here, so every adventure is a
   * different country. Everything else keeps the one constant it always had:
   * two people pressing «Почати гру» on the кремль should get the кремль,
   * and a mini-game with a different map each time is a different game.
   */
  const NEW_SEED_KEY = "vyzhyvannia.seed";
  const askedSeed = ((): number | null => {
    try {
      const raw = localStorage.getItem(NEW_SEED_KEY);
      /*
       * Read once and cleared, like the press that came with it.
       *
       * Left behind, the adventure's own seed would become the seed of every
       * new world after it - press «Почати пригоду» once and the кремль is a
       * different кремль for ever, which is the one thing a mini-game must
       * not be.
       */
      localStorage.removeItem(NEW_SEED_KEY);
      return raw === null ? null : Number(raw);
    } catch {
      return null;
    }
  })();
  const beginRequest = ((): "new" | "resume" | null => {
    try {
      const raw = localStorage.getItem(BEGIN_KEY);
      localStorage.removeItem(BEGIN_KEY);
      return raw === "new" || raw === "resume" ? raw : null;
    } catch {
      return null;
    }
  })();
  /**
   * Where this map's live world is kept.
   *
   * Per map, so starting a mini-game cannot write over the world somebody has
   * been building for a month - which is the one mistake a map picker could
   * make that would not be forgiven.
   */
  const liveSlot = liveSlotFor(playing, SAVE_SLOT);

  const lang = new OverlaySelect<Lang>(
    document.getElementById("lang-select") as HTMLSelectElement,
    document.getElementById("lang-row") as HTMLLabelElement,
    "vyzhyvannia.lang",
    // Each language is named in its own language, always: somebody who has
    // landed in the wrong one has to be able to find their way out of it.
    (["uk", "en"] as const).map((v) => ({ value: v, label: LANG_LABELS[v] })),
    parseLang,
    () => applyLang(),
  );

  const tone: ToneToggle = new ToneToggle(() => {
    applyTone();
    notify(say(tone.tone === "adult" ? "tone-adult" : "tone-family", tone.tone, lang.value),
      2600);
  });
  /*
   * Before anything is built out of what they say.
   *
   * 18+ decides whether hemp grows, so the switch has to be pointing at this
   * map's answer before the world is made from it - not after, when the
   * screen is ready to be told about it.
   */
  pointSettingsAtMap();

  /**
   * Hardcore, as this world was built.
   *
   * Read once rather than asked of the switch every frame: the raid clock,
   * the ceiling and the marching are one decision about what game this is,
   * and a game that changed halfway through an evening because somebody went
   * back to the menu would be two games with one save.
   *
   * And read *after* the pointing, for the reason 18+ is: the switches keep
   * an answer per map, and until they have been pointed at this one they are
   * still answering for whatever map they were last pointed at. Asked too
   * early, this was always the old single key - so the box could be ticked
   * on the кремль and the world would be built as an ordinary one.
   */
  const brutal = hardcore.value &&
    (playing.terrain ?? "islands") === "islands";

  const hud = document.getElementById("hud") as HTMLDivElement;

  // ---- saved game --------------------------------------------------------
  const store = new IndexedDbStore();
  const loaded = IndexedDbStore.isAvailable()
    ? await loadSave(store, liveSlotFor(mapById((() => {
      try { return localStorage.getItem("vyzhyvannia.map"); } catch { return null; }
    })()), SAVE_SLOT))
    : { save: null, staleTerrain: false, unreadable: false };
  const saved = loaded.save;

  /*
   * Joining a server, if we were asked to before the reload.
   *
   * Done here, before the world exists, because the server's seed is what the
   * world has to be grown from - the whole point of the seed travelling
   * instead of the terrain.
   *
   * Everything about it fails back to the single-player game. Not being able
   * to play with somebody is a disappointment; not being able to play at all
   * because of it would be a bug.
   */
  const net = new Connection();
  const joinUrl = takeJoinRequest();
  let joined: JoinedWorld | null = null;
  /*
   * A world this page is holding for other people, if «Створити» sent us here.
   *
   * It lives in this tab and nowhere else: our server puts a row in its list
   * and introduces people to us, and every game after that goes straight
   * between their machine and this one. Closing the tab closes the world.
   */
  let holding: Holding | null = null;
  /** Why a join on this reload was refused, for the panel to say. */
  let joinTrouble: UiKey | null = null;
  const hold = takeHoldRequest();
  if (hold !== null) {
    const me = loadSavedSkin();
    const name = savedName();
    const held = await holdWorld(
      hold.url, mapById(hold.map), hold.word, name, me.id,
      nameTag(hold.url, "", name), savedPassword(), net, heldStore(), hold.match);
    console.log(`hold ${hold.map}: ${held.ok ? held.holding.world : held.why}`);
    if (held.ok) {
      holding = held.holding;
      joined = held.joined;
      tellPlayed(hold.map);
      /*
       * Let everybody go on the way out.
       *
       * Not a nicety: the world is this tab, and people standing in a tab
       * that has gone would sit waiting for a snapshot that is never coming.
       * `pagehide` rather than `unload`, which a browser may skip entirely.
       */
      addEventListener("pagehide", () => { holding?.stop(); });
    } else {
      notify(uiText(held.why as UiKey, lang.value), 8000);
    }
  }
  if (joinUrl !== null) {
    /*
     * The name they typed, and the skin they picked.
     *
     * Two different things, which they were not before: what travelled as a
     * name was the *skin's* name, so everybody wearing the same козак was
     * called «Силач» - and a bag is kept against a name.
     */
    const me = loadSavedSkin();
    /*
     * Two ways in, and only the first line of each is different.
     *
     * A world on the machine that served this page is a WebSocket to it. A
     * world on another player's computer is an introduction through that
     * same machine and then a channel straight to theirs. After the wire
     * exists, `join` is the same hello either way and nothing below here
     * can tell which happened - which is the whole of what the port bought.
     */
    joined = joinUrl.guest
      ? await joinHeld(joinUrl.url, joinUrl.world, savedName(), me.id,
        joinUrl.word, savedPassword(), net)
      : await net.connect(
        joinUrl.url, savedName(), me.id, joinUrl.word, joinUrl.world,
        savedPassword());
    // The map the page was set to, which is the one the row was picked
    // from; a joined world does not say its map's id, only its own.
    if (joined !== null) tellPlayed(playing.id);
    if (joined === null) {
      /*
       * Said where it can be read. `notify` writes into the readout, which
       * is only drawn over a world - so a refused join put the home screen
       * back with no word about why: "повертає на головну, там немає ніяких
       * повідомлень". The reason is kept and the panel opens on it below,
       * once the panel exists.
       */
      joinTrouble = joinUrl.guest && lastKnock === "silent" ? "net-host-silent"
        : joinUrl.guest && lastKnock === "no-path" ? "net-no-path"
        : net.trouble === "wrong-word" ? "net-wrong-word"
        : net.trouble === "taken-name" ? "net-taken-name"
        : net.trouble === "late" ? "net-late"
        : net.trouble === "mismatch" ? "net-mismatch"
        : "net-unreachable";
      console.log(`join ${joinUrl.world}${joinUrl.guest ? " (guest)" : ""}: ${net.trouble || "unreachable"} - ${joinTrouble}`);
    }
  }

  // ---- world -------------------------------------------------------------
  // A save carries the world options it was made with, so an old world keeps
  // its own size and seed even if the defaults here change. A server's word
  // beats both: everybody in one world has to grow the same one.
  /*
   * The map decides the country; the save decides its size and its seed.
   *
   * A save slot belongs to one map, so whatever map is being played is the
   * right answer for `terrain` and `kremlin` whether or not the save happens
   * to carry them - and saves written before they were recorded carry
   * neither. Reopening a «Король гори» grew two islands with a mountain on
   * each, because "islands" is what the generator does when nobody says.
   */
  const fromMap = {
    ...(playing.terrain !== undefined ? { terrain: playing.terrain } : {}),
    kremlin: playing.kremlin,
  };
  /*
   * A save's own options are for continuing it, not for starting over.
   *
   * A new game took them anyway whenever a save was in the slot, so «Почати
   * спочатку» on «Один блок» kept the old four-chunk width from yesterday's
   * save and grew the new island two hundred blocks outside the world:
   * "перестворив гру, тепер острова із лігвом змія немає взагалі". The
   * dragon was counted - his lair has a position whether or not there is
   * ground under it - and nothing else was there. On a new game the map
   * says what the world is.
   */
  const world = new World({
    ...(joined?.world ?? (saved?.world !== undefined && beginRequest !== "new"
      ? { ...fromMap, ...saved.world }
      : undefined) ?? {
      // A seed of its own when one was asked for - see NEW_SEED_KEY. A save
      // carries its own and wins over both, or continuing an adventure would
      // be a different adventure.
      seed: askedSeed ?? 20260905,
      sizeXZ: playing.sizeXZ,
      sizeY: playing.sizeY,
      kremlin: playing.kremlin,
      ...(playing.terrain !== undefined ? { terrain: playing.terrain } : {}),
    }),
    // Not read from the save: the switch describes the player, not the world.
    adult: tone.tone === "adult",
  });

  /**
   * Nothing is grown until somebody says so.
   *
   * The world used to be built while the page was still loading, so by the
   * time you had read the map picker you were already standing in a map
   * somebody else had chosen - and with more maps coming that is the wrong
   * way round. Two things count as saying so: pressing Почати, and having a
   * world already saved, which is a choice made earlier.
   */
  let grown = false;
  let genMs = 0;
  /** Seconds since the adventure last let go of the country behind it. */
  let forgetClock = 0;
  /** The Змій of «Один блок», looked in on once a second like the adventure's. */
  let dragonClock = 0;

  // ---- renderer ----------------------------------------------------------
  const renderer = new Renderer(document.body);

  // Meshing runs on workers now, so this only queues the work. The world
  // appears chunk by chunk over the next few frames instead of after one long
  // synchronous freeze.
  const meshStart = performance.now();
  renderer.syncChunks(world);

  let reported = false;
  function reportWhenMeshed(): void {
    // Wait until the queue has drained *and* something has actually landed.
    if (reported || renderer.stats.meshing > 0 || renderer.stats.chunks === 0) return;
    reported = true;
    console.info(
      `worldgen ${genMs.toFixed(0)}ms | ` +
      `meshing ${(performance.now() - meshStart).toFixed(0)}ms on ${renderer.workerCount} workers | ` +
      `${renderer.stats.chunks} chunks | ` +
      `${renderer.stats.triangles.toLocaleString()} triangles`,
    );
  }

  // ---- player ------------------------------------------------------------
  // The world is reached only through this callback - the player physics has
  // no idea what a World is, which is what keeps it portable and testable.
  const isSolidAt = (x: number, y: number, z: number): boolean =>
    isSolid(world.getBlock(x, y, z));
  /**
   * And how full each cell is, which is the same question with shapes in it.
   *
   * Two blocks are read because a тин is a block and a half: half of it
   * sticks up into the cell above, and that half is what makes one course of
   * fence higher than a jump. See `cellFill`.
   */
  const fillAt = (x: number, y: number, z: number): number =>
    cellFill(world.getBlock(x, y, z), world.getBlock(x, y - 1, z));
  const isFluidAt = (x: number, y: number, z: number): boolean =>
    isFluid(world.getBlock(x, y, z));
  /** The same question the body asks: лава holds you up too, briefly. */
  const isWadeAt = (x: number, y: number, z: number): boolean =>
    isWadeable(world.getBlock(x, y, z));

  /**
   * The top of the water in a column, for a boat to sit on.
   *
   * Walks down from the surface rather than up from the bed: `groundAt` is
   * the highest opaque block, and over a lake that is the bed - so a search
   * upward from it would have to know how deep the lake is before it could
   * find the top of it. Down from a fixed ceiling over the ground is four or
   * five reads and always right.
   *
   * The +1 is the top of the cell: a source block of water is full height,
   * so the surface of water block `y` is at `y + 1`. It was `y + 0.5` for
   * one draft, which floated every boat half a block inside the lake.
   */
  const WATER_LOOK = 24;
  const surfaceAt = (x: number, z: number): number | null => {
    const col = Math.floor(x);
    const row = Math.floor(z);
    const bed = world.groundAt(col, row);
    if (bed < 0) return null;
    for (let y = bed + WATER_LOOK; y > bed; y--) {
      if (isWater(world.getBlock(col, y, row))) return y + 1;
    }
    return null;
  };
  const water: Water = { surfaceAt };
  /**
   * What the aiming ray stops at.
   *
   * Deliberately not the collision test: plants are walked through but must
   * still be harvestable, and water must let the ray reach the lake bed.
   */
  const isTargetableAt = (x: number, y: number, z: number): boolean =>
    isTargetable(world.getBlock(x, y, z));
  /**
   * What stops you *seeing* something, which is not what stops you hitting it.
   *
   * Glass, grass and leaves are all things a name should be readable through
   * - a пиг behind a blade of wild wheat is a пиг you can see. A wall is not.
   */
  const isOpaqueAt = (x: number, y: number, z: number): boolean =>
    isOpaque(world.getBlock(x, y, z));

  /**
   * Item to block, for the things that have no facing of their own.
   *
   * A мазана стіна grows arms toward whatever it touches and works that out
   * every time the mesher looks; a плита lies down. Neither has a direction
   * to be put down in, so neither needs the facing dance a стіл or a піч
   * does - what they need is one line each.
   */
  const PLAIN_PLACEMENTS: ReadonlyMap<number, number> = new Map([
    [PlainItemId.MazankaWall, wallId()],
    [PlainItemId.GraniteWall, graniteWallId()],
    [PlainItemId.MazankaSlab, MAZANKA_SLAB],
    [PlainItemId.GraniteSlab, GRANITE_SLAB],
  ]);

  const player = new PlayerController();
  // Not simply the middle of the map: that is often a lake, and since trees
  // exist it is often a treetop.
  // The first island is the players'. The second is where the orcs come from,
  // and the causeway between them is the only way across for either side.
  // The first island is the players'. The second is where the orcs come from,
  // and the causeway between them is the only way across for either side.
  // Where the islands *are* is settled when the world is made; what is on
  // them is not, which is why the spawn below waits.
  const home = world.islands[0] ?? { x: 0, z: 0 };
  // Filled in by growWorld: `findSpawn` on terrain that does not exist yet
  // answers about empty air.
  let worldSpawn = { x: 0, y: 0, z: 0 };
  /*
   * How many times the шопопало-блок has been hit open.
   *
   * What decides the next drop, with the seed. Kept with the game rather
   * than in the world's cells because nothing about the cell changes - the
   * block is always there - and it is saved with everything else, so coming
   * back tomorrow carries on down the table instead of starting it again.
   */
  let oneBlockBreaks = 0;

  /**
   * Where the player wakes. Moved by sleeping in a bed, and remembered in the
   * save - the first thing in the game that changes where you come back to.
   */
  let spawn = worldSpawn;

  /**
   * The people in the world.
   *
   * One so far, and he exists only for grown-ups. Villagers are not written
   * to the save - there is nothing about дядько Гриць worth persisting yet,
   * and putting him in the save format now would mean versioning a format for
   * a joke. He is placed the same way each time instead.
   */
  const villagers: Villager[] = [];

  /**
   * Arrows in the air, and arrows stuck in things.
   *
   * Not saved, like the villagers: an arrow is a thing that is happening, not
   * a thing the world contains.
   */
  const arrows: Arrow[] = [];
  /** Seconds as m:ss. Five minutes written as "300с" is not a time. */
  function clock(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
  }

  /**
   * Who is on top of the hill, as the server last said.
   *
   * Only ever filled in online: alone on the map there is nobody to be king
   * over, and a crown you award yourself is not a game.
   */
  const crown = { id: 0, name: "", held: 0 };

  /**
   * The round on «Король гори», and nowhere else.
   *
   * Alone the page keeps its own clock; online the server's is the one that
   * counts and this follows what it says. Either way the same two halves: a
   * minute in which nobody may move while everybody arrives, then five to
   * climb, and whoever is highest at the end has won it.
   */
  const round = { clock: 0, phase: "gather" as "gather" | "climb", left: 60 };

  /**
   * A shorter round, for anybody who has to watch one finish.
   *
   * `?round=4` makes the page's own clock run a four-second round. It moves
   * nothing online - the server's clock is the only one that counts there,
   * and it has a knob of its own that is not reachable from a browser - so
   * the worst anybody can do with this is end their own game early.
   *
   * Here because a check that had to wait six minutes to see the end of a
   * round is a check nobody runs, and the end of a round is the part that
   * was reported broken.
   */
  const shortRound = (() => {
    const asked = Number(new URLSearchParams(location.search).get("round"));
    return Number.isFinite(asked) && asked > 0 ? asked : null;
  })();
  const gatherFor = shortRound === null ? GATHER_SECONDS : shortRound / 2;
  const roundFor = shortRound === null
    ? GATHER_SECONDS + CLIMB_SECONDS : shortRound;
  const roundBanner = document.getElementById("round") as HTMLDivElement;

  /**
   * Out of a finished round, and the match is thrown away with it.
   *
   * A five-minute game is not a thing to keep: a save of one would sit in
   * the slot afterwards and «Продовжити» would offer to walk you back into a
   * match that is over, which is the one offer that button must never make.
   * Somebody else's world is left alone - it is theirs, the server keeps it,
   * and the next round there has already started.
   */
  async function leaveRound(): Promise<void> {
    overScreen.hidden = true;
    overLeft = 0;
    if (net.online) { location.reload(); return; }
    try {
      await store.remove(liveSlot);
    } catch {
      // No storage, or it refused. There is nothing to be done about it and
      // nothing that depends on it: the menu goes back up either way.
    }
    grown = false;
    overlay.classList.remove("hidden");
    input.touching = false;
    if (input.locked) document.exitPointerLock();
    void refreshHomeButtons();
    location.reload();
  }

  {
    /*
     * Two things to do when a round ends, and neither is guessing.
     *
     * Again is the same map from the top - alone that is the clock going
     * round, online it is the round the server has already started without
     * asking. Leaving puts the home screen back, which on a world you joined
     * is a reload because that is how joining works in reverse.
     */
    const again = document.getElementById("over-again") as HTMLButtonElement;
    const leave = document.getElementById("over-leave") as HTMLButtonElement;
    again.addEventListener("click", (e) => {
      e.stopPropagation();
      overScreen.hidden = true;
      overLeft = 0;
      if (!net.online) round.clock = 0;
      if (touchOnly) handToTouch(); else input.requestLock();
    });
    leave.addEventListener("click", (e) => { e.stopPropagation(); void leaveRound(); });
  }
  const overScreen = document.getElementById("over") as HTMLDivElement;
  const overWhat = document.getElementById("over-what") as HTMLDivElement;
  const overWho = document.getElementById("over-who") as HTMLDivElement;
  const overSoon = document.getElementById("over-soon") as HTMLDivElement;

  /**
   * Put the end of a round on the screen, and stop everything behind it.
   *
   * Without this the clock simply went round again and froze everybody for a
   * minute with nothing said, which from inside the game is indistinguishable
   * from the thing having hung - which is how it was reported.
   */
  /**
   * How long a finished round waits before it puts everybody out.
   *
   * A round is five minutes; a finished one sitting on the screen for ever
   * is a world held open by somebody who has walked away from their desk.
   * Sixty seconds is long enough to read who won and decide, and the two
   * buttons are there the whole time for anybody who has decided sooner.
   */
  const OVER_SECONDS = 60;
  /** Shortened along with the round, and for the same reason. */
  const overFor = shortRound === null ? OVER_SECONDS : Math.max(1, shortRound);
  /** Seconds left before a finished round shows itself out. */
  let overLeft = 0;

  function showRoundOver(winner: string): void {
    overLeft = overFor;
    overWhat.textContent = uiText("round-over", lang.value);
    overWho.textContent = winner.length > 0
      ? `${uiText("round-won", lang.value)}: ${winner}`
      : uiText("round-nobody", lang.value);
    overScreen.hidden = false;
    // Hands off the world while it is up: the round is finished, and walking
    // about during the applause is walking about in a match that has ended.
    input.touching = false;
    if (input.locked) document.exitPointerLock();
  }
  /*
   * «Сучасна війна»: the lobby, the clock, and the table at the end.
   *
   * One screen and one function that draws it, driven entirely by the `war`
   * message: the page holds no opinion about whose team is whose or how long
   * is left, because the server's answer is the only one that can be right
   * for everybody at once. What the page decides is what a person can press.
   */
  const warScreen = document.getElementById("war") as HTMLDivElement;
  const warNote = document.getElementById("war-note") as HTMLElement;
  const warTeams = document.getElementById("war-teams") as HTMLElement;
  const warScores = document.getElementById("war-scores") as HTMLTableElement;
  const warMake = document.getElementById("war-make") as HTMLElement;
  const warTeamName = document.getElementById("war-team-name") as HTMLInputElement;
  const warCreate = document.getElementById("war-create") as HTMLButtonElement;
  const warBegin = document.getElementById("war-begin") as HTMLButtonElement;
  const warLeave = document.getElementById("war-leave") as HTMLButtonElement;
  const warClock = document.getElementById("war-clock") as HTMLElement;
  const warCount = document.getElementById("war-count") as HTMLElement;
  const warUntil = document.getElementById("war-until") as HTMLElement;

  /** The last thing the server said about the match, or null off this map. */
  let warState: WarState | null = null;
  /*
   * When the whole game ends, on this page's clock: "гравці мають бачити
   * зворотній відлік". Set from the seconds the world last said were left,
   * and counted down here between one word from the world and the next.
   */
  const warEnds = document.getElementById("war-ends") as HTMLElement;
  let warEndsAt: number | null = null;
  setInterval(() => {
    const over = warState?.phase === "over";
    warEnds.hidden = warEndsAt === null || over;
    if (warEndsAt === null || over) return;
    const left = Math.max(0, Math.round(warEndsAt - performance.now() / 1000));
    const h = Math.floor(left / 3600);
    const m = String(Math.floor((left % 3600) / 60)).padStart(2, "0");
    const s = String(left % 60).padStart(2, "0");
    warEnds.textContent = `${uiText("war-ends", lang.value)} ${h}:${m}:${s}`;
    warEnds.classList.toggle("soon", left <= 300);
  }, 500);

  /** Who hit us last, so a death can say who did it. See `died`. */
  let struckBy = 0;

  function drawWar(war: WarState): void {
    const was = warState?.phase ?? "lobby";
    warState = war;
    if (war.ends !== undefined) warEndsAt = performance.now() / 1000 + war.ends;

    /*
     * The wall and the прапори are built here, not sent.
     *
     * Twenty-seven thousand cells of незламний блок is a megabyte of block
     * edits for one press of «Почати». They are a pure function of one
     * number - how many teams there are - so both sides raise them from the
     * same rules, exactly as a кремль is replayed from "raise this building
     * here" rather than shipped block by block. The server has its own copy
     * for the same reason it has its own world: it is the one that decides
     * what a person may walk through.
     */
    if (was === "lobby" && (war.phase === "prepare" || war.phase === "fight")) {
      world.raiseWar(war.teams.length, (team) =>
        war.teams[team]?.bots === true ? PIRATE_FLAG : flagId(team),
        (team) => war.hardcore === true && war.teams[team]?.bots !== true);
    }
    if (was === "prepare" && war.phase !== "prepare") {
      world.openWar(war.teams.length);
    }
    const playing = war.phase === "prepare" || war.phase === "fight";

    warScreen.hidden = playing;

    /*
     * The countdown, which is on the screen for the whole of the preparing.
     *
     * Shown in the lobby too, standing at whatever the match was made with:
     * "скільки я матиму часу" is the first thing anybody asks before
     * pressing «Почати», and the answer was only ever visible once it had
     * started running.
     */
    warClock.hidden = war.phase !== "prepare" && war.phase !== "lobby";
    if (!warClock.hidden) {
      const left = Math.max(0, Math.round(war.left));
      const mm = Math.floor(left / 60);
      const ss = String(left % 60).padStart(2, "0");
      warCount.textContent = `${mm}:${ss}`;
      warUntil.textContent = uiText(
        war.phase === "lobby" ? "war-prep-length" : "war-prepare", lang.value);
      // The last ten seconds are a warning rather than a fact, and are drawn
      // as one: red, and ticking.
      warClock.classList.toggle("soon", war.phase === "prepare" && left <= 10);
    }
    if (playing) return;

    // The lobby and the table are the same screen with different middles.
    const over = war.phase === "over";
    warMake.hidden = over || !war.owner || war.teams.some((t) => t.bots);
    warBegin.hidden = over || !war.owner;
    warBegin.disabled = war.teams.filter(
      (t) => t.bots || t.members.length > 0).length < 2;
    // Asleep for a reason, and the reason is said: a button that did
    // nothing when pressed was reported as broken.
    const againstBots = war.teams.some((t) => t.bots);
    const whyAsleep = againstBots ? "war-need-side" : "war-need-two";
    warBegin.title = warBegin.disabled ? uiText(whyAsleep, lang.value) : "";
    warScores.hidden = !over;
    warTeams.hidden = over;

    if (over) {
      const won = war.teams.find((t) => t.alive) ?? null;
      warNote.textContent = war.timeUp === true
        ? uiText("war-time-up", lang.value)
        : won === null
          ? uiText("war-nobody", lang.value)
          : `${uiText("war-won", lang.value)}: ${won.name}`;
      const body = warScores.querySelector("tbody") as HTMLElement;
      body.replaceChildren();
      for (const row of war.scores ?? []) {
        const tr = document.createElement("tr");
        if (won !== null && row.team === won.id) tr.className = "won";
        const team = war.teams.find((t) => t.id === row.team)?.name ?? "—";
        for (const cell of [row.name, team, String(row.kills), String(row.deaths)]) {
          const td = document.createElement("td");
          td.textContent = cell;
          tr.append(td);
        }
        body.append(tr);
      }
      if ((war.scores ?? []).length === 0) {
        const tr = document.createElement("tr");
        const td = document.createElement("td");
        td.colSpan = 4;
        td.textContent = uiText("war-quiet", lang.value);
        tr.append(td);
        body.append(tr);
      }
    } else {
      warNote.textContent = war.owner
        ? uiText(warBegin.disabled ? whyAsleep : "war-lobby-owner", lang.value)
        : uiText("war-lobby", lang.value);
      warTeams.replaceChildren();
      for (const team of war.teams) {
        const box = document.createElement("div");
        box.className = "war-team" + (team.id === war.you ? " mine" : "");
        const name = document.createElement("b");
        name.textContent = team.bots ? `${team.name} ☠` : team.name;
        const list = document.createElement("ul");
        for (const who of team.members) {
          const li = document.createElement("li");
          li.textContent = who;
          list.append(li);
        }
        box.append(name, list);
        if (!team.bots && team.id !== war.you) {
          const join = document.createElement("button");
          join.type = "button";
          join.className = "home-tab";
          join.textContent = uiText("war-join", lang.value);
          join.addEventListener("click", () => net.joinTeam(team.id));
          box.append(join);
        }
        warTeams.append(box);
      }
      if (war.teams.length === 0) {
        const empty = document.createElement("div");
        empty.id = "war-empty";
        empty.textContent = uiText("war-no-teams", lang.value);
        warTeams.append(empty);
      }
    }

    // Hands off the world while the screen is up: nobody is playing yet.
    input.touching = false;
    if (input.locked) document.exitPointerLock();
  }

  warCreate.addEventListener("click", () => {
    const name = warTeamName.value.trim();
    if (name.length === 0) return;
    net.makeTeam(name);
    warTeamName.value = "";
  });
  warBegin.addEventListener("click", () => net.begin());
  warLeave.addEventListener("click", () => { window.location.reload(); });

  const roundWhat = document.getElementById("round-what") as HTMLElement;
  const roundLeft = document.getElementById("round-left") as HTMLElement;

  /** Nobody moves while everybody is still arriving. */
  function frozen(): boolean {
    return world.opts.terrain === "hill" && round.phase === "gather";
  }

  /** The orcs' own arrows, which only ever hit the player. */
  const orcArrows: Arrow[] = [];
  /** In the air, thrown by a требушет. */
  const firebolts: Firebolt[] = [];
  /** Everything currently alight. */
  const fire = new Fire();
  /** And everybody currently alight, which is a different list. */
  const burning = new Burning();

  /** Everything lying on the ground waiting to be walked over. */
  const pickups: Pickup[] = [];
  /*
   * The boats, and which one we are in.
   *
   * A boat is furniture until somebody gets into it and then it is where
   * they are - see core/entity/boat.ts for why it has no physics of its own.
   * `aboard` is the one we are in, by reference, because there is exactly
   * one of us on this screen.
   */
  const boats: Boat[] = [];
  let aboard: Boat | null = null;

  /**
   * Танки, and the one this page is driving.
   *
   * The same arrangement the boats have and for the reasons written there: a
   * machine is furniture until somebody gets in, and while they are in it
   * the machine is where they are. `driving` is by reference because there
   * is exactly one of them and it is the one under us.
   */
  const tanks: Tank[] = [];
  let driving: Tank | null = null;
  /** Снаряди in the air, ours and everybody else's alike. */
  const shells: Shell[] = [];
  /** Seconds of track noise owed, so a moving tank is heard rather than seen. */
  let trackNoise = 0;

  /**
   * Fire the gun, if there is a снаряд in the bag and the barrel is cool.
   *
   * Along the crosshair rather than along the hull: the gun is what you aim
   * and the hull is what you drive, which is the whole reason a tank is
   * worth getting into rather than being a fast pair of boots.
   */
  function fireShell(tank: Tank): void {
    if (tank.reload > 0) return;
    if (!inventory.take(PlainItemId.TankShell, 1)) {
      notify(say("no-shells", tone.tone, lang.value), 1800);
      return;
    }
    ui.refresh();
    recipes.refresh();
    tank.reload = TANK_RELOAD;

    const eye = player.eyePosition();
    const look = player.lookDirection();
    // Out of the muzzle rather than out of the driver's face, or the first
    // thing every shell hits is the tank it was fired from.
    const from = {
      x: eye.x + look.x * 2.6,
      y: eye.y + look.y * 2.6,
      z: eye.z + look.z * 2.6,
    };
    const velocity = {
      x: look.x * SHELL_SPEED,
      y: look.y * SHELL_SPEED,
      z: look.z * SHELL_SPEED,
    };
    shells.push(makeShell(from, velocity, tank.id));
    if (net.online) {
      net.fireShell(from.x, from.y, from.z, velocity.x, velocity.y, velocity.z,
        tank.id);
    }
    audio.play("breakStone", { rate: 0.4, volume: 0.9 });
  }

  /**
   * A танк has gone up.
   *
   * "Потім танк вибухає, а гравець випадає з нього і втрачає 50% здоров'я."
   * The crew is thrown clear rather than killed - half of everything is a
   * catastrophe you can walk away from, which is what makes the fifth hit
   * worth avoiding rather than worth ignoring.
   */
  function tankBlast(at: { x: number; y: number; z: number }): void {
    boomHeard(at.x, at.y, at.z);
    if (driving !== null &&
      Math.hypot(driving.x - at.x, driving.z - at.z) < 0.5) {
      driving.rider = null;
      driving = null;
    }
    const away = Math.hypot(
      player.position.x - at.x, player.position.y - at.y,
      player.position.z - at.z);
    const share = blastShare(away);
    if (share <= 0) return;
    // A share of the whole rather than a number of points: what half of a
    // person's сила is, is the page's own business - the same arrangement
    // armour and drowning already have.
    health.hurt(Math.max(1, Math.round(health.max * share)));
    notify(say("tank-gone", tone.tone, lang.value), 2600);
  }
  /**
   * Everybody else, where they are drawn.
   *
   * Refilled every frame from the same smoothed places the renderer is
   * given, so a swing lands on the body somebody can see rather than on the
   * snapshot behind it.
   */
  const seenPlayers: { id: number; x: number; y: number; z: number }[] = [];
  /** Seconds since the last stroke of the oars was heard. */
  let rowSound = 0;

  /** The orcs. */
  const orcs: Orc[] = [];

  /** What the player can take. Not saved yet - a death resets it anyway. */
  const health = new Vitals(PLAYER_MAX_HP);

  /**
   * How far from the player a village keeps its people.
   *
   * Further than the herd - a village is a fixed thing you can see from
   * across a field, and people who pop into existence as you reach the тин
   * would give the game away. Nothing is lost by forgetting them: everything
   * about a villager is worked out from where their house stands.
   */
  const FOLK_KEEP = 160;

  /**
   * Put the people in the villages, and forget the ones left behind.
   *
   * "Засели село селянами." Who lives in each хата is `household`, which
   * reads the beds the builder put down - so nobody is ever standing in a
   * house with no bed for them, and no bed is ever empty.
   */
  function restockFolk(): void {
    if (playing.terrain !== "adventure") return;
    const me = player.position;

    for (let i = villagers.length - 1; i >= 0; i--) {
      const v = villagers[i]!;
      // The two by the player's own spawn have no house and are nobody's
      // neighbours; they stay whatever happens.
      if (v.bed === null) continue;
      if (Math.hypot(v.home.x - me.x, v.home.z - me.z) > FOLK_KEEP) {
        villagers.splice(i, 1);
      }
    }

    for (const { house, floor, plot } of world.homesNear(me.x, me.z, FOLK_KEEP)) {
      const home = { x: house.x + (house.w >> 1), y: floor + 1, z: house.z + (house.d >> 1) };
      // Already somebody's house: the household is put down whole or not at
      // all, so one of them standing means all of them are.
      if (villagers.some((v) => v.home.x === home.x && v.home.z === home.z)) {
        continue;
      }
      household(house, floor).forEach((who, n) => {
        const roll = (Math.abs(house.x * 31 + house.z * 17 + n * 7) % 97) / 97;
        /*
         * Where there is actually room for them.
         *
         * The plan says "the middle of the room", and on the outer ring two
         * хати can stand close enough that one lays its floor across the
         * other's - a body put down inside that floor gets shoved out
         * through the wall by the collision.
         */
        const stand = world.roomFor(who.at.x, who.at.y, who.at.z);
        if (stand === null) return;
        villagers.push(makeVillager(
          nameForKind(who.kind, roll), skinForKind(who.kind),
          lineForKind(who.kind),
          stand,
          {
            kind: who.kind, home, bed: who.bed,
            // Where the way out is, and what counts as being indoors.
            door: { x: doorCell(house).x, y: floor + 1, z: doorCell(house).z },
            porch: {
              x: porchCell(house).x, y: floor + 1, z: porchCell(house).z,
            },
            mat: { x: matCell(house).x, y: floor + 1, z: matCell(house).z },
            room: roomBox(house),
            // Children do not work the garden. They are children.
            ...(plot !== null && (who.kind === "чоловік" || who.kind === "жінка")
              ? { plot }
              : {}),
          },
        ));
      });
    }
  }

  /**
   * A crop within reach of somebody working, if there is one.
   *
   * The cell they are standing on first and then the ring round it, so a
   * villager who has drifted a step off the row still finds their own
   * garden rather than nothing. Ripe crops are skipped: there is no work to
   * do on a row that is ready, and without this every tended plot would sit
   * at "ripe" and the growing would be invisible.
   */
  function tendable(v: Villager): { x: number; y: number; z: number } | null {
    const fx = Math.floor(v.x);
    const fy = Math.floor(v.y);
    const fz = Math.floor(v.z);
    for (let r = 0; r <= 2; r++) {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
          for (let dy = -1; dy <= 1; dy++) {
            const at = { x: fx + dx, y: fy + dy, z: fz + dz };
            const id = world.getBlock(at.x, at.y, at.z);
            if (isCrop(id) && nextCropStage(id) !== 0) return at;
          }
        }
      }
    }
    return null;
  }

  /**
   * Bring the population in line with the 18+ switch, and with the map.
   *
   * Two reasons there might be nobody: the switch is off, or this is not a
   * map anybody lives on - see `GameMap.villagers`.
   */
  function syncPopulation(): void {
    if (tone.tone !== "adult" || playing.villagers !== true) {
      villagers.length = 0;
      return;
    }
    if (villagers.length > 0) return;
    /*
     * Not before the world exists.
     *
     * This used to run at startup, when `worldSpawn` was still nought and
     * the terrain had not been grown - so `findSpawn(5, 3)` answered about
     * the empty sky over the corner of the map, and the two of them fell
     * out of it and drowned. "Чому селяни стоять під водою": on the shipped
     * seed they ended up at 17, 42, 6, eleven blocks under the sea.
     *
     * The herd and the fish learned this already - see growWorld, where the
     * comment says the same thing about fourteen pigs.
     */
    if (!grown) return;
    // A few steps from where the player wakes, so you meet them early. Two of
    // them, and not two of the same: a village that will one day have children
    // in it needs a чоловік and a жінка, and pairing men with men - which is
    // what Minecraft does - was never going to be the answer here.
    villagers.push(
      makeVillager("дядько Гриць", "selianyn", "villager-hit", world.findSpawn(
        Math.floor(worldSpawn.x) + 5, Math.floor(worldSpawn.z) + 3)),
      makeVillager("тітка Одарка", "selianka", "villager-hit-her", world.findSpawn(
        Math.floor(worldSpawn.x) + 7, Math.floor(worldSpawn.z) + 4)),
    );
  }

  /**
   * Death: everything you were carrying stays where you fell.
   *
   * There is no grave and no timer - you are simply back at your bed with
   * nothing, and your things are lying where it happened for as long as
   * anything lies anywhere. Dropping the bag rather than keeping it is what
   * makes a raid a risk; it costs nothing to implement now that things can lie
   * on the ground, and it would have been unimplementable a week ago.
   */
  function die(): void {
    /*
     * Where the bag lands, which is not always where you fell.
     *
     * Nothing holds a dropped thing up but the ground, and the world reads as
     * air outside itself - so dying over a shaft, or off the edge of the map,
     * spilled everything into a fall with no bottom. It counted as lying
     * there for the full five minutes and was out of reach the whole time,
     * which is the worst of both.
     *
     * With nothing at all below, the pile goes to the bed instead. Losing a
     * walk back is the price of dying; losing the bag to a hole in the floor
     * is not a price, it is a bug with a story.
     */
    const fell = {
      x: Math.floor(player.position.x),
      y: Math.floor(player.position.y),
      z: Math.floor(player.position.z),
    };
    const ground = restingCell(fell, isSolidAt);
    const at = ground ?? {
      x: Math.floor(spawn.x), y: Math.floor(spawn.y), z: Math.floor(spawn.z),
    };
    if (ground === null) notify(say("died-lost", tone.tone, lang.value), 6000);
    // Armour falls with everything else. Kit that survived a death the bag
    // did not would make the кольчуга the one thing worth wearing into a
    // fight you expect to lose, which is the opposite of what it is for.
    const spilled = [
      ...inventory.spillOnDeath(keepInventory.value),
      ...worn.spillOnDeath(keepInventory.value),
    ].map((slot) => ({ item: slot.id, count: slot.count }));
    // Your own bag lies twice as long as a stray cobble: you wake where your
    // bed is, and the walk back is the whole of the punishment.
    drop(spilled, at, DEATH_LIFETIME);

    // Whatever was on the line is not on it any more.
    line.reset();
    health.revive();
    respawn();
    notify(say(keepInventory.value ? "died-kept" : "died", tone.tone, lang.value), 4000);
    ui.refresh();
    recipes.refresh();
  }

  function respawn(): void {
    // Whatever was going on, it is not going on any more. A body put back at
    // the spawn while still marked as being in a boat would be dragged back
    // to the middle of the lake on the next tick.
    if (aboard !== null) { aboard.rider = null; aboard = null; }
    player.position.x = spawn.x;
    player.position.y = spawn.y;
    player.position.z = spawn.z;
    player.velocity.x = 0;
    player.velocity.y = 0;
    player.velocity.z = 0;
  }
  respawn();

  /**
   * Make the world match the 18+ switch, without a restart.
   *
   * Terrain is deterministic from the seed, so regenerating it is cheap and
   * gives exactly the same ground back - the only difference is the hemp,
   * which generation places or does not place depending on the switch.
   * Everything the player changed is lifted out first and put back after, so
   * a house does not evaporate because somebody ticked a box.
   *
   * The sweep afterwards is what makes the guarantee hold: generation alone
   * would leave hemp the player had planted standing in a modified chunk.
   */
  function applyTone(): void {
    const adult = tone.tone === "adult";
    if (world.opts.adult !== adult) {
      const kept = world.modifiedChunks().map((chunk) => ({
        cx: chunk.cx, cy: chunk.cy, cz: chunk.cz, blocks: chunk.blocks.slice(),
      }));
      world.opts.adult = adult;
      world.generate();
      for (const k of kept) world.loadChunk(k.cx, k.cy, k.cz, k.blocks);
      if (!adult) world.stripAdultBlocks();
      renderer.invalidateMeshes();
    }
    syncPopulation();
  }

  /**
   * Write the home screen in the chosen language.
   *
   * The panel's words live in index.html as markup, which cannot switch
   * language on its own, so every one of them is set from here. The list of
   * keys is built rather than written, from the table in core/text - which is
   * why that list is data now and not twenty lines of hand-written HTML.
   */
  function applyHomeText(value: Lang): void {
    const set = (id: string, text: string): void => {
      const el = document.getElementById(id);
      if (el !== null) el.textContent = text;
    };
    /*
     * Two buttons, each of which says only what it does.
     *
     * One button that changed its word between "Продовжити" and "Почати" had
     * to pick one of them, and after the map picker was touched it was
     * picking the wrong one - it said Продовжити over a map with nothing to
     * continue, and walked you into the map you had just left.
     */
    set("tab-play", uiText("home-resume", value));
    set("tab-save", uiText("home-save", value));
    set("tab-new", uiText(newLabel(), value));
    set("tab-solo", uiText("home-solo", value));
    set("solo-head", uiText("home-solo-head", value));
    set("solo-back", uiText("home-back", value));
    set("pick-note",
      uiText(playTab.hidden ? "home-pick-first" : "home-resume-note", value));
    set("tab-map", uiText("home-seed-map", value));
    set("tab-net", uiText("net-together", value));
    set("tab-register", uiText("net-register", value));
    set("tab-settings", uiText("home-settings", value));
    set("tab-saves", uiText("home-saves", value));
    set("over-again", uiText("round-again", value));
    set("over-leave", uiText("round-leave", value));
    sayTheMap();
    for (const [cls, text] of [
      ["paused-note", uiText("home-paused", value)],
      // Empty unless there is something to say, which is a machine with no
      // pointer on it. "click to play" was a thing anybody could already see.
      ["cta", noPointer ? uiText("home-no-pointer", value) : ""],
    ] as const) {
      const el = document.querySelector(`.${cls}`);
      if (el !== null) el.textContent = text;
    }

    // Each switch is a line of text and a smaller line under it.
      set("map-text", uiText("home-map", value));
    set("skin-text", uiText("home-skin", value));
    homeSkin.relabel(value);
    set("lang-text", uiText("home-language", value));
    for (const [row, head, note] of [
      ["creative-row", "home-creative", "home-creative-note"],
      ["keep-row", "home-keep", "home-keep-note"],
      ["hardcore-row", "home-hardcore", "home-hardcore-note"],
      ["adult-row", "home-adult", "home-adult-note"],
    ] as const) {
      const span = document.querySelector(`#${row} .adult-text`);
      if (span === null) continue;
      span.replaceChildren();
      if (row === "adult-row") {
        // The "18+" is bold and is not a word, so it is not translated.
        const b = document.createElement("b");
        b.textContent = "18+";
        span.append(b, ` ${uiText(head, value)}`);
      } else {
        span.append(uiText(head, value));
      }
      if (note !== null) {
        const i = document.createElement("i");
        i.textContent = uiText(note, value);
        span.append(i);
      }
    }

    /*
     * The music, in «Налаштування», which is where somebody looks for it.
     *
     * A switch and a slider, and the switch earns its place: some people play
     * with their own music on, and for them a game that insists on its own is
     * a game played muted - which takes the axe and the water with it.
     *
     * It lived at the bottom of «Керування», under the key list, where
     * nobody found it. Built here rather than written into the markup so it
     * is rebuilt in whichever language the menu is in.
     */
    const settings = document.getElementById("panel-settings");
    if (settings !== null) {
      const row = document.createElement("label");
      row.className = "music-row";
      const onOff = document.createElement("input");
      onOff.type = "checkbox";
      onOff.id = "music-on";
      onOff.checked = musicOn;
      const name = document.createElement("b");
      name.textContent = uiText("music", value);
      const level = document.createElement("input");
      level.type = "range";
      level.id = "music-level";
      level.min = "0"; level.max = "100"; level.step = "5";
      level.value = String(Math.round(audio.musicVolume * 100));
      onOff.addEventListener("change", () => {
        setMusic(onOff.checked);
        level.disabled = !onOff.checked;
        remember();
      });
      level.addEventListener("input", () => {
        audio.setMusicVolume(Number(level.value) / 100);
        remember();
      });
      level.disabled = !musicOn;
      row.append(onOff, name, level);

      // And the hand, under the music: two radios, because there are two.
      const handRow = document.createElement("div");
      handRow.className = "hand-row";
      const handName = document.createElement("b");
      handName.textContent = uiText("hand", value);
      handRow.append(handName);
      for (const which of ["right", "left"] as const) {
        const choice = document.createElement("label");
        choice.className = "hand-choice";
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "hand";
        radio.id = `hand-${which}`;
        radio.checked = hand === which;
        radio.addEventListener("change", () => {
          if (radio.checked) chooseHand(which);
        });
        choice.append(radio, uiText(`hand-${which}`, value));
        handRow.append(choice);
      }

      /*
       * And the keys under it, still headed «Керування».
       *
       * They had a button of their own until the music got one. Seven
       * buttons is one more than this screen holds to - and of the two,
       * the keys are the thing you read once and the volume is the thing
       * you come back to, so the keys are the half that moved in.
       */
      const heading = document.createElement("b");
      heading.className = "keys-head";
      heading.textContent = uiText("home-controls", value);
      settings.replaceChildren(row, handRow, heading, ...CONTROLS.flatMap((line) => {
        const b = document.createElement("b");
        b.textContent = pick(line.key, value);
        return [b, `  ${pick(line.what, value)}`, document.createElement("br")];
      }));
    }
  }

  /**
   * Push the chosen language into everything that draws words.
   *
   * Each panel holds its own `lang` and redraws from it, rather than the
   * language being read from a global at paint time: the panels are objects
   * that already own their own state, and a hidden global read is the kind of
   * dependency that quietly stops being called.
   */
  /**
   * Show «Продовжити» only when the *chosen* map has a world to go back to.
   *
   * Asked of the store rather than of `saved`, which is the save this session
   * loaded - and after the picker has been touched, that is the wrong map's.
   * That was the whole bug: pick a new map and the button still said
   * Продовжити, and pressing it walked you back into the old world.
   */
  /**
   * What «Почати гру» is offering right now.
   *
   * Over a world you are standing in it is «Почати спочатку»: the same
   * press, said for what it does. It used to be hidden there instead, and
   * on «Один блок» that left somebody who had fallen off with nothing to
   * press. A different map is a new game there; «Пригода» is a new
   * country every time and says so.
   */
  function newLabel(): UiKey {
    if (grown && chosenMap.value === playing.id) return "home-restart";
    return chosenMap.value === ADVENTURE.id ? "home-adventure" : "home-new";
  }

  async function refreshHomeButtons(): Promise<void> {
    const slot = liveSlotFor(mapById(chosenMap.value), SAVE_SLOT);
    let there = false;
    try {
      there = IndexedDbStore.isAvailable() &&
        (await store.list()).some((row) => row.name === slot);
    } catch {
      // No storage, or it refused. Offering nothing is the honest answer.
    }
    // Never once there is a world on the screen: the home screen is behind it.
    playTab.hidden = grown || !there;
    /*
     * And «Зберегти» is the other way round: only with a world to save.
     *
     * Not on somebody else's, either - `saveGame` refuses that and says why,
     * but a button that exists to be refused is a button that lies about
     * what it does.
     */
    saveTab.hidden = !grown || net.online;
    /*
     * Except the one that takes you somewhere else.
     *
     * Once a world is up, «Почати гру» is an offer to flatten the one you are
     * standing in, so it goes - unless the picker has been moved to a
     * different map, in which case it is the only way to get there and the
     * alternative is a dead end: a chosen map, and nothing to press.
     */
    newTab.hidden = false;
    newTab.textContent = uiText(newLabel(), lang.value);
    hasAdventure = false;
    try {
      hasAdventure = IndexedDbStore.isAvailable() &&
        (await store.list()).some(
          (row) => row.name === liveSlotFor(ADVENTURE, SAVE_SLOT));
    } catch { /* no storage, so nothing to write over */ }
    pickNote.hidden = grown;
    /*
     * And say it again, now that we know which it is.
     *
     * The note under the buttons is "choose a map" or "your world is
     * waiting", and which one depends on the answer that just came back from
     * the store. Asking the store is a promise, so the first pass of
     * `applyHomeText` ran before it resolved and left the wrong sentence up.
     */
    applyHomeText(lang.value);
  }

  function applyLang(): void {
    const value = lang.value;
    ui.lang = value;
    recipes.lang = value;
    saves.lang = value;
    skins.lang = value;
    vitals.lang = value;
    ui.retitle();
    void saves.refresh();
    network.lang = value;
    network.retitle();
    // The tick over the book has words on it too.
    recipes.retitle();
    recipes.refresh();
    chosenMap.setOptions(
      SOLO_MAPS.map((m) => ({ value: m.id, label: pick(m.name, value) })));
    applyHomeText(value);
  }


  /**
   * The clock the whole game is arranged around.
   *
   * Stepped with the live count of lairs, so breaking the last one stops the
   * raids for good - which is the win condition, and the only reason to cross
   * the water at all.
   */
  const raid = new RaidClock(
    brutal ? HARDCORE_FIRST_RAID : RAID_INTERVAL);
  /**
   * How many orcs the island will hold at once.
   *
   * "Кількість спавнерів збільшувати не обов'язково - просто більший ліміт на
   * максимум орків." The same five лігва send the same waves; what hardcore
   * changes is how many of them may be standing about at once.
   */
  const orcCap = brutal ? HARDCORE_MAX_ORCS : MAX_ORCS;
  /**
   * Seconds of hardcore left before the orcs march. See `HARDCORE_PEACE`.
   *
   * Counted off the same fixed tick as the raid clock, and like it not saved:
   * a reloaded world starts its quiet ten minutes again, as it starts its
   * raid clock again.
   */
  let peaceLeft = brutal ? HARDCORE_PEACE : 0;
  /** The slow drip between raids: one orc per lair every five minutes. */
  const lairClock = new LairClock();
  // Counted in growWorld, for the same reason as the spawn: an ungrown world
  // has no lairs in it and reporting nought would end the raids before they
  // began.
  let lairsLeft = 0;

  /**
   * The livestock.
   *
   * On the players' island only. The far one is for orcs, and a pig grazing
   * between the lairs would be a free lunch behind enemy lines.
   *
   * Not saved, the same as the villagers: they wander, so where they were
   * when the game closed is not a fact worth keeping, and a fresh herd on
   * load is what every world has always done with its people.
   */
  const beasts: Beast[] = [];
  /**
   * Змії, one to a лігво, and only while somebody is near enough to meet one.
   *
   * Not spawned with the world: there are sixteen lairs in a country four
   * thousand blocks across, and sixteen thousand-point bodies being stepped
   * for a player who is nowhere near any of them is sixteen thousand points
   * of nothing.
   */
  const dragons: Dragon[] = [];
  /**
   * Лігва whose Змій has been killed. "Після смерті не відроджується."
   *
   * Keyed by the hall's own position, which is arithmetic out of the seed -
   * so the key is the same every time the country is grown again, and the
   * set is the one thing about a lair that has to be written to the save.
   */
  const slainLairs = new Set<string>();
  const lairKey = (at: { x: number; z: number }): string => `${at.x},${at.z}`;
  function spawnHerd(): void {
    const home2 = world.islands[0] ?? home;
    const HERD = 14;
    for (let i = 0; i < HERD; i++) {
      // The кремль's own herd: stock only. Собаки and коти belong to the
      // adventure, where somebody walks far enough to meet one.
      const kind: BeastKind = i % 4 === 0 ? "tur" : i % 4 === 1 ? "pig" : "kurka";
      // Scattered over the island rather than in one pen: they are wild, and
      // finding them is part of hunting them.
      const angle = (i / HERD) * Math.PI * 2 + i * 0.7;
      const away = (0.2 + (i % 5) * 0.14) * home2.radius;
      beasts.push(makeBeast(kind, world.findSpawn(
        Math.round(home2.x + Math.cos(angle) * away),
        Math.round(home2.z + Math.sin(angle) * away),
        true,
      )));
    }
  }

  /**
   * Life in a world with no edges to stock.
   *
   * The herd and the карасі are scattered over the island when the world is
   * made, which works for a map that exists all at once. An adventure does
   * not: it is a thousand blocks across and only the couple of hundred round
   * the player have been grown, so scattering over the whole of it puts
   * nothing anywhere - four thousand tries at finding water in country that
   * is not there yet.
   *
   * So the life follows instead. Anything that falls a long way behind is
   * taken away, anything missing is put down in the country ahead, and the
   * numbers are what the кремль map has over an island: a walk should meet
   * about as much as a walk there does.
   */
  /**
   * Keep the country round the player stocked, and let go of what is behind.
   *
   * The rules for how much and where live in core/entity/restock.ts, and
   * they are the server's rules too. What is left here is the half a page
   * only this side can do: turning "a свиня, here" into a свиня. That split
   * is the point of it - the lone game and the realm would otherwise have
   * two copies of the same arithmetic, and two copies drift.
   */
  /**
   * A Змій over every лігво near enough to matter, and none anywhere else.
   *
   * On the same clock as the rest of the life, which is once a second: a
   * dragon is a fact about a mountain rather than about this frame, and
   * asking sixteen districts where their halls are sixty times a second
   * would be asking the same question fifty-nine times too often.
   */
  function restockDragons(): void {
    /*
     * The island's one Змій, on «Один блок».
     *
     * Not on the lair grid, and never far: the map is sixty-four blocks
     * across. He is there from the start and gone for good once slain.
     */
    if (world.opts.terrain === "one") {
      const at = world.dragonLairs()[0];
      if (at === undefined || slainLairs.has(lairKey(at))) return;
      if (dragons.some((d) => d.home.x === at.x && d.home.z === at.z)) return;
      dragons.push(makeDragon(at, ZMII.maxHp));
      return;
    }
    if (world.opts.terrain !== "adventure") return;
    const near = 200;
    for (let i = dragons.length - 1; i >= 0; i--) {
      const d = dragons[i]!;
      const away = Math.hypot(d.home.x - player.position.x,
        d.home.z - player.position.z);
      if (d.slain || away > near * 1.4) dragons.splice(i, 1);
    }
    const gx = Math.floor(player.position.x / LAIR_CELL);
    const gz = Math.floor(player.position.z / LAIR_CELL);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const at = world.lairAt(gx + dx, gz + dz);
        if (at === null) continue;
        if (slainLairs.has(lairKey(at))) continue;
        const away = Math.hypot(at.x - player.position.x, at.z - player.position.z);
        if (away > near) continue;
        if (dragons.some((d) => d.home.x === at.x && d.home.z === at.z)) continue;
        dragons.push(makeDragon(
          { x: at.x, y: world.probeColumn(at.x, at.z).ground - HALL_DEPTH, z: at.z },
          ZMII.maxHp,
        ));
      }
    }
  }

  function restockLife(): void {
    const me: Person[] = [{ x: player.position.x, z: player.position.z }];
    const dark = cycle.phase === "night" || cycle.phase === "dusk";

    /*
     * Backwards, because splicing shortens the list under the loop.
     *
     * Nothing is saved about a свиня that walked off the edge of what is
     * grown: the herd is remade on load anyway, and a beast standing in a
     * column that no longer exists would be standing in the sky.
     */
    for (let i = beasts.length - 1; i >= 0; i--) {
      if (strayedFromAll(beasts[i]!, me, LIFE_KEEP)) beasts.splice(i, 1);
    }
    for (let i = fish.length - 1; i >= 0; i--) {
      if (strayedFromAll(fish[i]!, me, LIFE_KEEP)) fish.splice(i, 1);
    }
    for (let i = orcs.length - 1; i >= 0; i--) {
      const foe = orcs[i]!;
      if (!WILD_FOES.includes(foe.breed.kind)) continue;
      /*
       * Gone at first light, and gone when left far behind.
       *
       * The sun is what deals with a мавка - not a sabre, and not the player
       * deciding to wait. A вовкулака stays out in the day: it is a wolf,
       * not a ghost.
       */
      if (nightOnly(foe.breed.kind) && !dark) { orcs.splice(i, 1); continue; }
      // And the day one at dusk: the two are one мавка in two skins.
      if (dayOnly(foe.breed.kind) && dark) { orcs.splice(i, 1); continue; }
      if (strayedFromAll(foe, me, FOES_KEEP)) orcs.splice(i, 1);
    }

    /*
     * Only an adventure stocks itself as it is walked.
     *
     * A кремль is put out whole when it is made - it exists all at once and
     * has an island to scatter a herd over - and a hill has neither pasture
     * nor water nor anything to be afraid of.
     */
    if (playing.terrain !== "adventure") return;

    const country: Wood = {
      groundAt: (x, z) => world.groundAt(x, z),
      blockAt: (x, y, z) => world.getBlock(x, y, z),
      fluidAt: (x, y, z) => isFluidAt(x, y, z),
      inside: (x, z) => world.withinBounds(x, 1, z),
      leafAt: (x, y, z) => isLeaf(world.getBlock(x, y, z)),
    };
    for (const put of whatToPutDown(country, me[0]!, {
      beasts: beasts.length,
      fish: fish.length,
      wolves: orcs.filter((o) => o.breed.kind === "vovkulaka").length,
      mavkas: orcs.filter((o) => o.breed.kind === "mavka" || o.breed.kind === "mavkaNight").length,
      turn: beasts.length,
    }, world.seaLevel, BlockId.Air, dark)) {
      const at = { x: put.x, y: put.y, z: put.z };
      if (put.what === "karas") fish.push(makeFish(at));
      else if (put.what === "vovkulaka" || put.what === "mavka" || put.what === "mavkaNight") {
        orcs.push(makeOrc(at, put.what));
      } else beasts.push(makeBeast(put.what as BeastKind, at));
    }
  }

  /**
   * The карасі.
   *
   * Put wherever there is water deep enough to turn round in, all over the
   * world rather than only near the shore: a lake with nothing in it is a
   * lake nobody fishes.
   */
  const fish: Fish[] = [];
  function stockFish(): void {
    const span = world.opts.sizeXZ * 16;
    // Forty, not ninety. Each fish is three draw calls whether or not anybody
    // is near enough to see it, and a lake wants enough fish to look alive
    // rather than enough to be a shoal.
    const wanted = 40;
    for (let tries = 0; tries < 4000 && fish.length < wanted; tries++) {
      const x = 4 + Math.floor(Math.random() * (span - 8));
      const z = 4 + Math.floor(Math.random() * (span - 8));
      const ground = world.groundAt(x, z);
      // Two clear cells of water above the bed, so it has somewhere to swim
      // and is not spawned wedged against the bottom.
      if (ground >= world.seaLevel - 1) continue;
      const y = ground + 2;
      if (!isFluidAt(x, y, z) || !isFluidAt(x, y + 1, z)) continue;
      fish.push(makeFish({ x: x + 0.5, y: y + 0.5, z: z + 0.5 }));
    }
  }

  /** The line, when there is one in the water. One rod, one player. */
  const line = new FishingLine();

  /**
   * Two guards at every lair, and one leader over the lot.
   *
   * The lairs are inside the кремль now, so this is the garrison: walking
   * into a fortress means walking into whoever is standing in it, rather than
   * finding an empty building with a spawner in the corner.
   *
   * One leader for the whole place, not one per lair. It is meant to be the
   * thing you remember from the fight, and fourteen of them would be a mob.
   */
  /**
   * Two скелети in every room of the замок, and Чахлик in his hall.
   *
   * Put down once, when the world is made, at the posts the building itself
   * names. Not restocked and not wandered in from outside: a guard is part
   * of the place, and one that respawns is a place you cannot clear.
   *
   * Nothing happens on any other map - there is no замок on one - and
   * nothing happens twice, because this runs where the world is grown.
   */
  /* ---- music ------------------------------------------------------------ */

  /*
   * One director for the world being played, made when it is grown.
   *
   * It is told the map and whether this is 18+ once, because neither changes
   * without the world being made again - and `applyTone` does exactly that.
   */
  let music: Music | null = null;
  let musicOn = true;
  /** What the pause was last frame, so the context is not told every frame. */
  let musicPaused = false;

  /*
   * Kept between visits, because a preference somebody has to set every time
   * is a preference the game keeps overriding.
   */
  const MUSIC_KEY = "vyzhyvannia.music";
  function remember(): void {
    try {
      localStorage.setItem(MUSIC_KEY, JSON.stringify({
        on: musicOn, level: audio.musicVolume,
      }));
    } catch { /* no storage, no memory */ }
  }
  try {
    const kept = localStorage.getItem(MUSIC_KEY);
    if (kept !== null) {
      const was = JSON.parse(kept) as { on?: boolean; level?: number };
      if (was.on === false) musicOn = false;
      if (typeof was.level === "number") audio.setMusicVolume(was.level);
    }
  } catch { /* a bad note is no note */ }

  /*
   * Which hand the weapon is in. Kept the same way as the music: it is about
   * the person, not the world.
   */
  let hand: Hand = "right";
  try {
    hand = parseHand(localStorage.getItem(HAND_KEY));
  } catch { /* no storage, the right hand */ }
  renderer.setHand(hand);
  function chooseHand(chosen: Hand): void {
    hand = chosen;
    renderer.setHand(hand);
    try {
      localStorage.setItem(HAND_KEY, hand);
    } catch { /* no storage, no memory */ }
  }

  function beginMusic(): void {
    const on = world.opts.terrain === "hill" ? "hill"
      : world.opts.terrain === "adventure" ? "adventure" : "kremlin";
    music = new Music(on, world.opts.adult === true);
  }

  /**
   * A frame of it: how near the кремль, and whether anything should start.
   *
   * The distance is measured to where the fortress was actually raised. The
   * кремль stands on the second island and where that is comes out of the
   * seed, so there is no coordinate to write down - see `kremlinAt`.
   */
  function stepMusic(dt: number): void {
    if (music === null || !musicOn) return;
    // Nothing is decided until there is something to play it with: before
    // the first click there is no AudioContext, and a track handed over then
    // is a track that never starts while the director counts it as playing.
    if (!audio.ready) return;
    const at = world.kremlinAt;
    music.nearKremlin(at === null ? null
      : Math.hypot(player.position.x - at.x, player.position.z - at.z));
    /*
     * And the nearest Змій, measured to the beast rather than to his лігво.
     *
     * The кремль is a place and does not move; he does. What the cue is for
     * is the moment he breaks off circling and comes at you, so the distance
     * that matters is to him.
     */
    let nearest: number | null = null;
    for (const beast of dragons) {
      if (beast.slain) continue;
      const away = Math.hypot(
        player.position.x - beast.x, player.position.z - beast.z);
      if (nearest === null || away < nearest) nearest = away;
    }
    music.nearDragon(nearest);
    const start = music.step(dt);
    if (start !== null) {
      audio.playMusic(`./music/${start.file}`);
      /*
       * And it says what it is putting on.
       *
       * Because "немає музики" was somebody standing in a world that was
       * about to play something and had no way of knowing it. A name on the
       * screen for a few seconds is the difference between silence that is
       * between two tracks and silence that is broken.
       *
       * The title, in English, whatever the game is being played in: they
       * are the composer's names for them and they are the same names on
       * his own list.
       */
      notify(`♪  ${start.title}`, 4000);
    }
  }

  /** Turn it off, or on. Off cuts what is playing; on waits for the gap. */
  function setMusic(on: boolean): void {
    musicOn = on;
    if (!on) { music?.stop(); stopMenuMusic(); }
    // On, on the home screen, means now: there is no rotation out here to
    // wait for a gap in.
    else if (!grown) playMenuMusic();
  }
  /* ---- the home screen's own music -------------------------------------- */

  /**
   * The menu track, which belongs to no world.
   *
   * "Main Menu (The Steppe's Awakening) на головну сторінку." The director is
   * made when a world is grown and this plays before there is one, so it is
   * started by hand rather than through the rotation - and the rotation is
   * told never to pick it by its being a cue.
   *
   * It starts on a click because a browser will not make an AudioContext
   * before a gesture, and the home screen is a screen full of buttons.
   */
  const MENU_TRACK = TRACKS.find((t) => t.cue === "menu") ?? null;
  /** The timer that starts it again after it has run out. */
  let menuAgain: ReturnType<typeof setTimeout> | null = null;

  function playMenuMusic(): void {
    if (MENU_TRACK === null || !musicOn || !audio.ready) return;
    // Only before there is a world. Opening the menu over a game in progress
    // must not take the world's music away from it.
    if (grown) return;
    if (audio.musicPlaying) return;
    audio.playMusic(`./music/${MENU_TRACK.file}`);
    /*
     * And again after it, with the same silence the world gets.
     *
     * A timer rather than the director's own clock, because the home screen
     * has no frame loop of its own to step one with - and somebody sitting
     * on the menu reading the map should not be left in silence for ever.
     */
    if (menuAgain !== null) clearTimeout(menuAgain);
    menuAgain = setTimeout(
      playMenuMusic, (MENU_TRACK.seconds + GAP_SHORT) * 1000);
  }

  /** Off, and staying off: a world is starting, or the music was turned off. */
  function stopMenuMusic(): void {
    if (menuAgain !== null) { clearTimeout(menuAgain); menuAgain = null; }
    audio.stopMusic();
  }


  /*
   * And nothing plays into an empty room.
   *
   * A tab left open in the background playing music is the thing people
   * close the tab over. The world keeps running - other people are in it -
   * but the sound stops.
   */
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) { music?.stop(); audio.stopMusic(); }
  });

  /*
   * Four handles, for `check:music` and nothing else.
   *
   * The rules are tested without a browser; what is left needs one - that
   * the file is served, that it decodes, that it is not fetched before it is
   * wanted, and that the switch switches. None of those can be seen from
   * outside the page, and walking to the кремль to find out would be a
   * minute of swimming in a check. The same trick the map page uses for what
   * its last frame was made of.
   */
  const debug = window as unknown as {
    __atKremlin?: () => { x: number; z: number } | null;
    __goToKremlin?: () => void;
    __give?: (id: number, count: number, slot: number) => void;
    __camp?: () => {
      chests: number; stacks: number; buried: number; farthest: number;
    };
    __walkable?: () => boolean;
    __aimed?: () => { x: number; y: number; z: number; id: number } | null;
    __blockAt?: (x: number, y: number, z: number) => number;
    __whereAmI?: () => { x: number; y: number; z: number };
    __others?: () => { id: number; name: string; x: number; z: number }[];
    __drawn?: () => { id: number; x: number; y: number; z: number }[];
    __musicPlaying?: () => boolean;
    __audioClock?: () => number;
    __musicOff?: () => void;
    __chestsNear?: () => { x: number; y: number; z: number }[];
    __standAndLook?: (x: number, y: number, z: number, yaw: number, pitch: number) => void;
  };
  /*
   * Hardcore, seen from outside the page: чи стоять скрині і чи є дорога.
   *
   * Both are laid once, as the world is grown, and neither leaves a mark on
   * the screen - so a browser check has no other way to ask whether the two
   * halves of hardcore actually happened. The kit itself is checked without
   * a browser; what is asked here is only "did this world get one".
   */
  debug.__camp = () => {
    let chests = 0;
    let stacks = 0;
    let buried = 0;
    let farthest = 0;
    const heart = world.islands[0] ?? { x: worldSpawn.x, z: worldSpawn.z };
    const middle = { x: Math.round(heart.x), z: Math.round(heart.z) };
    for (let dx = -16; dx <= 16; dx++) {
      for (let dz = -16; dz <= 16; dz++) {
        const [x, z] = [middle.x + dx, middle.z + dz];
        for (let y = 2; y < world.opts.sizeY * 16 - 2; y++) {
          if (!isChest(world.getBlock(x, y, z))) continue;
          const held = world.containers.open(x, y, z).slots
            .filter((slot) => slot !== null).length;
          if (held === 0) continue;
          chests++;
          stacks += held;
          // A скриня with the hill on top of it is a скриня nobody finds.
          if (world.getBlock(x, y + 1, z) !== BlockId.Air) buried++;
          farthest = Math.max(farthest, Math.hypot(dx, dz));
        }
      }
    }
    return { chests, stacks, buried, farthest: Math.round(farthest) };
  };
  debug.__walkable = () => {
    const near = world.islands[0] ?? null;
    const gate = world.kremlinAt;
    if (near === null || gate === null) return false;
    // As far as the gate mouth, which is past where the road stops: what is
    // asked is whether the two cuts meet, not whether the road exists.
    const to = { x: gate.x - GATE_MOUTH, z: gate.z };
    return walkLine((x, y, z) => isSolidAt(x, y, z), near, to,
      world.groundAt(Math.round(near.x), Math.round(near.z)) + 1).ok;
  };
  /*
   * What is under the crosshair, and what is in a cell. For `check:adventure`.
   *
   * Digging takes time now, so a check about it has to name a block, hit it,
   * and then ask whether it is still there - three things a browser cannot
   * see. Reading them off the page is the whole of the trick; nothing here
   * changes the world.
   */
  debug.__aimed = () => {
    const hit = aim();
    if (hit === null) return null;
    return {
      x: hit.x, y: hit.y, z: hit.z, id: world.getBlock(hit.x, hit.y, hit.z),
    };
  };
  debug.__blockAt = (x, y, z) => world.getBlock(x, y, z);
  // Where this page's own player is standing. For `check:war`, which has to
  // ask whether two people woke up on opposite sides of a wall.
  // Everybody else, as this page has them. For checks about two players
  // being where the match says they are.
  debug.__drawn = () => renderer.drawnPlayers();
  debug.__others = () => net.players.map((p) => ({
    id: p.id, name: p.name, x: Math.round(p.x), z: Math.round(p.z),
  }));
  debug.__whereAmI = () => ({
    x: Math.round(player.position.x),
    y: Math.round(player.position.y),
    z: Math.round(player.position.z),
  });
  debug.__atKremlin = () => world.kremlinAt;
  /* For the checks that look at a скриня: where they are, and a place to stand. */
  debug.__chestsNear = () => {
    const out: { x: number; y: number; z: number }[] = [];
    const p = player.position;
    for (let dx = -16; dx <= 16; dx++) for (let dz = -16; dz <= 16; dz++) {
      for (let dy = -8; dy <= 8; dy++) {
        const [x, y, z] = [Math.floor(p.x) + dx, Math.floor(p.y) + dy, Math.floor(p.z) + dz];
        if (isChest(world.getBlock(x, y, z))) out.push({ x, y, z });
      }
    }
    return out;
  };
  debug.__standAndLook = (x, y, z, yaw, pitch) => {
    player.position.x = x; player.position.y = y; player.position.z = z;
    player.yaw = yaw; player.pitch = pitch;
  };
  debug.__goToKremlin = () => {
    const at = world.kremlinAt;
    if (at === null) return;
    player.position.x = at.x;
    player.position.z = at.z + 20;
    player.position.y = world.groundAt(at.x, at.z + 20) + 2;
  };
  /**
   * Put a stack in a belt slot. For checks, and for nothing else.
   *
   * A check that wants a требушет in hand had to open the bag, search the
   * комора, click a shelf, click a slot and hope - five gestures and a
   * repaint, and it landed the stack somewhere else about one run in three.
   * None of that is what the check is about, and a check that fails for
   * reasons of its own teaches nobody anything.
   */
  debug.__give = (id: number, count: number, slot: number) => {
    inventory.set(slot, { id, count });
    ui.select(slot);
    ui.refresh();
    recipes.refresh();
  };
  debug.__musicPlaying = () => audio.musicPlaying;
  // The audio clock, which stops when the context is suspended. It is the
  // only honest way to ask "is sound actually running" from outside.
  debug.__audioClock = () => audio.clock;
  debug.__musicOff = () => setMusic(false);

  function manTheCastle(): void {
    const at = world.castleAt;
    if (at === null) return;
    for (const post of CASTLE_POSTS) {
      const cell = castleCell(at, post);
      orcs.push(makeOrc(
        { x: cell.x + 0.5, y: cell.y, z: cell.z + 0.5 }, "skeleton"));
    }
    const seat = castleCell(at, CASTLE_THRONE);
    orcs.push(makeOrc(
      { x: seat.x + 0.5, y: seat.y, z: seat.z + 0.5 }, "chakhlyk"));
    console.info(
      `замок at ${at.x},${at.y},${at.z}: ${CASTLE_POSTS.length} guards and him`);
  }

  function garrison(): void {
    const lairs = world.standingLairs();
    for (const lair of lairs) {
      for (let i = 0; i < 2; i++) {
        orcs.push(makeOrc(
          world.findSpawn(lair.x + (i === 0 ? 3 : -3), lair.z + 2),
          i === 0 ? "sabre" : "archer",
          lair,
        ));
      }
    }
    const first = lairs[0];
    if (first !== undefined && !orcs.some((o) => o.breed.kind === "leader")) {
      orcs.push(makeOrc(world.findSpawn(first.x, first.z + 4), "leader"));
    }
  }

  /**
   * Where each body was last seen, so it can be drawn between.
   *
   * Snapshots arrive fifteen a second and the screen draws sixty: a position
   * applied the moment it lands is held for four frames and then jumps, and
   * everybody else in the world moves like a chess piece. See core/net/smooth.
   */
  const trails = new Map<string, Trail>();
  function trailFor(key: string, delay?: number): Trail {
    let trail = trails.get(key);
    if (trail === undefined) { trail = new Trail(delay); trails.set(key, trail); }
    return trail;
  }

  /**
   * Bring the local orcs in line with the server's.
   *
   * Online, nothing here decides anything about an orc: the raid clocks, the
   * pathing, the blows and the hit points all live on the server, and this is
   * the drawing of what it said. It is still a list of real `Orc` objects
   * rather than a shape of its own, because everything that already reads
   * that list - the name over its head, the arrow that flies at it, the model
   * on the screen - goes on working without knowing where the orcs come from.
   *
   * The id is the server's. Two clients each numbering their own orcs from
   * one would send their sabres at each other's.
   */
  function mirrorOrcs(list: readonly OrcState[]): void {
    const seen = new Set<number>();
    for (const state of list) {
      if (!(state.kind in ORC_BREEDS)) continue;
      seen.add(state.id);
      let orc = orcs.find((o) => o.id === state.id);
      if (orc === undefined) {
        orc = mirrorOrc(state.id,
          { x: state.x, y: state.y, z: state.z }, state.kind as OrcKind);
        orcs.push(orc);
      }
      /*
       * Drawn between the two places it was last seen, not at the newest.
       *
       * A mirror never steps itself, so without this an orc is set down
       * fifteen times a second and stands perfectly still in between.
       */
      const trail = trailFor(`orc:${state.id}`);
      trail.push({
        x: state.x, y: state.y, z: state.z, yaw: state.yaw, pitch: 0,
      }, net.arrivedAt);
      const now = performance.now() / 1000;
      const where = trail.at(now);
      /*
       * The legs come from the snapshots, not from the frame.
       *
       * They are driven by `vx`/`vz`, and the difference between two
       * interpolated positions a frame apart is the same speed divided by
       * four - which had every orc on the island tiptoeing.
       */
      const flat = trail.speed;
      orc.vx = flat; orc.vz = 0;
      orc.x = where.x;
      orc.y = where.y;
      orc.z = where.z;
      orc.yaw = where.yaw;
      // Its hit points are the server's; the flash is ours to draw.
      if (state.hurt === true) orc.vitals.showBlow();
      // So is the swing of its arm: the count comes over, the clock is ours.
      orc.struck = state.blows ?? 0;
    }
    for (let i = orcs.length - 1; i >= 0; i--) {
      if (!seen.has(orcs[i]!.id)) orcs.splice(i, 1);
    }
  }

  /**
   * Bring the herd, the карасі and the villagers in line with the server's.
   *
   * The same shape as `mirrorOrcs` and for the same reasons - the server's
   * ids, no health of our own, drawn between snapshots. What is different is
   * that only what is *near* arrives: a body that falls out of the list has
   * not died, it has walked over a hill, so it is simply forgotten rather
   * than mourned.
   */
  function mirrorHerd(): void {
    const now = performance.now() / 1000;
    const place = <T extends { id: number; x: number; y: number; z: number;
      yaw: number }>(key: string, body: T, from: {
        x: number; y: number; z: number; yaw: number; pitch?: number }): number => {
      // The herd comes every other tick, so its trail is drawn that much
      // further behind; see Realm.tick.
      const trail = trailFor(key, 2 / 15);
      trail.push({ ...from, pitch: from.pitch ?? 0 }, net.arrivedAt);
      const at = trail.at(now);
      body.x = at.x; body.y = at.y; body.z = at.z; body.yaw = at.yaw;
      return trail.speed;
    };

    const seenBeasts = new Set<number>();
    for (const state of net.beasts) {
      /*
       * Whatever breed the server says, as long as it is one we know.
       *
       * A list of the three that existed at the time was what used to be
       * here, so the day a fourth was added every dog on the server was
       * silently dropped on the way in.
       */
      const breed = state.kind as BeastKind;
      if (BREEDS[breed] === undefined) continue;
      seenBeasts.add(state.id);
      const found = beasts.find((b) => b.id === state.id);
      const beast = found ?? mirrorBeast(state.id, breed,
        { x: state.x, y: state.y, z: state.z });
      if (found === undefined) beasts.push(beast);
      // The legs come from the snapshots; see mirrorOrcs.
      beast.vx = place(`beast:${state.id}`, beast, state);
      beast.vz = 0;
      if (state.hurt === true) beast.vitals.showBlow();
    }
    for (let i = beasts.length - 1; i >= 0; i--) {
      if (!seenBeasts.has(beasts[i]!.id)) beasts.splice(i, 1);
    }

    /*
     * The човни, which are the server's list and not ours.
     *
     * Mirrored outright rather than eased between snapshots: a boat with
     * nobody in it does not move at all, and one with somebody in it moves
     * with a body that is already being eased - the player standing in it.
     * Our *own* boat is left alone, because the oars are ours and a snapshot
     * a fifth of a second old would drag it backwards under our feet.
     */
    const seenBoats = new Set<number>();
    for (const state of net.boats) {
      seenBoats.add(state.id);
      const had = boats.find((b) => b.id === state.id);
      const one = had ?? mirrorBoat(state.id,
        { x: state.x, y: state.y, z: state.z }, state.yaw);
      if (had === undefined) boats.push(one);
      one.rider = state.rider;
      if (aboard !== null && aboard.id === state.id) continue;
      one.x = state.x;
      one.y = state.y;
      one.z = state.z;
      one.yaw = state.yaw;
    }
    for (let i = boats.length - 1; i >= 0; i--) {
      const gone = boats[i]!;
      if (seenBoats.has(gone.id)) continue;
      // Somebody took it out of the water, or we walked out of sight of it.
      if (aboard === gone) aboard = null;
      boats.splice(i, 1);
    }

    /*
     * And the танки, on exactly the same terms.
     *
     * Ours is left where our own levers put it - a snapshot a fifth of a
     * second old would drag the machine backwards under the driver - and
     * everybody else's is mirrored outright. The hit count is always the
     * server's: it is what decides when one goes up, and two pages counting
     * separately would disagree about the fifth shell.
     */
    const seenTanks = new Set<number>();
    for (const state of net.tanks) {
      seenTanks.add(state.id);
      const had = tanks.find((t) => t.id === state.id);
      const one = had ?? mirrorTank(state.id,
        { x: state.x, y: state.y, z: state.z }, state.yaw, state.hits);
      if (had === undefined) tanks.push(one);
      one.rider = state.rider;
      one.hits = state.hits;
      if (driving !== null && driving.id === state.id) continue;
      one.x = state.x;
      one.y = state.y;
      one.z = state.z;
      one.yaw = state.yaw;
    }
    for (let i = tanks.length - 1; i >= 0; i--) {
      const gone = tanks[i]!;
      if (seenTanks.has(gone.id)) continue;
      // Blown up, picked up, or simply out of sight.
      if (driving === gone) driving = null;
      tanks.splice(i, 1);
    }
    /*
     * And the server has the last word on whose boat it is.
     *
     * Getting in is optimistic - the hull answers the oars on the frame the
     * button was pressed rather than a round trip later - so this is where a
     * refusal lands: somebody else was already in it, and the snapshot says
     * so.
     */
    if (aboard !== null && net.online &&
      aboard.rider !== null && aboard.rider !== net.joined?.you) {
      aboard = null;
      notify(say("boat-taken", tone.tone, lang.value), 1800);
    }

    const seenFish = new Set<number>();
    for (const state of net.fish) {
      seenFish.add(state.id);
      const had = fish.find((f) => f.id === state.id);
      const one = had ?? mirrorFish(state.id,
        { x: state.x, y: state.y, z: state.z });
      if (had === undefined) fish.push(one);
      one.vx = place(`fish:${state.id}`, one, state);
      one.vz = 0;
      one.pitch = state.pitch;
      if (state.hurt === true) one.vitals.showBlow();
    }
    for (let i = fish.length - 1; i >= 0; i--) {
      if (!seenFish.has(fish[i]!.id)) fish.splice(i, 1);
    }

    const seenFolk = new Set<number>();
    for (const state of net.folk) {
      seenFolk.add(state.id);
      const knew = villagers.find((v) => v.id === state.id);
      const man = knew ?? mirrorVillager(state.id, state.name, state.skinId,
        state.line as LineKey, { x: state.x, y: state.y, z: state.z });
      if (knew === undefined) villagers.push(man);
      place(`folk:${state.id}`, man, state);
      /*
       * He has just said something, so say it out loud.
       *
       * A count rather than his remaining seconds, for the reason a blow is
       * counted: at fifteen snapshots a second a line still running looks
       * exactly like one just started, and he would repeat himself for three
       * and a half seconds.
       */
      const said = state.said ?? 0;
      if (said !== man.said) {
        man.said = said;
        man.speaking = SPEECH_SECONDS;
        sayAloud(man.line);
      }
      if (state.hurt === true) man.vitals.showBlow();
    }
    for (let i = villagers.length - 1; i >= 0; i--) {
      if (!seenFolk.has(villagers[i]!.id)) villagers.splice(i, 1);
    }
  }

  /**
   * Land a wave on the players' shore.
   *
   * Not at the lairs: an orc that has to walk the length of the neck arrives
   * long after the raid it belongs to, and the half-hour would mean nothing.
   * They come ashore on the side facing their own island, which is the side a
   * player watching the water would expect them from.
   */
  /**
   * Where the orcs walk when they can see nobody, or null for "stay put".
   *
   * "Орки будуть йти на другий острів." The island the player is standing on,
   * not the player - an army marches on a place. Once they are within sight
   * of somebody the chase takes over, which is what turns a march into a
   * raid at the moment it reaches you.
   *
   * The road cut between the two islands is what makes this possible at all:
   * an орк walks straight at what it wants and climbs a block, so without a
   * ramp up the far shore a march ends at the water's edge.
   */
  function marchOn(): { x: number; z: number } | null {
    if (!brutal || peaceLeft > 0) return null;
    let best: { x: number; z: number } | null = null;
    let bestAway = Infinity;
    for (const island of world.islands) {
      const away = Math.hypot(
        island.x - player.position.x, island.z - player.position.z);
      if (away < bestAway) { bestAway = away; best = { x: island.x, z: island.z }; }
    }
    return best;
  }

  /**
   * The road between the two islands, cut once when the world is made.
   *
   * "Для того щоб вони могли дійти - додай сходи і до замку, і до острова
   * гравця", and then "сходи можна додати незалежно від цього нового режиму"
   * - so every кремль world gets one, hardcore or not. It is a road anybody
   * can walk: the player out to the fortress, and whatever is in the
   * fortress back.
   *
   * From the middle of the near island to the кремль's gate, which faces the
   * near island - it always has.
   *
   * The road is given the gate's sill and arrives at it. That is the whole
   * of what makes this work on more than one seed: the fortress is founded
   * fifteen blocks into the island's dome and its gate comes out thirty
   * blocks away, so where the sill sits relative to the ground in front of
   * it is whatever the hillside happens to be doing - measured across twelve
   * seeds, anything from two blocks under the ground to thirteen over it.
   * Aiming the road at a *number* rather than at the ground is what stops
   * that mattering.
   */
  function cutTheRoad(): void {
    const near = world.islands[0];
    const at = world.kremlinAt;
    if (near === undefined || at === null) return;
    /*
     * As far as the кремль's own way out, and no further.
     *
     * The fortress paves the last sixteen blocks in front of its gate - a
     * cutting through the lip of the hollow it sits in. The road used to be
     * aimed at the sill and run over that cutting, which put a track of mud
     * over the fortress's own stone and had the two of them arguing about
     * the same corridor. Now the road ends where the кремль's paving begins
     * and arrives at the height it begins at.
     */
    const foot = world.kremlinFoot;
    const cut = world.cutRoad(
      { x: Math.round(near.x), z: Math.round(near.z) },
      foot !== null ? { x: foot.x, z: foot.z }
        : { x: at.x - ROAD_STOPS_SHORT, z: at.z },
      undefined, undefined,
      foot !== null ? foot.level : world.kremlinSill ?? undefined);
    console.info(`road to the кремль: ${cut} blocks`);
  }

  /**
   * Four chests of kit, for a game that starts with five minutes on it.
   *
   * "Додай чотири скрині: перша і друга повні сетів броні, третя з шаблями,
   * требушетами, вогняними снарядами, стрілами та луками, четверта з їжею,
   * дронами й усім необхідним для дронів."
   *
   * In the middle of the island, standing on the grass in a row - "постав їх
   * по центру острова", after the first four went in beside where the player
   * wakes and nobody could find them.
   *
   * They were in the world; they were *inside the hill*. The old placing
   * dropped each one from two blocks over the player's head and took the
   * first solid block under it, which is what a falling item does - but the
   * island is a dome, and two steps out from the middle the ground is
   * already higher than the player's head. So the search started underground
   * and stopped at the first stone it met: four full скрині buried in the
   * hillside, invisible, with a check that could see them perfectly well
   * because it read the world rather than looked at it.
   *
   * Now each one is put where a person could stand: on top of the ground,
   * with two clear blocks over it. That is `findSpawn`'s question, and it is
   * asked per chest.
   */
  function stockTheCamp(): void {
    const heart = world.islands[0] ?? { x: worldSpawn.x, z: worldSpawn.z };
    const middle = {
      x: Math.round(heart.x), z: Math.round(heart.z),
    };

    /** The top of the ground here, if somebody could stand on it. */
    const standingOn = (x: number, z: number): number | null => {
      const ground = world.groundAt(x, z);
      if (ground <= world.seaLevel) return null;
      if (world.getBlock(x, ground + 1, z) !== BlockId.Air) return null;
      if (world.getBlock(x, ground + 2, z) !== BlockId.Air) return null;
      return ground + 1;
    };

    /*
     * A row of four through the middle, and the nearest clear ground to each.
     *
     * The row is what you want to see: four chests side by side read as
     * "this is for you" where four scattered ones read as scenery. The
     * search around each is for the tree that happens to be standing there -
     * never far, because the middle of an island is the flattest part of it.
     */
    const taken = new Set<string>();
    for (let i = 0; i < CAMP_KIT.length; i++) {
      const want = { x: middle.x - 1 + i, z: middle.z + 1 };
      let at: { x: number; y: number; z: number } | null = null;
      for (let radius = 0; radius <= 6 && at === null; radius++) {
        for (let dz = -radius; dz <= radius && at === null; dz++) {
          for (let dx = -radius; dx <= radius && at === null; dx++) {
            if (radius > 0 && Math.abs(dx) !== radius && Math.abs(dz) !== radius) {
              continue;
            }
            const x = want.x + dx;
            const z = want.z + dz;
            if (taken.has(`${x},${z}`)) continue;
            const y = standingOn(x, z);
            if (y !== null) at = { x, y, z };
          }
        }
      }
      if (at === null) continue;
      taken.add(`${at.x},${at.z}`);
      world.setBlock(at.x, at.y, at.z, chestId(0));
      const box = world.containers.open(at.x, at.y, at.z);
      CAMP_KIT[i]!.forEach((stack, slot) => {
        if (slot < CHEST_SLOTS) box.set(slot, { ...stack });
      });
      world.containers.markStocked(at.x, at.y, at.z);
    }
  }

  function landRaid(count: number): void {
    const near = world.islands[0] ?? home;
    const far = world.islands[1] ?? home;
    const toward = Math.atan2(far.z - near.z, far.x - near.x);
    for (let i = 0; i < count; i++) {
      // Spread along the shore rather than in a heap, so a single wall does
      // not hold the whole wave.
      const angle = toward + (i / Math.max(1, count - 1) - 0.5) * 0.9;
      const away = near.radius * 0.92;
      /*
       * One archer in every three.
       *
       * Enough that standing in the open costs you, few enough that a wave is
       * still something you can walk into and fight. All sabres was a wave you
       * could hold at a doorway forever; all archers would be one you could
       * not approach at all.
       */
      orcs.push(makeOrc(world.findSpawn(
        Math.round(near.x + Math.cos(angle) * away),
        Math.round(near.z + Math.sin(angle) * away),
      ), i % 3 === 2 ? "archer" : "sabre"));
    }
  }

  // ---- speech ------------------------------------------------------------
  const speech = document.getElementById("speech") as HTMLDivElement;
  const speechName = document.createElement("b");
  const speechLine = document.createTextNode("");
  speech.append(speechName, speechLine);

  /** Park whatever is being said over the head of whoever is saying it. */
  /**
   * Names over heads, and over whatever is under the crosshair.
   *
   * Other players get one always: a world with people in it and no names is a
   * world of identical strangers, and the бій is the moment you most need to
   * know which of them is your friend. Animals and orcs get one only when you
   * look at them, because a field of labels is not a field.
   */
  function updateNameTags(): void {
    const tags: NameTag[] = [];

    for (const other of net.players) {
      tags.push({
        text: other.name,
        x: other.x, y: other.y + PLAYER_HEIGHT + 0.35, z: other.z,
      });
    }

    const looked = input.locked ? aimedCreature() : null;
    if (looked !== null) {
      tags.push({ ...looked, aimed: true });
    }
    nameTags.show(tags, (x, y, z) => renderer.project(x, y, z));
  }

  /**
   * What is under the crosshair, and what it is called.
   *
   * Further than you can reach, on purpose: "what is that thing over there"
   * is the question a name answers, and a name you can only read once you are
   * close enough to hit it answers it too late.
   */
  function aimedCreature(): NameTag | null {
    const eye = player.eyePosition();
    const look = player.lookDirection();
    const struck = pickEntity(
      [...villagers, ...orcs, ...beasts], eye.x, eye.y, eye.z,
      look.x, look.y, look.z, NAME_RANGE, ORC_WIDTH, ORC_HEIGHT,
    );
    /*
     * A Змій is named from further away than anything else.
     *
     * His own box, because he is four blocks tall and nearly three across
     * and one `pickEntity` takes one size for its whole list; and his own
     * range, because a thing that size is a landmark - by the time you are
     * within the range a пиг is named at, you are inside his.
     */
    const seenDragon = pickEntity(
      dragons.filter((d) => !d.slain), eye.x, eye.y, eye.z,
      look.x, look.y, look.z, NAME_RANGE * 3, ZMII.width, ZMII.height,
    );
    if (seenDragon !== null && (struck === null ||
        seenDragon.distance < struck.distance)) {
      const wall = raycastVoxels(
        eye.x, eye.y, eye.z, look.x, look.y, look.z,
        NAME_RANGE * 3, isOpaqueAt,
      );
      if (wall !== null && wall.distance < seenDragon.distance) return null;
      const d = seenDragon.entity;
      return {
        text: ZMII.label,
        x: d.x, y: d.y + ZMII.height + 0.6, z: d.z,
      };
    }
    if (struck === null) return null;
    /*
     * And not through a wall.
     *
     * "Щоб неможливо було бачити ніки тварин якщо на них навестися через
     * блок." Hitting one through a block was already refused - the block ray
     * has to come back further away than the body does - and reading its
     * name was not, so a пиг on the other side of a хата announced itself
     * through the wall.
     *
     * The block ray is cast the length of the name range rather than the
     * length of an arm: a name is readable much further away than a blow
     * reaches, and using `aim()` here would have let everything past twelve
     * blocks be named through anything.
     */
    const wall = raycastVoxels(
      eye.x, eye.y, eye.z, look.x, look.y, look.z, NAME_RANGE, isOpaqueAt,
    );
    if (wall !== null && wall.distance < struck.distance) return null;
    const it = struck.entity;

    const villager = villagers.find((v) => v === it);
    if (villager !== undefined) {
      return {
        text: villager.name,
        x: villager.x, y: villager.y + VILLAGER_HEIGHT + 0.35, z: villager.z,
      };
    }
    const orc = orcs.find((o) => o === it);
    if (orc !== undefined) {
      return {
        text: lang.value === "en" ? orc.breed.en : orc.breed.label,
        x: orc.x, y: orc.y + ORC_HEIGHT + 0.35, z: orc.z,
      };
    }
    const beast = beasts.find((b) => b === it);
    if (beast !== undefined) {
      return {
        text: lang.value === "en" ? beast.breed.en : beast.breed.label,
        x: beast.x, y: beast.y + beast.breed.height + 0.35, z: beast.z,
      };
    }
    return null;
  }

  function updateSpeech(): void {
    // The one who spoke most recently, not the first in the list: hit her
    // after him and it is her answer you want to read.
    let talking: Villager | undefined;
    for (const v of villagers) {
      if (v.speaking > 0 && (talking === undefined || v.speaking > talking.speaking)) {
        talking = v;
      }
    }
    if (talking === undefined) {
      speech.hidden = true;
      return;
    }
    const at = renderer.project(
      talking.x, talking.y + VILLAGER_HEIGHT + 0.35, talking.z,
    );
    // Behind the camera projects to a point in front of it - hide it instead.
    speech.hidden = !at.visible;
    if (!at.visible) return;

    speech.style.left = `${Math.round(at.x)}px`;
    speech.style.top = `${Math.round(at.y)}px`;
    speechName.textContent = talking.name;
    speechLine.nodeValue = say(talking.line, tone.tone, lang.value);
  }

  // ---- time of day -------------------------------------------------------
  // Start mid-morning so the first thing you see is lit.
  /*
   * Noon on the hill, and it stays there.
   *
   * «Король гори» is five minutes long and the sun does not need to set in
   * the middle of one: a race that goes dark halfway is a race decided by
   * whoever happened to be near the top when it did. Every other map keeps
   * its morning and its evening, which is most of what makes them a place
   * rather than a match.
   */
  const cycle = new DayNightCycle(playing.terrain === "hill" ? 0.5 : 0.32);
  if (playing.terrain === "hill") cycle.timeScale = 0;
  /*
   * On somebody else's world the hour is theirs.
   *
   * Every page used to run its own clock, so two people standing in the same
   * field could be at noon and at midnight and neither was wrong. It looked
   * like a curiosity until something started coming out of the lakes after
   * dark: a мавка the server has put down in front of you at midnight, on a
   * screen showing noon, is a bug that cannot be explained.
   */
  if (joined !== null) cycle.t = joined.time;

  // ---- inventory ---------------------------------------------------------
  // Starts empty: everything you place has to be mined first.
  const inventory = new Inventory();
  // The crafting grid is just a nine-slot inventory, so every click rule -
  // pick up, merge, split - works on it unchanged.
  const craftGrid = new Inventory(GRID_SLOTS);
  // What is worn. Four slots in ARMOUR_SLOTS order, and an Inventory rather
  // than four fields so every click rule works on it unchanged - the same
  // trick the crafting grid uses.
  const worn = new Inventory(ARMOUR_SLOTS.length);
  /** Points of armour on the body right now. */
  const armourPoints = (): number => {
    let total = 0;
    for (let i = 0; i < ARMOUR_SLOTS.length; i++) {
      const piece = worn.get(i);
      if (piece === null) continue;
      const def = itemArmour(piece.id);
      // Only counted when it is in the slot it belongs to. The UI refuses to
      // put it anywhere else, and a save could still carry a bad one.
      if (def !== undefined && def.slot === ARMOUR_SLOTS[i]) total += def.points;
    }
    return total;
  };
  // One tooltip shared by every panel: only one thing is ever hovered.
  const tooltip = new Tooltip(document.body);
  const ui = new InventoryUI(inventory, craftGrid, worn, document.body, tooltip);
  ui.setCreative(creative.value);
  /*
   * And which map this is, which decides what exists to be made.
   *
   * The танк and its снаряди are «Сучасна війна»'s alone - "поки він має
   * бути доступний тільки на мапі сучасна війна" - so the bag and the book
   * both need to know where they are standing.
   */
  ui.setMap(playing.id);
  // Сила and броня, above the hotbar where the eye already is.
  const vitals = new VitalsUI(document.body);
  const nameTags = new NameTags(document.body);
  // Mounted inside the inventory window, not beside it.
  const recipes = new RecipeBook(inventory, craftGrid, ui.bookHost, tooltip);
  // The book, like the bag, leaves out what belongs to another map.
  recipes.setMap(playing.id);
  // The book, blinking in the far corner when something arrives that it
  // knows about. See `watchTheBag`.
  const nudge = new RecipeNudge(document.body);
  /*
   * Пригоди, and the one set that says which are done.
   *
   * Filled from the save on the way in - see `loaded` below - and read by
   * `captureSave` on the way out. The book and the save share this object
   * rather than each keeping a copy, because two copies of "have I been paid
   * for this" is one copy too many.
   */
  const claimedQuests = new Set<string>();
  const quests = new QuestBook(inventory, claimedQuests, ui.questHost);
  ui.onBookToggle = (open) => {
    if (open) { recipes.refresh(); nudge.hide(); }
  };
  // Drawn as it opens, never on a clock: the numbers come off the bag.
  ui.onQuestsToggle = (open) => { if (open) quests.refresh(); };
  quests.onClaim = (quest) => {
    ui.refresh();
    recipes.refresh();
    notify(`${pick(quest.name, lang.value)}: ${itemLabel(quest.reward.id)}`, 4000);
    audio.play("place");
  };
  quests.onRefused = (why) => {
    // The only refusal a player can do anything about is a full bag.
    if (why === "full") notify(uiText("quests-full", lang.value), 4000);
  };

  /**
   * Everything the player has ever had in their hands, and the nudge.
   *
   * A recipe book nobody opens is a recipe book nobody has, and most of what
   * is worth making here is not guessable: there is nothing about a lump of
   * смола that says "this is half a смолоскип". So the moment to point at the
   * book is the moment such a thing lands in the bag.
   *
   * Seeded from whatever is already in the bag rather than started empty, so
   * loading a world with a full inventory does not open with a blink about
   * planks somebody cut an hour ago. What counts as new is new *to this
   * session's owner of the bag*, which is the honest meaning of "I have not
   * seen this before" when there is nothing in the save that remembers.
   */
  const everHeld = new Set<number>();
  const seedTheBag = (): void => {
    for (const slot of [...inventory.slots, ...worn.slots]) {
      if (slot !== null) everHeld.add(slot.id);
    }
  };

  /**
   * Notice a thing arriving that the book has something to say about.
   *
   * Walked once a frame off the slot array. Forty slots and a Set lookup
   * each is nothing next to a frame, and doing it here rather than at every
   * `inventory.add` in this file is the difference between one place that
   * cannot be forgotten and a dozen that can - there are nine of those call
   * sites, plus the pickups, plus the crafting result.
   */
  function watchTheBag(): void {
    // The deciding is `noticeNewIngredients`, in core, where it can be asked
    // questions; what is left here is when to blink.
    const fresh = noticeNewIngredients(inventory.slots, everHeld);
    // Never over the book it is pointing at, and never on the home screen.
    if (fresh.length > 0 && !ui.open && grown) nudge.show();
  }

  ui.onChange = () => recipes.refresh();
  // What the panel throws away lands in the world, which is the game's half.
  ui.onDrop = (stack) => { throwStack(stack); };
  recipes.onFill = () => ui.refresh();
  ui.onStationsChange = (stations) => recipes.setStations(stations);

  // ---- character ---------------------------------------------------------
  /*
   * Whoever the home screen settled on, not whatever storage said.
   *
   * This runs when the page loads and the strip is clicked long afterwards,
   * so reading the key again here would read it *before* the choice was
   * made: picking a селянка on the home screen started a game as Силач, and
   * the only sign of it was the name in the debug panel.
   */
  const skins = new SkinPicker(document.body, homeSkin.selected);
  renderer.setSkin(skins.selected);
  skins.onSelect = (def) => {
    renderer.setSkin(def);
    // Show the change immediately - there is no point picking a character you
    // cannot see.
    renderer.cameraMode = "third";
    homeSkin.showChosen(def);
  };
  // And the other way about: the strip is the same choice, seen earlier.
  homeSkin.onSelect = (def) => { skins.select(def); };

  /*
   * The server's world, once it has finished sending it.
   *
   * Chunks arrive as they come and are laid straight in; block edits arrive
   * batched; a building arrives as an instruction and is replayed here with
   * the same pure `raise` the server ran. Every one of those bypasses
   * `setBlock`, which is why the light and the lair register are told to
   * rebuild - the trap that has caught this project twice already.
   */
  if (joined !== null) {
    net.onChunk = (chunk) => {
      world.loadChunk(chunk.cx, chunk.cy, chunk.cz, chunk.blocks);
      renderer.invalidateMeshes();
    };
    net.onBlocks = (edits) => {
      world.setBlocks(edits.map((e) => ({ x: e.x, y: e.y, z: e.z, id: e.id })));
    };
    // Somebody else's bolt, watched rather than only felt.
    net.onBolt = (b) => {
      /*
       * Somebody else's fire, and which kind of fire it is.
       *
       * A трeбушет's bolt arrives with its speed already in the direction -
       * hence the 1 - and his cold fire arrives as a heading to be thrown at
       * `COLD_SPEED`. Getting that wrong makes his fire hang in the air.
       */
      firebolts.push(b.cold === true
        ? makeFirebolt({ x: b.x, y: b.y, z: b.z },
          { x: b.dx, y: b.dy, z: b.dz }, COLD_SPEED, true)
        : makeFirebolt({ x: b.x, y: b.y, z: b.z },
          { x: b.dx, y: b.dy, z: b.dz }, 1));
    };
    /*
     * And somebody else's spark.
     *
     * The server has already lit its own copy, and what the fire eats comes
     * back as ordinary edits - this is only so the flames are drawn here
     * too. A fire nobody but the striker can see is a fire that looks like
     * blocks vanishing on their own.
     */
    net.onSpark = (at) => {
      fire.light(at.x, at.y, at.z, world.getBlock(at.x, at.y, at.z), false);
    };
    /*
     * The world's hour, now and then.
     *
     * Set outright rather than eased towards: both ends run the same clock at
     * the same speed, so this is only ever a few seconds out and nobody sees
     * it move - except on a tab that was asleep, where it is an hour out and
     * ought to jump.
     */
    net.onSky = (time) => { cycle.t = time; };
    // The clock is shared; a speed left over from playing alone is not.
    cycle.timeScale = 1;
    /*
     * And the server's word on where we are, when it will not take ours.
     *
     * Snapped rather than eased: this only happens when the two of us
     * disagree by more than anybody could have walked, and easing a
     * disagreement that large is a slide across the map. The velocity goes
     * with it, or the body carries on in the direction it was refused for.
     */
    net.onWhere = (at) => {
      player.position.x = at.x;
      player.position.y = at.y;
      player.position.z = at.z;
      player.velocity.x = 0;
      player.velocity.y = 0;
      player.velocity.z = 0;
      if (aboard !== null) { aboard.rider = null; aboard = null; }
      /*
       * And, in a match, this is home from now on.
       *
       * "Кожна команда з'являється на своїй частині мапи" - and somebody who
       * dies there has to wake up in their own quarter rather than at
       * whatever the world's spawn happens to be, which on this map is the
       * middle and therefore inside somebody else's wall.
       */
      if (at.home === true) spawn = { x: at.x, y: at.y, z: at.z };
    };
    net.onRaised = (order) => {
      const building = buildingNamed(order.building);
      if (building === undefined) return;
      const cells: { x: number; y: number; z: number; id: number }[] = [];
      raise(
        building, (order.facing & 3) as DoorWood extends never ? never : 0 | 1 | 2 | 3,
        order.x, order.y, order.z,
        (x, y, z) => world.getBlock(x, y, z),
        (x, y, z) => world.withinBounds(x, y, z),
        (x, y, z, id) => { cells.push({ x, y, z, id }); },
        isNaturalGround,
        world.seaLevel + 1,
      );
      world.setBlocks(cells);
    };
    /*
     * Something got us, and the server is the one that says so.
     *
     * What our armour does about it is still ours: armour, falling and
     * drowning are all the body's own business, and a health bar with two
     * owners is a health bar that flickers.
     */
    /*
     * The шопопало-блок's gift, worked out by the world.
     *
     * It falls at your own feet rather than at the block: online you may
     * have stepped off it by the time the answer arrives, and a drop left
     * behind on a one-block map falls for ever.
     */
    net.onGave = (item, count) => {
      drop([{ item, count }], {
        x: Math.floor(player.position.x),
        y: Math.floor(player.position.y),
        z: Math.floor(player.position.z),
      });
      notify(oneBlockSaid(item, lang.value), 1400);
      ui.refresh();
      recipes.refresh();
    };

    net.onHurt = (amount, from, by) => {
      // Remembered whether or not the blow lands hard enough to say
      // anything: it is who gets the kill if the next one finishes it.
      if (typeof by === "number" && by > 0) struckBy = by;
      if (health.hurt(damageAfterArmour(amount, armourPoints())) <= 0) return;
      audio.play("breakWood", { rate: 0.6 });
      notify(say(from === "player" ? "player-hit" : "orc-hit",
        tone.tone, lang.value), 1200);
    };
    /*
     * Somebody loosed an arrow. Drawn, and nothing more.
     *
     * Into the orcs' list, which online is simply "arrows that are not ours":
     * it is flown against nobody, because the server already flew it and has
     * said what it hit.
     */
    net.onArrow = (a) => {
      orcArrows.push(makeArrow(
        { x: a.x, y: a.y, z: a.z }, { x: a.dx, y: a.dy, z: a.dz }));
      audio.play("breakGrass", { rate: 1.4, volume: 0.5 });
    };
    // An orc went down, and the server rolled what it was carrying - so
    // everybody finds the same gold on the same body.
    net.onFell = (fell) => {
      drop(fell.drops.map((d) => ({ item: d.item, count: d.count })),
        { x: fell.x, y: fell.y, z: fell.z });
    };
    // Somebody else's вибухоблок. The blocks it took are travelling as
    // ordinary edits; this is only so it is heard.
    net.onBoom = (at) => {
      // A танк going up is not a вибухоблок going off: it takes half of
      // everybody's сила within four blocks and leaves the ground alone.
      if (at.tank === true) { tankBlast(at); return; }
      boomHeard(at.x, at.y, at.z);
    };
    /*
     * Somebody else's снаряд, flown here as well.
     *
     * Drawn rather than adjudicated: what it hits is the server's to say,
     * and this page flies it only so that a shell crossing the field is a
     * thing you can see coming and get behind something for.
     */
    net.onShell = (shot) => {
      shells.push(makeShell(
        { x: shot.x, y: shot.y, z: shot.z },
        { x: shot.vx, y: shot.vy, z: shot.vz },
        shot.from,
      ));
    };
    /*
     * Who is on top of the hill.
     *
     * Only said out loud when it changes hands or somebody wins it: a line
     * every second would be a line nobody reads. The steady state is the
     * readout, which shows it whether or not it just changed.
     */
    net.onKing = (king) => {
      const wasKing = crown.id;
      crown.id = king.id; crown.name = king.name; crown.held = king.held;
      // The server's clock is the one that counts: two machines counting
      // separately are two machines disagreeing about when the round ends.
      if (king.phase !== undefined) round.phase = king.phase;
      if (king.left !== undefined) round.left = king.left;
      /*
       * The round ended - whoever won it, and whether anybody did.
       *
       * `over` rather than the old `won`: an empty name is what the screen
       * turns into «Ніхто не дійшов», exactly as the single-player hill has
       * always done. Reading a "somebody won" flag meant a round that ended
       * in a tie, or with nobody on the hill, put no screen up at all and
       * started again under the player with the mouse still captured.
       */
      if (king.over) {
        showRoundOver(king.name);
        audio.play("place", { rate: 1.8 });
        return;
      }
      if (king.id === wasKing) return;
      if (king.id === 0) return;
      notify(king.id === net.joined?.you
        ? say("crown-yours", tone.tone, lang.value)
        : `нагорі: ${king.name}`, 2200);
    };
    /*
     * «Сучасна війна»: the lobby, the clock, and the table at the end.
     *
     * The server is the only clock and the only scoreboard, so the page's
     * whole part in this is to draw what it is told - and, on the one tick
     * where the phase turns from "prepare" to "fight", to say so out loud:
     * the wall coming down is the loudest moment of the match and it happens
     * on the other side of the map from most people.
     */
    net.onWar = (war) => {
      const was = warState?.phase ?? "lobby";
      drawWar(war);
      if (was === "prepare" && war.phase === "fight") {
        notify(say("war-begins", tone.tone, lang.value), 5000);
        audio.play("breakWood", { rate: 0.5 });
      }
      if (was !== "over" && war.phase === "over") {
        audio.play("place", { rate: 1.8 });
      }
    };
    net.onTrouble = () => {
      notify(uiText("net-dropped", lang.value), Infinity);
    };
    /*
     * The server said no to something, and we are still playing.
     *
     * Shown the way the single player game shows it - a line for a few
     * seconds - rather than as a connection failure, which is what it looked
     * like before `build` had a code of its own.
     */
    net.onRefused = (code, why) => {
      /*
       * The match ended and the world went with it.
       *
       * Not a fault and not worth a "зв'язок обірвано": the race is run.
       * The page says so and goes back to the home screen, which is where
       * the next one is started from.
       */
      if (code === "closed") {
        notify(why.length > 0 ? why
          : say("round-closed", tone.tone, lang.value), 6000);
        window.setTimeout(() => { window.location.reload(); }, 2500);
        return;
      }
      if (code !== "build") return;
      notify(say(why === "off-the-map" ? "build-off-map" : "build-no-room",
        tone.tone, lang.value), 3200);
    };
    // What we grew, so the server can refuse us if it is not its world.
    net.ready(world.opts.seed, world.opts.sizeXZ, world.opts.sizeY);
  }

  // ---- growing the world -------------------------------------------------
  /**
   * Build the terrain, and everything that can only be known once it exists.
   *
   * Called once, from whichever of the two things counts as choosing: the
   * Почати button, or a save being there when the page loaded. Safe to call
   * twice - a second press must not regrow the world under somebody.
   */
  function growWorld(kind: "new" | "resume" = "resume"): void {
    if (grown) return;
    grown = true;
    // A game played alone starts here and nowhere else; the joins and the
    // holds say so where they succeed. See platform/played.
    if (joined === null) tellPlayed(playing.id);

    const genStart = performance.now();
    world.generate();
    genMs = performance.now() - genStart;

    /*
     * On the block, on the map that is one block.
     *
     * `findSpawn` looks for ground under a column and there is none anywhere
     * except that one cell - so it is told rather than asked. This is also
     * where anybody who dies comes back to: "поки немає ліжка - блок
     * відновлення той самий перший блок".
     */
    const only = world.oneBlockAt;
    worldSpawn = only !== null
      ? { x: only.x + 0.5, y: only.y + 1, z: only.z + 0.5 }
      : world.findSpawn(Math.round(home.x), Math.round(home.z));
    spawn = worldSpawn;
    player.position.x = worldSpawn.x;
    player.position.y = worldSpawn.y;
    player.position.z = worldSpawn.z;
    lairsLeft = world.standingLairs().length;

    /*
     * Everything that lives in it, now that there is an it.
     *
     * These three ran at startup until the world stopped growing at startup:
     * a herd scattered over a world with no ground in it put fourteen pigs
     * nowhere, forty карасі found no water to be in, and `standingLairs()`
     * on an ungenerated world returns none - so the кремль came up with no
     * garrison at all. Nothing announced it. The island was simply empty.
     */
    /*
     * Nothing lives on the hill.
     *
     * «Король гори» is a cube standing on a floor: no water for карасі, no
     * pasture for a herd, and no lairs for a garrison. Skipped rather than
     * left to find nothing, because "find nothing" is four thousand tries at
     * placing a fish in a world with no water in it.
     */
    if (world.opts.terrain === "adventure" && joined === null) {
      // An adventure stocks itself as it is walked, and there is no island to
      // scatter a herd over - only the couple of hundred blocks that exist.
      restockLife();
      restockFolk();
      /*
       * And the замок gets its garrison, which it never did.
       *
       * `manTheCastle` lived in the branch below - the one for the кремль -
       * and the замок is only ever on *this* map. So on the one map that has
       * one, it was never called at all: sixteen guards and Чахлик were
       * written, tested, drawn and shipped, and not one of them was ever put
       * into a game. "Castle is empty, it is noone there", twice.
       */
      manTheCastle();
    } else if (world.opts.terrain !== "hill" &&
        world.opts.terrain !== "one" && joined === null) {
      /*
       * And nothing on «Один блок» either.
       *
       * One cell in the whole world: a herd scattered over it is fourteen
       * animals falling for ever, and the fish would have no water. What
       * lives there is what the block gives you, and you let it out yourself.
       */
      spawnHerd();
      stockFish();
      /*
       * And the road across, on a world that is being made rather than
       * walked back into: it is written with ordinary edits, so a world that
       * has one already has it in its own chunks.
       */
      if (kind === "new") {
        cutTheRoad();
        if (brutal) stockTheCamp();
      }
      // Not the orcs, when online: they are the server's, and a second
      // garrison grown here would stand inside the first one until the next
      // snapshot swept it away.
      if (joined === null) garrison();
    }
    // And the two people, now that there is ground for them to stand on and
    // a spawn to stand near. Nothing on a map that has nobody.
    syncPopulation();
    // Every map has music, including the hill and including a world somebody
    // else is hosting: what is playing is this machine's business.
    beginMusic();

    /*
     * The bag this world was keeping, if it had one.
     *
     * Online the bag belongs to the world rather than to this browser: it is
     * kept against the name you came in under and handed back when you
     * return, so clearing your history no longer loses your evening.
     */
    const kept = net.keptBag;
    if (kept !== null) {
      inventory.slots.forEach((_, i) => inventory.set(i, kept.slots[i] ?? null));
      worn.slots.forEach((_, i) => worn.set(i, kept.worn[i] ?? null));
      ui.refresh();
      recipes.refresh();
    }

    /*
     * Not when online: the world on the wire is the world, and laying a local
     * save over the top of it would put one player in a different one.
     *
     * And not when this is a new game: «Почати гру» means the world you get
     * is the world the seed makes, which is the whole difference between it
     * and «Продовжити».
     */
    if (saved !== null && joined === null && kind === "resume") {
      restoreSave(
        saved, world, player, inventory, craftGrid, worn, cycle, pickups, boats);
      if (saved.spawn !== undefined) spawn = saved.spawn;
      /*
       * And which пригоди were already paid for.
       *
       * Into the same Set the book and `captureSave` share, so there is one
       * answer to "have I been paid for this" rather than two that can
       * disagree. Unknown ids are dropped on the way in - see
       * `questsFromSave`.
       */
      claimedQuests.clear();
      for (const id of questsFromSave(saved.quests)) claimedQuests.add(id);
      // And which лігва are already empty. A save from before Змії existed
      // has none, which is exactly right: nobody had killed one.
      slainLairs.clear();
      for (const key of saved.slainLairs) slainLairs.add(key);
      // And where this world had got to in the шопопало-блок's table.
      oneBlockBreaks = saved.oneBlockBreaks ?? 0;
      dragons.length = 0;
      quests.refresh();
      if (saved.skinId !== skins.selected.id) skins.select(skinById(saved.skinId));
      ui.refresh();
      console.info(
        `loaded save from ${new Date(saved.savedAt).toLocaleString()} - ` +
        `${saved.chunks.length} modified chunks`,
      );
    }

    renderer.syncChunks(world);
    /*
     * Whatever is in the bag already counts as seen.
     *
     * Before the frame loop gets a chance to look at it - otherwise loading
     * a world with a full inventory opens with the book blinking about
     * planks that were cut an hour ago.
     */
    seedTheBag();
    nudge.hide();
    // There is a world now, so the game may be seen. See body.no-world.
    document.body.classList.remove("no-world");
    pausedNote.hidden = false;
    void refreshHomeButtons();
    /*
     * And on a machine with no pointer, hand it straight over.
     *
     * Here rather than at the button, because the button is not the only way
     * in: picking a different map reloads and finishes here, and joining a
     * world reloads and finishes here. Both of those grew a world and left
     * the screen up with nothing anybody could press.
     */
    if (touchOnly) handToTouch();
  }

  /* ---- the требушет, wound and loosed ------------------------------------ */

  /**
   * The engine being wound, if the button is down on one.
   *
   * Its cell rather than a reference, because a block is a number in an array
   * and the thing under your hand can be dug out from under it while you
   * hold the button - in which case there is nothing to throw from and the
   * wind is simply dropped.
   */
  let winding: { x: number; y: number; z: number; power: number } | null = null;

  /** A frame of winding: the arm comes back while the button is held. */
  function windTrebuchet(dt: number): void {
    if (winding === null) return;
    if (!isTrebuchet(world.getBlock(winding.x, winding.y, winding.z))) {
      winding = null;
      return;
    }
    winding.power = Math.min(1, winding.power + dt / WIND_SECONDS);
    /*
     * Said out loud while it winds, because both halves of the aim are
     * invisible otherwise: how hard, and how high.
     *
     * The angle is off the same look the throw uses, so what is on the
     * screen is what is about to happen rather than a second opinion of it.
     */
    const look = player.lookDirection();
    const up = Math.round(Math.asin(Math.max(-1, Math.min(1, look.y))) * 180 / Math.PI);
    notify(`требушет  сила ${Math.round(winding.power * 100)}%  ` +
      `кут ${Math.max(0, up)}°`, 400);
  }

  /**
   * Let go: throw where the player is looking, as hard as it was wound.
   *
   * The снаряд is spent here rather than when the wind starts - a wind you
   * changed your mind about costs nothing, and a wind interrupted by the
   * pointer being lost is not a shot.
   */
  function looseTrebuchet(): void {
    const wound = winding;
    winding = null;
    if (wound === null) return;
    const at = world.getBlock(wound.x, wound.y, wound.z);
    if (!isTrebuchet(at)) return;
    if (inventory.get(ui.selectedSlot)?.id !== PlainItemId.Firebolt) return;
    const look = player.lookDirection();
    /*
     * The engine turns with the shot, so a row of them stands the way it
     * last threw rather than the way it was built.
     *
     * The same way round as putting one down: the machine stands with its
     * arm towards whoever is working it.
     */
    if (trebuchetFacing(at) !== facingFromLook(look.x, look.z)) {
      edit(wound.x, wound.y, wound.z,
        trebuchetId(facingFromLook(look.x, look.z)));
    }
    throwBolt(
      { x: wound.x + 0.5, y: wound.y + 1.2, z: wound.z + 0.5 },
      trebuchetThrow(look, wound.power),
    );
    inventory.takeFromSlot(ui.selectedSlot, 1);
    audio.play("breakWood", { rate: 0.5 });
    ui.refresh();
    recipes.refresh();
  }

  // ---- the drone ---------------------------------------------------------
  /**
   * One drone, reused.
   *
   * There is never more than one in the air: it is the thing you are looking
   * through, and you have one pair of eyes. A second would need a second
   * camera and a reason.
   */
  const drone = new Drone();
  /**
   * Two questions, which used to be one.
   *
   * `droneAloft` is whether the machine is in the air; `atControls` is
   * whether your eyes are in it. They were the same flag, which is why
   * stepping out of the drone dropped it out of the sky - "коли виходиш з
   * дрона він зчезає". Now leaving is leaving the controls: it stays up,
   * hovering and spending its battery, until either you take the пульт and
   * fly it again or the battery goes flat and it comes down.
   */
  let droneAloft = false;
  let atControls = false;

  /** How big the world is, so it cannot be flown out of. */
  const droneBounds = {
    spanXZ: world.opts.sizeXZ * 16,
    high: world.opts.sizeY * 16,
  };

  /**
   * Send it up, and go with it.
   *
   * It leaves the bag - it is in the air now, not in your hand - and comes
   * back only as something lying on the ground where it came down.
   */
  function launchDrone(): void {
    if (droneAloft) return;
    const eye = player.eyePosition();
    const look = player.lookDirection();
    drone.launch(
      { x: eye.x + look.x * 1.5, y: eye.y + 0.8, z: eye.z + look.z * 1.5 },
      player.yaw, player.pitch,
    );
    /*
     * Loaded before it leaves, and not a round more.
     *
     * Six passes over the кремль and what you missed you come back for. Taken
     * from the bag now rather than drawn from it in flight, because a drone
     * that can reach into your pockets from three hundred blocks away is a
     * siege engine that never has to land.
     */
    drone.load = Math.min(DRONE_LOAD, inventory.countOf(PlainItemId.Firebolt));
    if (drone.load > 0) inventory.take(PlainItemId.Firebolt, drone.load);
    droneAloft = true;
    atControls = true;
    inventory.takeFromSlot(ui.selectedSlot, 1);
    ui.refresh();
    recipes.refresh();
    audio.play("place");
    notify(say("drone-up", tone.tone, lang.value), 3000);
  }

  /**
   * Everything on the ground, plus the drone if it is in the air.
   *
   * A save taken mid-flight would otherwise lose it outright: it has left the
   * bag and has not yet landed, so it exists only in a variable. Written down
   * as something lying where it was flying, which is what a reload amounts to
   * - the pilot is gone, so it came down.
   */
  function groundState(): readonly Pickup[] {
    if (!droneAloft) return pickups;
    const spent = spentForm(PlainItemId.Drone) ?? PlainItemId.DroneFlat;
    return [...pickups, makePickup(
      { id: spent, count: 1 },
      { x: drone.x, y: drone.y, z: drone.z }, undefined, 0, DEATH_LIFETIME,
    )];
  }

  /**
   * Leave the controls, and leave it flying.
   *
   * Your eyes come back to your own head and the machine stays exactly where
   * it is, hovering on what is left of its battery. Taking it out of the sky
   * is no longer something a key does: "щоб коли ти виходив він залишався на
   * місці і літав поки не закінчиться батарея".
   */
  function leaveControls(): void {
    if (!atControls) return;
    atControls = false;
    notify(say("drone-left", tone.tone, lang.value), 3000);
  }

  /**
   * Back to the controls of something already up.
   *
   * What the пульт does. There is only ever one drone, so it needs no way of
   * choosing between them - and nothing to say if there is none.
   */
  function takeControls(): boolean {
    if (!droneAloft || !drone.flying) return false;
    atControls = true;
    return true;
  }

  /**
   * And the battery going flat, which is the only thing that brings it down.
   *
   * What lands is the flat one: a stack in the bag has nowhere to keep "three
   * minutes left", so a flight costs a battery whether it lasted five minutes
   * or five seconds.
   */
  function landDrone(reason: "flat" | "recalled"): void {
    if (!droneAloft) return;
    droneAloft = false;
    atControls = false;
    const fell = {
      x: Math.floor(drone.x), y: Math.floor(drone.y), z: Math.floor(drone.z),
    };
    // What a дрон becomes, asked of the item rather than written here twice.
    const spent = spentForm(PlainItemId.Drone) ?? PlainItemId.DroneFlat;
    const landing = [{ item: spent, count: 1 }];
    // Whatever it did not drop comes down with it, rather than evaporating.
    if (drone.load > 0) landing.push({ item: PlainItemId.Firebolt, count: drone.load });
    drone.load = 0;
    drop(landing, restingCell(fell, isSolidAt) ?? fell, DEATH_LIFETIME);
    notify(say(reason === "flat" ? "drone-flat" : "drone-down",
      tone.tone, lang.value), 4000);
  }

  // ---- saving ------------------------------------------------------------
  let saving = false;

  async function saveGame(reason: string): Promise<void> {
    /*
     * Never onto somebody else's world.
     *
     * The автосave was already turned off when joining a server, with a note
     * beside it saying why - and Ctrl+S went straight past it, writing the
     * server's world into the slot where this browser keeps its own. Guarded
     * here rather than at each call site, because a third way of asking for a
     * save will be written one day and it will not remember to ask.
     */
    if (net.online) {
      notify(uiText("net-playing", lang.value), 4000);
      return;
    }
    // Overlapping writes would race for the same slot; skipping is safe
    // because an autosave follows shortly anyway.
    if (saving) return;
    saving = true;
    try {
      const bytes = encodeSave(
        captureSave(
          world, player, inventory, craftGrid, worn, cycle, skins.selected.id, spawn,
          groundState(), boats, claimedQuests, slainLairs, oneBlockBreaks,
        ),
      );
      await store.write(liveSlot, bytes);
      saveNotice = `збережено (${(bytes.byteLength / 1024).toFixed(1)} КБ)`;
    } catch (error) {
      // Out of quota, private browsing, storage blocked - tell the player
      // rather than pretending the game is safe on disk.
      console.error("save failed", error);
      saveNotice = say("save-failed", tone.tone, lang.value);
    } finally {
      saving = false;
      saveNoticeUntil = performance.now() + 2500;
      if (reason === "manual") console.info(`saved: ${saveNotice}`);
    }
  }

  /**
   * Say a line out loud, if there is a recording of it.
   *
   * The speech bubble goes up either way - this is added on top of the written
   * line, never instead of it. A language or a tone nobody has recorded yet is
   * simply quiet, which is what lets the Ukrainian voice ship before the
   * English one without the game looking broken in English.
   */
  function sayAloud(line: LineKey): void {
    const take = voiceFor(line, lang.value, tone.tone);
    if (take === undefined) return;
    audio.speak(voiceUrl(take));
  }

  function notify(message: string, ms = 2500): void {
    saveNotice = message;
    saveNoticeUntil = performance.now() + ms;
  }

  /** Write the current game out as a file the player keeps. */
  function exportSave(): void {
    try {
      const save = captureSave(
        world, player, inventory, craftGrid, worn, cycle, skins.selected.id, spawn,
        groundState(), boats, claimedQuests, slainLairs,
      );
      const bytes = encodeSave(save);
      downloadSave(bytes, suggestedFilename(save.savedAt));
      notify(`експортовано (${(bytes.byteLength / 1024).toFixed(1)} КБ)`);
    } catch (error) {
      console.error("export failed", error);
      notify(say("export-failed", tone.tone, lang.value));
    }
  }

  /**
   * Throw this world away and grow another.
   *
   * The game had no way to do this, which was fine while a world could only
   * get better and stopped being fine the moment the terrain generator
   * changed under one: a save written before that is a set of differences
   * against land that no longer exists, and the autosave will have stamped
   * the mess as current within thirty seconds of it appearing.
   *
   * The old world is copied to the backup slot first, so "start again" never
   * means "destroy". Reloading is blunt and exactly right - the world, its
   * chunk meshes and the worker pool would all have to be rebuilt in flight
   * otherwise, and nobody does this in a hurry.
   */
  async function startFresh(): Promise<void> {
    const ok = confirm(
      uiText("ask-fresh", lang.value),
    );
    if (!ok) return;

    try {
      const current = await store.read(liveSlot);
      if (current !== null) await store.write(BACKUP_SLOT, current);
      await store.remove(liveSlot);
    } catch (error) {
      console.error("could not clear the save", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    location.reload();
  }

  /**
   * Bring back whatever is in the backup slot, and put this world there.
   *
   * The backup slot filled up on its own - a save the terrain had moved under
   * was put there rather than thrown away - and until now nothing could take
   * it out again. That made "the old one is in the backup slot" a consolation
   * rather than a fact.
   *
   * It is a swap, not a load: the world being left goes into the slot the
   * other one came out of, so pressing this twice puts everything back. That
   * matters more here than anywhere else in the game, because the one thing
   * this must never do is cost somebody the world they were just playing.
   *
   * When the backup was grown by an older generator it is re-stamped, which
   * is the one place the version check is deliberately overridden - and the
   * confirmation says so in as many words.
   */
  async function restoreBackup(): Promise<void> {
    let bytes: ArrayBuffer | null;
    try {
      bytes = await store.read(BACKUP_SLOT);
    } catch (error) {
      console.error("could not read the backup slot", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    if (bytes === null) {
      notify(uiText("no-backup", lang.value), 4000);
      return;
    }

    let incoming;
    try {
      incoming = decodeSave(bytes);
    } catch (error) {
      const why = error instanceof SaveFormatError ? error.message : "unreadable";
      console.error("the backup is not readable", error);
      notify(`резервний світ не читається: ${why}`, 5000);
      return;
    }

    const moved = incoming.worldgen !== WORLDGEN_VERSION;
    const ok = confirm(
      `${uiText("ask-restore", lang.value)}\n\n` +
      `${describeSave(incoming, lang.value)}\n\n` +
      (moved ? `${uiText("ask-restore-moved", lang.value)}\n\n` : "") +
      uiText("ask-restore-note", lang.value),
    );
    if (!ok) return;

    try {
      const current = await store.read(liveSlot);
      // The restored world first: if the second write fails, the worst case
      // is two copies of the same world rather than none of either.
      await store.write(
        SAVE_SLOT, moved ? encodeSave(restampWorldgen(incoming)) : bytes,
      );
      if (current !== null) await store.write(BACKUP_SLOT, current);
      else await store.remove(BACKUP_SLOT);
    } catch (error) {
      console.error("could not restore the backup", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    location.reload();
  }

  /**
   * Play one of the player's own named saves.
   *
   * The same shape as an import, and for the same reasons: the incoming world
   * may have a different seed or size, so it is written to the live slot and
   * the page is reloaded rather than swapped in mid-flight. The world being
   * left goes to the backup slot, so picking the wrong row costs nothing.
   *
   * The named save is *not* consumed - it stays exactly where it was, which
   * is what makes these snapshots rather than places you move between.
   */
  async function loadNamedSlot(name: string): Promise<void> {
    let bytes: ArrayBuffer | null;
    try {
      bytes = await store.read(slotKey(name));
    } catch (error) {
      console.error("could not read the save", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    if (bytes === null) { notify(`«${name}» не знайдено`, 4000); return; }

    let incoming;
    try {
      incoming = decodeSave(bytes);
    } catch (error) {
      const why = error instanceof SaveFormatError ? error.message : "unreadable";
      console.error("the save is not readable", error);
      notify(`«${name}» не читається: ${why}`, 5000);
      return;
    }
    if (incoming.worldgen !== WORLDGEN_VERSION) {
      notify(say("terrain-changed", tone.tone, lang.value), 6000);
      return;
    }

    try {
      const current = await store.read(liveSlot);
      if (current !== null) await store.write(BACKUP_SLOT, current);
      await store.write(liveSlot, bytes);
    } catch (error) {
      console.error("could not switch to the save", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    location.reload();
  }

  /**
   * Load a save file, replacing the current world.
   *
   * The imported world may have a different seed or size, which would mean
   * rebuilding the world, the chunk meshes and the worker state mid-flight.
   * Writing it to the slot and reloading is blunt but exactly correct, and
   * this is not an operation anyone does in a hurry.
   *
   * The current world is copied to a backup slot first. Overwriting somebody's
   * progress on a misclick is not a thing this game should be able to do.
   */
  async function importSave(): Promise<void> {
    const bytes = await pickSaveFile();
    if (bytes === null) return;

    let incoming;
    try {
      incoming = decodeSave(bytes);
    } catch (error) {
      const why = error instanceof SaveFormatError ? error.message : "unreadable";
      console.error("import failed", error);
      notify(`не збереження: ${why}`, 4000);
      return;
    }

    if (incoming.worldgen !== WORLDGEN_VERSION) {
      // The same trap, arriving by hand instead of off the disk.
      notify(say("terrain-changed", tone.tone, lang.value), 6000);
      return;
    }

    const ok = confirm(
      `${uiText("ask-import", lang.value)}\n\n` +
      `${describeSave(incoming, lang.value)}\n\n` +
      uiText("ask-import-note", lang.value),
    );
    if (!ok) return;

    try {
      const current = await store.read(liveSlot);
      if (current !== null) await store.write(BACKUP_SLOT, current);
      await store.write(liveSlot, bytes);
    } catch (error) {
      console.error("could not write the imported save", error);
      notify(say("write-failed", tone.tone, lang.value), 4000);
      return;
    }
    location.reload();
  }

  if (loaded.staleTerrain) {
    notify(say("terrain-changed", tone.tone, lang.value), 8000);
  }

  if (loaded.unreadable) {
    /*
     * Saving stays off for the whole session.
     *
     * There is a world on that disk that this build could not open, and the
     * one thing that must not happen is a fresh world being written over it
     * while the player works out what went wrong. A copy is already in the
     * backup slot; "Повернути попередній" will offer it.
     */
    notify(uiText("save-unreadable", lang.value), Infinity);
  } else if (joined !== null) {
    /*
     * Nothing is saved while playing on somebody else's world.
     *
     * The world belongs to the server. Writing it into this browser's live
     * slot would overwrite the single-player world with somebody else's - the
     * one mistake the map picker was built to make impossible, arriving by
     * another door.
     */
    notify(uiText("net-playing", lang.value), 5000);
  } else if (IndexedDbStore.isAvailable()) {
    setInterval(() => void saveGame("auto"), AUTOSAVE_INTERVAL);
    // Closing the tab is the most likely way to lose progress.
    window.addEventListener("pagehide", () => void saveGame("exit"));
  } else {
    saveNotice = say("no-storage", tone.tone, lang.value);
    saveNoticeUntil = Infinity;
  }

  // ---- input -------------------------------------------------------------
  const playTab = document.getElementById("tab-play") as HTMLButtonElement;
  const saveTab = document.getElementById("tab-save") as HTMLButtonElement;
  const newTab = document.getElementById("tab-new") as HTMLButtonElement;
  const pickNote = document.getElementById("pick-note") as HTMLDivElement;
  const pausedNote = document.querySelector(".paused-note") as HTMLDivElement;

  /** The click-to-play overlay belongs up only when nothing else has focus. */
  /**
   * Is the world being played right now?
   *
   * **One question, asked in one place.** It used to be asked twice, in two
   * expressions that were nearly the same: the overlay hid when
   * `(locked || touching)` and the simulation paused when `!locked`. So a
   * page that had `touching` set and no pointer lock took the overlay down -
   * it looks like you are playing - and froze the world, with nothing on the
   * screen saying so and no way to get out of it. You could look around and
   * nothing moved, not even the animals; opening the skin picker showed the
   * overlay again, and closing it asked for the pointer and let you back in.
   * That is exactly how it was reported.
   *
   * A finished round is not being played either. The two buttons on it are
   * the only things that should answer, and the clock behind them has
   * stopped.
   */
  /** A window over the game: the bag, the book, the people. */
  function panelOpen(): boolean {
    return ui.open || skins.open;
  }

  /**
   * The game has the player's hands: no window over it, and it has the mouse.
   *
   * What the click-to-play screen is hung on, and nothing else - it is about
   * whether the *player* is playing, not about whether the *world* is
   * running. Those were one question until the bag stopped being a pause.
   */
  function beingPlayed(): boolean {
    return grown && overScreen.hidden && warScreen.hidden &&
      (input.locked || input.touching) && !panelOpen();
  }

  /**
   * And whether the world is running, which is a different question.
   *
   * "Інвентар не має ставити гру на паузу. На паузу гра стає при натисканні
   * Esc." A window over the game is not a pause: rummaging in the bag while a
   * тур walks up to you is a decision, and one this game should let a player
   * make badly. Only the pause screen - no pointer and no window to explain
   * why - stops the hour, the fires and the orcs.
   *
   * The player themself still stands still while a window is open: walking is
   * gated on having the pointer, which a window takes away. The world moves
   * round them, which is the whole point.
   */
  function worldRunning(): boolean {
    // And never while a match is waiting to start or has finished: a lobby
    // is not a pause screen, but it is just as much "not playing yet".
    return grown && overScreen.hidden && warScreen.hidden &&
      (input.locked || input.touching || panelOpen());
  }

  function updateOverlay(): void {
    /*
     * And never over a finished round.
     *
     * The end-of-round screen drops the pointer, which is what un-hides this
     * - so the pause overlay came up on top of «Зіграти ще раз» and «Вийти з
     * гри» and swallowed every click aimed at them. A click on it asks for
     * the pointer back, so pressing a button put you straight back into a
     * match that had ended with the mouse steering again, which is how it
     * was reported: "неможливо нажати бо мишка продовжує керувати
     * персонажем".
     *
     * A finished round is not a paused game. It has its own two buttons and
     * they are the only things on the screen that should answer.
     */
    /*
     * And never behind a window.
     *
     * The pause screen says "nothing is moving", and with the bag open that
     * is no longer true - so it would be a lie drawn behind the bag. A window
     * is its own explanation for why the mouse is a mouse.
     */
    overlay.classList.toggle("hidden",
      !overScreen.hidden || !warScreen.hidden || beingPlayed() || panelOpen());
  }

  /**
   * Say so when the pointer cannot be taken.
   *
   * Pressing «Почати гру» on a phone grew a world, asked for the pointer,
   * was refused - there is no pointer on a touch screen - and left the
   * player looking at a pause screen with "click to play" written on it and
   * nothing to click. The world was running behind it the whole time.
   *
   * Measured rather than guessed at from the browser's name: ask for the
   * lock, and if it has not arrived by the time anybody could have noticed,
   * this is a machine that cannot give it. That is true of a phone, of a
   * browser with the feature switched off, and of anything else nobody has
   * thought of yet.
   */
  /**
   * A machine whose only pointer is a finger.
   *
   * Asked of the machine rather than worked out from a lock that was
   * refused, because the refusal only happens where a lock was *asked for* -
   * and the route that matters most does not ask. Picking a different map
   * reloads the page and finishes the job on the way back up, where there is
   * no click to hang a request on; so the world grew, nothing asked for the
   * pointer, nothing noticed it had not arrived, and the screen sat there
   * saying "click to play" at somebody with nothing to click.
   *
   * `(pointer: fine)` is the standard question: a laptop with a touchscreen
   * has both and stays a laptop, a phone has only the coarse one.
   */
  const touchOnly = (() => {
    try {
      return navigator.maxTouchPoints > 0 &&
        !window.matchMedia("(pointer: fine)").matches;
    } catch {
      return false;
    }
  })();

  /** Hand the world over to a finger, and say what a finger can do. */
  function handToTouch(): void {
    if (!grown) return;
    /*
     * Nor while a round is over. See updateOverlay: this handler is what
     * took the pointer back out from under the two buttons.
     */
    if (!overScreen.hidden || !warScreen.hidden) return;
    noPointer = true;
    input.touching = true;
    touchKeys.hidden = false;
    const cta = document.querySelector(".cta");
    if (cta !== null) cta.textContent = uiText("home-no-pointer", lang.value);
    updateOverlay();
  }

  function expectLock(): void {
    if (touchOnly) { handToTouch(); return; }
    window.setTimeout(() => {
      if (input.locked || !grown) return;
      /*
       * Only on a machine that has a finger to drive with.
       *
       * A refused lock is not proof there is no pointer. Browsers refuse one
       * for about a second after Escape released it, and they refuse one
       * asked for without a click at all - so an ordinary laptop hit this
       * every time somebody pressed Escape and clicked back in too quickly,
       * and was quietly put into touch mode. Before the pause and the
       * overlay were made to agree that froze the world with nothing saying
       * so; it is survivable now, and it is still wrong.
       */
      try {
        if (navigator.maxTouchPoints === 0) return;
      } catch { return; }
      /*
       * No pointer here, so a finger drives instead.
       *
       * The overlay has to come down before any of that can work: it covers
       * the screen, and a touch on it is a touch on it rather than on the
       * world behind. Which is also why this cannot wait for somebody to
       * touch the canvas - they cannot reach it.
       */
      noPointer = true;
      input.touching = true;
      touchKeys.hidden = false;
      const cta = document.querySelector(".cta");
      if (cta !== null) cta.textContent = uiText("home-no-pointer", lang.value);
      updateOverlay();
    }, 900);
  }
  const touchKeys = document.getElementById("touch-keys") as HTMLDivElement;
  /** True once a lock was asked for and did not arrive. */
  let noPointer = false;

  const input = new Input(renderer.renderer.domElement, updateOverlay);

  /*
   * The two buttons a keyboard would have made unnecessary.
   *
   * Jump, because a hill you cannot climb is a hill you cannot look from -
   * and a way back to the menu, because there is no Escape on a phone and
   * without one the only way out of a world is the browser's own back
   * button, which loses the tab.
   */
  {
    const jump = document.getElementById("touch-jump") as HTMLButtonElement;
    const menu = document.getElementById("touch-menu") as HTMLButtonElement;
    const hold = (on: boolean) => (e: Event): void => {
      e.preventDefault();
      e.stopPropagation();
      input.state.jump = on;
    };
    jump.addEventListener("touchstart", hold(true), { passive: false });
    jump.addEventListener("touchend", hold(false), { passive: false });
    jump.addEventListener("touchcancel", hold(false), { passive: false });
    // A mouse works on these too: a laptop with a touchscreen is a machine
    // that has both, and a check drives them with clicks.
    jump.addEventListener("mousedown", hold(true));
    jump.addEventListener("mouseup", hold(false));
    for (const kind of ["touchstart", "click"] as const) {
      menu.addEventListener(kind, (e) => {
        e.preventDefault();
        e.stopPropagation();
        input.touching = false;
        updateOverlay();
      }, { passive: false });
    }
  }
  overlay.addEventListener("click", () => {
    // Every click, not just the first: a browser may refuse the earliest one,
    // and the context also suspends itself when the tab sleeps.
    audio.unlock();
    // The takes are fetched now rather than when somebody throws a punch.
    audio.preload(VOICE.map(voiceUrl));
    // And the menu's own track, which needed exactly this click to exist.
    playMenuMusic();

    /*
     * A click on the background is "carry on", not "begin".
     *
     * Before there is a world, the only thing that starts one is the button:
     * otherwise reaching past the panel to click the sky would drop you into
     * whichever map happened to be selected, which is the thing this whole
     * change is about.
     */
    if (!grown) return;
    /*
     * A finger cannot take a pointer that is not there.
     *
     * On a phone this click is the one that says "carry on" after the menu
     * was opened, so it has to put the world back rather than ask for a lock
     * that will never arrive - which is what left somebody looking at their
     * own menu with the game running behind it.
     */
    if (touchOnly) { handToTouch(); return; }
    input.requestLock();
    expectLock();
  });

  /*
   * Any first touch of the page, and not only a click on the sky.
   *
   * The click handler above is on the overlay, and every button on the home
   * screen stops the click from reaching it - so somebody who presses
   * «Керування» first, which is most people, made an AudioContext and no
   * music. This is the same gesture asked for on the way in.
   *
   * Only before there is a world: once one is running, the pause screen
   * suspends the context on purpose and a stray `unlock` here would resume
   * a game that is meant to be quiet.
   */
  document.addEventListener("pointerdown", () => {
    if (grown) return;
    audio.unlock();
    playMenuMusic();
  }, { capture: true });

  /**
   * Start playing, one way or the other.
   *
   * `resume` walks back into the world that is already there; `new` grows a
   * fresh one and does not read the save at all.
   *
   * **If the chosen map is not the one this session was built for, it
   * reloads.** The world and everything hanging off it were made from the map
   * at startup, so there is no changing it in flight - but that is this
   * screen's problem to solve, not the player's. Before, the picker printed
   * "reload the page" and left a Продовжити button that would walk you into
   * the old map anyway. Now the press is remembered across the reload and
   * honoured on the way back up, exactly as joining a server already worked.
   */
  function begin(kind: "new" | "resume"): void {
    audio.unlock();
    audio.preload(VOICE.map(voiceUrl));
    // The world takes over the music from here.
    stopMenuMusic();

    if (chosenMap.value !== playing.id) {
      try { localStorage.setItem(BEGIN_KEY, kind); } catch { /* no storage */ }
      location.reload();
      return;
    }
    growWorld(kind);
    input.requestLock();
    expectLock();
  }

  playTab.addEventListener("click", (e) => { e.stopPropagation(); begin("resume"); });
  /*
   * And saving, said on the button itself.
   *
   * `saveGame` reports through `notify`, which writes a line on the HUD -
   * and the HUD is behind this panel while the panel is open, so pressing
   * the button would have looked like pressing nothing. The word on the
   * button is the one place the eye already is.
   */
  saveTab.addEventListener("click", (e) => {
    e.stopPropagation();
    saveTab.disabled = true;
    void saveGame("manual").finally(() => {
      saveTab.textContent = uiText("home-saved", lang.value);
      window.setTimeout(() => {
        saveTab.textContent = uiText("home-save", lang.value);
        saveTab.disabled = false;
      }, 1600);
    });
  });
  newTab.addEventListener("click", (e) => {
    e.stopPropagation();
    /*
     * An adventure is a new country every time; the others are not.
     *
     * It used to have a button of its own, which meant choosing the map and
     * then pressing something other than «Почати гру» - two ways of saying
     * the same thing, one of which quietly ignored the map picker. Now it is
     * a map like any other and this is the whole of what makes it different:
     * a fresh seed. The кремль and the hill are the same country every time
     * on purpose, because that is what makes them a race.
     */
    if (chosenMap.value === ADVENTURE.id) {
      if (hasAdventure && !confirm(uiText("home-adventure-over", lang.value))) return;
      try {
        localStorage.setItem(NEW_SEED_KEY, String(
          Math.floor(Math.random() * 2147483647)));
        localStorage.setItem(BEGIN_KEY, "new");
        localStorage.setItem("vyzhyvannia.map", ADVENTURE.id);
      } catch { /* no storage: it will start the map it is already on */ }
      audio.unlock();
      location.reload();
      return;
    }
    /*
     * Starting over, from inside the world.
     *
     * Asked first, because it flattens the world you are standing in - the
     * backup keeps it, and that is the sentence. Then the page reloads
     * into a fresh one: the same road a different map takes, rather than
     * growing a second world under the first.
     */
    if (grown && chosenMap.value === playing.id) {
      if (!confirm(uiText("home-new-over", lang.value))) return;
      try { localStorage.setItem(BEGIN_KEY, "new"); } catch { /* no storage */ }
      audio.unlock();
      location.reload();
      return;
    }
    /*
     * Asked, because a new world writes over the old one.
     *
     * The live slot is per map and a fresh world autosaves straight into it.
     * There is a backup behind it, and "your evening is in the резерв" is not
     * a sentence anybody should have to be told after the fact.
     */
    if (!playTab.hidden && !confirm(uiText("home-new-over", lang.value))) return;
    begin("new");
  });

  /*
   * The home screen: two buttons on the left, what they open on the right.
   *
   * Only one detail panel is up at a time, and clicking the button that is
   * already up closes it - so the screen can be put back to just the title
   * and the switches without starting the game.
   */
  /*
   * Playing together lives in its own column.
   *
   * The single-player menu is hidden rather than disabled, and the network
   * menu takes its place - which is what makes going online read as somewhere
   * you have gone rather than a third thing to look at.
   */
  const homeMenu = document.getElementById("home-menu") as HTMLDivElement;
  const soloMenu = document.getElementById("solo-menu") as HTMLDivElement;
  const soloTab = document.getElementById("tab-solo") as HTMLButtonElement;
  const soloBack = document.getElementById("solo-back") as HTMLButtonElement;
  const netMenu = document.getElementById("net-menu") as HTMLDivElement;
  const netDetail = document.getElementById("net-detail") as HTMLDivElement;
  const netTab = document.getElementById("tab-net") as HTMLButtonElement;

  const network = new NetworkUI(netMenu, netDetail, {
    probe: probeServer,
    account: askAccount,
    back: () => showNetwork(false),
    hold: (url, map, word, match) => {
      holdOnReload(url, map, word, match);
      location.reload();
    },
    mayHost: (url, name, password) =>
      askHosting(url, "kremlin", name, nameTag(url, "", name), password),
    kept: (owner) => heldAlready(heldStore(), owner),
    forget: async (owner) => { await forgetHeld(heldStore(), owner); },
    join: (url, word, world, guest) => {
      joinOnReload(url, word, world, guest === true);
      location.reload();
    },
    // The host *with* its port: without it every unencrypted page was sent
    // to 5274 whatever port it actually came from.
  }, location.host, location.protocol === "https:");

  /**
   * The single-player window: which map, how you want to play it, go.
   *
   * It replaces the first screen rather than unfolding below it, the same
   * way playing together does - which is what makes it read as somewhere you
   * have gone rather than as a fourth thing to look at.
   */
  function showSolo(open: boolean): void {
    homeMenu.hidden = open;
    soloMenu.hidden = !open;
    if (open) {
      // Closed through the panel, like everything else that opens it: it is
      // the thing that knows a knock is no longer wanted.
      network.setOpen(false);
      showTab(null);
      settingsForMap();
      void refreshHomeButtons();
    }
    sayTheMap();
  }

  /**
   * What the chosen map is, in a sentence, under the picker.
   *
   * The element is looked up here rather than held in a `const` beside the
   * others, because this is called from `applyHomeText` - which runs while
   * the page is still being wired, long before that part of it exists. A
   * const read too early is not undefined, it is a thrown error and a blank
   * screen, and this file has already lost an evening to that once.
   */
  function sayTheMap(): void {
    const where = document.getElementById("map-blurb");
    if (where === null) return;
    const map = mapById(chosenMap.value);
    where.textContent = lang.value === "en" ? map.blurb.en : map.blurb.uk;
  }

  soloTab.addEventListener("click", (e) => { e.stopPropagation(); showSolo(true); });
  soloBack.addEventListener("click", (e) => { e.stopPropagation(); showSolo(false); });

  function showNetwork(open: boolean): void {
    homeMenu.hidden = open;
    soloMenu.hidden = true;
    /*
     * Through the panel rather than round it.
     *
     * These two lines used to hide and show `netMenu` and `netDetail`
     * directly, which is what `NetworkUI.setOpen` is for - and because
     * nothing ever called it, nothing ever knocked when the panel was
     * opened. The list of worlds appeared only after a trip through
     * «Створити» and back, which is the trip that switches pane and so does
     * knock. "Нажимаю створити і потім назад приєднатись і скрін вже інший."
     */
    network.setOpen(open);
    // The network panel shares the box with Налаштування and Збереження.
    homeDetail.hidden = !open;
    if (open) {
      settingsPanel.hidden = true;
      savesPanel.hidden = true;
    }
  }
  netTab.addEventListener("click", (e) => {
    e.stopPropagation();
    showNetwork(true);
  });
  // The same panel, opened on the one thing this button is about.
  const registerTab = document.getElementById("tab-register") as HTMLButtonElement;
  registerTab.addEventListener("click", (e) => {
    e.stopPropagation();
    showNetwork(true);
    network.openRegister();
  });

  /** The box the panels open in. Hidden when none of them is open. */
  const homeDetail = document.querySelector(".home-detail") as HTMLDivElement;
  const savesPanel = document.getElementById("panel-saves") as HTMLDivElement;
  const settingsPanel = document.getElementById("panel-settings") as HTMLDivElement;
  const settingsTab = document.getElementById("tab-settings") as HTMLButtonElement;
  const savesTab = document.getElementById("tab-saves") as HTMLButtonElement;

  const saves = new SavesUI(savesPanel, {
    list: () => store.list(),
    /*
     * Carry on with a map's own world.
     *
     * Picking the map and resuming is exactly what the home screen does, so
     * it is done the same way rather than through a second path into a
     * world: set the picker, and `begin("resume")` takes it from there.
     */
    playWorld: async (mapId: string) => {
      chosenMap.choose(mapId);
      begin("resume");
    },
    mapName: (mapId: string) => {
      const map = mapById(mapId);
      return lang.value === "en" ? map.name.en : map.name.uk;
    },
    maps: () => ({ maps: MAPS, defaultMapId: DEFAULT_MAP.id }),
    saveAs: async (name) => {
      const bytes = encodeSave(
        captureSave(
          world, player, inventory, craftGrid, worn, cycle, skins.selected.id,
          spawn, groundState(), boats, claimedQuests,
        ),
      );
      await store.write(slotKey(name), bytes);
      notify(`збережено як «${name}»`);
    },
    rename: async (from, to) => {
      // Copy, then delete: if the write fails there are two names for one
      // world, which is a nuisance. The other order loses the world.
      const bytes = await store.read(slotKey(from));
      if (bytes === null) return;
      await store.write(slotKey(to), bytes);
      await store.remove(slotKey(from));
    },
    remove: (name) => store.remove(slotKey(name)),
    load: (name) => loadNamedSlot(name),
    exportFile: exportSave,
    importFile: importSave,
    restoreBackup,
    startFresh,
  });
  /*
   * On somebody else's world the panel offers nothing.
   *
   * There is no personal copy of a world the server keeps for everybody at
   * once, and a panel full of buttons that would each do the wrong thing is
   * worse than a sentence saying so.
   */
  saves.onServer = joined !== null;

  /*
   * One of the two is always open.
   *
   * The first version toggled, so clicking the tab that was already up left
   * the right-hand half an empty box with a line of text in it. There is
   * nothing a player wants from that state, and having to click twice to get
   * back out of it is worse than never being able to reach it.
   */
  /**
   * Open one of them, or none.
   *
   * None is where the screen starts: Керування used to be open before
   * anybody asked, which made a button look pressed on a page nobody had
   * touched yet. Pressing the one that is already open closes it, so the
   * screen can be put back to the title and the buttons.
   */
  function showTab(tab: "settings" | "saves" | null): void {
    settingsPanel.hidden = tab !== "settings";
    savesPanel.hidden = tab !== "saves";
    settingsTab.classList.toggle("active", tab === "settings");
    savesTab.classList.toggle("active", tab === "saves");
    // An empty bordered box a third of the screen tall is furniture for a
    // room nobody is in.
    homeDetail.hidden = tab === null;
    if (tab === "saves") void saves.refresh();
  }
  // Clicks anywhere in the menu are the player reaching for a button, never a
  // request to start playing.
  for (const [tab, name] of [
    [settingsTab, "settings"], [savesTab, "saves"],
  ] as const) {
    tab.addEventListener("click", (e) => {
      e.stopPropagation();
      // Pressing the open one closes it. Two presses of one button should
      // not leave you looking at the same thing you started with.
      showTab(tab.classList.contains("active") ? null : name);
    });
  }
  /*
   * Керування is open to begin with.
   *
   * With nothing open the right-hand half is a large empty box holding one
   * line of text telling you to click something, which is worse than simply
   * showing the keys - and the keys are what was on this screen before, so
   * nothing anybody relied on has moved.
   */
  // Everything that draws words now exists, so the chosen language can be
  // pushed into all of it - including the home screen this sits on.
  /*
   * A reset link, followed.
   *
   * Down here, after every panel exists, and that is not a tidiness thing:
   * `showNetwork` reaches for `controlsPanel`, which is a `const` declared
   * further down, so calling it from higher up threw "cannot access before
   * initialization" and killed `main` outright. The page came up with a
   * half-opened menu and nothing else working - the third time this session
   * that one enormous function's dead zone has bitten.
   *
   * The ticket travels in the address so the message can be opened on any
   * machine - which is the whole point of a reset, and why it cannot be tied
   * to the browser that asked for one. Taken out of the bar once read, so a
   * refresh is not a second attempt at a ticket that has been spent.
   */
  {
    const ticket = new URLSearchParams(location.search).get("reset");
    if (ticket !== null && ticket.length > 0) {
      /*
       * The ticket first, and the panel second.
       *
       * Opening the panel knocks, and a knock that fails writes "nobody
       * answered" over whatever the page had decided to say - which here is
       * the one sentence telling somebody who arrived from a reset message
       * what to do. Handing the ticket over first is what stops it: a panel
       * holding one does not knock at all, which is the rule `show` has
       * always had and `setOpen` now shares.
       */
      network.openReset(ticket);
      showNetwork(true);
      history.replaceState(null, "", location.pathname);
    }
  }

  applyLang();
  /*
   * Nothing open, so nothing looks pressed on a page nobody has touched.
   *
   * Unless somebody arrived from a reset message, in which case the network
   * panel is already up with one thing to do on it - and closing it here
   * would have been this line quietly undoing the line above.
   */
  if (netMenu.hidden) showTab(null);
  /*
   * And now it may be looked at.
   *
   * Everything above this line is the difference between the markup and the
   * menu: the language, both <select>s, which panel is open. Until it has
   * run, the overlay is raw HTML with empty lists in it - see `body.booting`.
   */
  document.body.classList.remove("booting");

  /*
   * Nothing is grown without somebody pressing something.
   *
   * A save being *there* is not a choice - it is only evidence of an old one,
   * and taking it as consent meant the world loaded a few seconds after the
   * page did, exactly as before, while the button sat there unpressed. The
   * only difference it makes is what the button says: walking back into a
   * world you were in the middle of is not the same act as starting one, and
   * "Почати" over a хата you spent an evening on reads as an offer to flatten
   * it.
   *
   * Joining a server is the exception, and only because the page reloaded in
   * order to do it: the asking was the click that got you here.
   */
  if (joined !== null) {
    growWorld();
  } else if (beginRequest !== null) {
    /*
     * A press from before the reload, honoured now.
     *
     * The reload *is* the click: picking a different map and pressing
     * «Почати гру» cannot change the world in flight, so the screen reloads
     * itself and finishes the job here. Growing without a press would be the
     * bug this whole design is about; this is a press, it just happened a
     * second and a page ago.
     */
    growWorld(beginRequest);
  } else {
    pickNote.hidden = false;
    pausedNote.hidden = true;
    void refreshHomeButtons();
  }

  // Closing a panel hands control straight back to the game.
  const resumePlay = (): void => {
    updateOverlay();
    input.requestLock();
  };
  ui.onClose = resumePlay;
  skins.onClose = resumePlay;

  /** Open a panel: it needs the cursor, so give up the pointer lock. */
  function openPanel(show: () => void): void {
    tooltip.hide();
    show();
    document.exitPointerLock();
    updateOverlay();
  }

  function toggleInventory(): void {
    if (ui.open) { ui.close(); return; }
    if (skins.open) skins.close();
    openPanel(() => ui.show());
  }

  /**
   * B shows the recipes.
   *
   * The book is a column of the inventory window, so this opens that window if
   * it is closed. One key, one window, one way to leave.
   */
  function toggleRecipeBook(): void {
    if (!ui.open) {
      if (skins.open) skins.close();
      openPanel(() => ui.show());
      ui.setBookOpen(true);
      return;
    }
    ui.toggleBook();
  }

  function toggleSkins(): void {
    if (skins.open) { skins.close(); return; }
    if (ui.open) ui.close();
    openPanel(() => skins.show());
  }

  // ---- block interaction -------------------------------------------------

  /** What the player is currently aiming at, or null. Refreshed every frame. */
  function aim() {
    const eye = player.eyePosition();
    const dir = player.lookDirection();
    return raycastVoxels(
      eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, REACH, isTargetableAt,
    );
  }

  /**
   * Put what a block yielded on the ground.
   *
   * Everything that comes out of a block goes through here rather than into
   * the bag directly. Until pickups existed, breaking was *refused* when the
   * bag was full - the only honest option, since anything that did not fit
   * would have ceased to exist. Now a full bag means a pile on the floor.
   */
  /**
   * Change a block, here and everywhere.
   *
   * Every door that swings, every stove that catches, every чан that fills
   * went through `world.setBlock` and stopped there - so two people standing
   * at the same door watched it open on one screen and stay shut on the
   * other. Seventeen call sites did that; they all come here now.
   *
   * Done locally *and* sent, rather than sent and waited for: a door that
   * opens a fifth of a second after you press it feels broken, and the server
   * is still the one that decides - its `blocks` message writes over whatever
   * we guessed.
   *
   * Placing and breaking keep their own messages: those have checks of their
   * own that this deliberately does not.
   */
  function edit(x: number, y: number, z: number, id: number): void {
    world.setBlock(x, y, z, id);
    net.set(x, y, z, id);
  }

  /**
   * Let one go, here and everywhere.
   *
   * The velocity travels rather than a direction and a speed: the one dropped
   * from a drone leaves with the drone's own speed, and no single number
   * describes both that and a trebuchet's lob. Everybody runs the same pure
   * flight over it and it lands in the same place - and the server runs it
   * too, which is what makes the fire it starts everyone's fire.
   */
  /**
   * Take the ball of blocks out, and hurt whoever was standing in it.
   *
   * Single player only - online the server owns this, for the same reason it
   * owns the fire: a blast changes blocks away from anybody's hands, and two
   * clients each blowing their own hole in the same hill would be two people
   * standing in different worlds.
   *
   * It does **not** set off what it takes. Three blocks in ten are live, so a
   * chain would be self-sustaining and the first unlucky block would take the
   * whole cube with it.
   */
  function blastAt(x: number, y: number, z: number): void {
    for (const cell of blastCells(x, y, z)) {
      if (!world.withinBounds(cell.x, cell.y, cell.z)) continue;
      // Only the hill: the stone floor under it is what stops a fall being a
      // fall out of the world.
      if (world.getBlock(cell.x, cell.y, cell.z) !== BlockId.Boom) continue;
      world.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
    }
    boomHeard(x, y, z);
    const away = Math.hypot(
      player.position.x - x, player.position.y - y, player.position.z - z);
    const hurt = blastHurt(away);
    if (hurt > 0 && health.hurt(damageAfterArmour(hurt, armourPoints())) > 0) {
      notify(say("boom", tone.tone, lang.value), 1600);
    }
  }

  /** The noise, wherever it came from - ours or somebody else's. */
  function boomHeard(x: number, y: number, z: number): void {
    const away = Math.hypot(
      player.position.x - x, player.position.y - y, player.position.z - z);
    // Quieter with distance, and never silent: an explosion across the hill
    // is how you find out somebody else is climbing.
    audio.play("breakStone", {
      rate: 0.35, volume: Math.max(0.15, 1 - away / 90),
    });
  }

  function throwBolt(
    from: { x: number; y: number; z: number },
    velocity: { x: number; y: number; z: number },
  ): void {
    firebolts.push(makeFirebolt(from, velocity, 1));
    net.bolt(from, velocity);
  }

  function drop(
    drops: readonly { item: number; count: number }[],
    cell: { x: number; y: number; z: number },
    keepFor = PICKUP_LIFETIME,
  ): void {
    pickups.push(...scatterDrops(
      drops.map((d) => ({ id: d.item, count: d.count })), cell, keepFor,
    ));
  }

  /**
   * The blow that is not digging: a човен, an animal, a person, an орк.
   *
   * Split off from `breakBlock` when blocks grew durability. Everything in
   * here happens on the press and happens *once* - holding the button down
   * on a тур is not four blows, and a fight in this game has never been
   * about how long you can hold a mouse. Digging is the other half, and it
   * is the half that is paced.
   *
   * Returns true when the blow found something, so the caller knows not to
   * start digging the wall behind it.
   */
  function strikeSomething(): boolean {
    /*
     * A човен comes out of the water before anything else is hit.
     *
     * Otherwise a boat is a thing you can make and never take back: there is
     * nothing to break and nothing to pick up, and the first one anybody
     * launches is on that lake for good. One blow puts it back in the bag,
     * and you have to be out of it - a passenger cannot pocket the boat they
     * are standing in.
     */
    if (aboard === null) {
      const eyeK = player.eyePosition();
      const lookK = player.lookDirection();
      const got = pickEntity(
        boats, eyeK.x, eyeK.y, eyeK.z, lookK.x, lookK.y, lookK.z,
        REACH, 1.1, 0.7,
      );
      const block = aim();
      if (got !== null && (block === null || block.distance > got.distance)) {
        const at = boats.findIndex((b) => b === got.entity);
        if (at >= 0 && boats[at]!.rider === null) {
          // Online it is the server that takes it off the water; ours goes
          // when the next snapshot no longer has it.
          if (net.online) net.scuttle(boats[at]!.id);
          else boats.splice(at, 1);
          /*
           * Straight into the bag, not onto the ground.
           *
           * A boat is the only thing in this game you pick up by hitting it
           * rather than by walking over it, and a човен that lands in the
           * water beside where it was floating is a човен you then have to
           * fish out. Onto the ground only when there is no room for it.
           */
          if (inventory.add(PlainItemId.Choven, 1) > 0) {
            drop([{ item: PlainItemId.Choven, count: 1 }], {
              x: Math.floor(got.entity.x), y: Math.floor(got.entity.y),
              z: Math.floor(got.entity.z),
            });
          }
          ui.refresh();
          recipes.refresh();
          audio.play("breakWood");
          return true;
        }
      }
    }

    // People come before blocks: he is standing in front of one, and hitting
    // the wall behind a man you were clearly aiming at reads as a bug.
    const eye = player.eyePosition();
    const look = player.lookDirection();
    const struck = pickEntity(
      [...villagers, ...orcs, ...beasts, ...fish],
      eye.x, eye.y, eye.z, look.x, look.y, look.z,
      REACH, ORC_WIDTH, ORC_HEIGHT,
    );
    /*
     * And the people, who are a different size and a different list.
     *
     * Their own `pickEntity` because the one above takes a single box for
     * everything in it, and a person is narrower than an орк. Whoever is
     * nearer wins - a свиня between you and somebody's back is a свиня you
     * hit.
     */
    const hitSomebody = pickEntity(
      seenPlayers, eye.x, eye.y, eye.z, look.x, look.y, look.z,
      REACH, PLAYER_WIDTH, PLAYER_HEIGHT,
    );
    if (hitSomebody !== null &&
      (struck === null || hitSomebody.distance <= struck.distance)) {
      const aimedAt = aim();
      if (aimedAt === null || aimedAt.distance > hitSomebody.distance) {
        const held = inventory.get(ui.selectedSlot);
        /*
         * Their сила is theirs, so this is a message and nothing else.
         *
         * Nothing is taken off here and nothing is drawn white: the server
         * decides whether the swing could have reached, tells them it
         * landed, and tells everybody so the blow is seen. Which is the same
         * arrangement an orc gets, and for the same reason.
         */
        net.strike("player", hitSomebody.entity.id, itemDamage(held?.id ?? 0));
        audio.play("breakWood", { rate: 0.8 });
        strikeLeft = STRIKE_SECONDS;
        return true;
      }
    }
    /*
     * And a Змій, who is his own list and his own size.
     *
     * Four blocks tall and nearly three across, against an орк's one and a
     * bit - one `pickEntity` cannot take both, because it is given one box
     * for everything in its list.
     */
    const hitDragon = pickEntity(
      dragons.filter((d) => !d.slain),
      eye.x, eye.y, eye.z, look.x, look.y, look.z,
      REACH, ZMII.width, ZMII.height,
    );
    if (hitDragon !== null &&
        (struck === null || hitDragon.distance <= struck.distance)) {
      const wall = aim();
      if (wall === null || wall.distance > hitDragon.distance) {
        const held = inventory.get(ui.selectedSlot);
        const dead = hurtDragon(hitDragon.entity, itemDamage(held?.id ?? 0));
        audio.play("breakWood", { rate: 0.6 });
        strikeLeft = STRIKE_SECONDS;
        if (dead) {
          /*
           * Twenty-three scales on the ground, and the лігво stays empty.
           *
           * "Після смерті не відроджується" - so the hall is written down as
           * emptied, which is the one fact about it that cannot come back
           * out of the seed.
           */
          slainLairs.add(lairKey(hitDragon.entity.home));
          const drop = ZMII.drop;
          if (drop !== undefined) {
            for (const p of scatterDrops([{ id: drop.item, count: drop.min }],
              { x: hitDragon.entity.x, y: hitDragon.entity.y, z: hitDragon.entity.z })) {
              pickups.push(p);
            }
          }
        }
        return true;
      }
    }

    if (struck !== null) {
      const hitBlock = aim();
      if (hitBlock === null || hitBlock.distance > struck.distance) {
        const held = inventory.get(ui.selectedSlot);
        const damage = itemDamage(held?.id ?? 0);
        /*
         * Their health is the server's, when there is one.
         *
         * So the blow travels as a request naming which body and how hard,
         * and the server decides whether we were near enough to land it.
         * Nothing is taken off a mirror here: it would be put back by the
         * next snapshot, and until it was, this screen would show a body at
         * half health that everybody else sees untouched. What the body does
         * about it - run, turn round, say its piece - is the server's too,
         * and comes back in the next snapshot.
         */
        const kind: CreatureKind | null =
          orcs.some((o) => o === struck.entity) ? "orc"
          : beasts.some((b) => b === struck.entity) ? "beast"
          : fish.some((f) => f === struck.entity) ? "fish"
          : villagers.some((v) => v === struck.entity) ? "villager"
          : null;
        const theirs = net.online && kind !== null;
        if (theirs && kind !== null) net.strike(kind, struck.entity.id, damage);
        const dealt = theirs ? damage : struck.entity.vitals.hurt(damage);
        // A blow that lands during his grace period does nothing, and should
        // not make him say anything either - otherwise one swing sets him off
        // three times.
        if (dealt > 0) {
          audio.play("breakWood", { rate: 0.8 });
          // What it does about it is the server's, online: this screen would
          // otherwise send a pig running one way and everybody else's the
          // other. Alone, it happens here.
          if (theirs) return true;
          // An animal that has just been hit runs, and it runs from wherever
          // the blow came from rather than from where it last saw anyone.
          const beast = beasts.find((b) => b === struck.entity);
          if (beast !== undefined) spook(beast, player.position.x, player.position.z);
          const hooked = fish.find((f) => f === struck.entity);
          if (hooked !== undefined) startle(hooked, player.position.x, player.position.z);
          const villager = villagers.find((v) => v === struck.entity);
          if (villager !== undefined) {
            faceToward(villager, player.position.x, player.position.z);
            villager.speaking = SPEECH_SECONDS;
            sayAloud(villager.line);
          }
        }
        return true;
      }
    }

    return false;
  }

  /**
   * Take the aimed block out of the world.
   *
   * Called when the last blow lands, not when the button goes down: see
   * `Digging` and the table in core/block/breaking.ts for how many that is.
   */
  /**
   * What the шопопало-блок just gave up: a creature, or something to carry.
   *
   * "Хочу щоб істоти вискакували одразу" - so a creature is not handed over
   * as the thing you let it out of, it simply arrives, standing on the
   * block. Everything else scatters at your feet the way any drop does.
   */
  function letOutOrGive(
    item: number, at: { x: number; y: number; z: number },
  ): void {
    const def = spawnForItem(item);
    if (def !== undefined) {
      const born = oneBlockBirth(at);
      switch (def.kind) {
        case "pig": case "tur": case "kurka": case "sobaka": case "kit":
          beasts.push(makeBeast(def.kind, born));
          break;
        // A карась out of a block with no water under it would drown in the
        // air, which is a sentence nobody wants to have to say. It comes out
        // as the thing you let it out of, to be put in water when there is
        // some.
        case "karas":
          drop([{ item, count: 1 }], at);
          break;
        case "orc":
          orcs.push(makeOrc(born));
          break;
        case "selianyn":
          villagers.push(
            makeVillager("дядько Гриць", "selianyn", "villager-hit", born));
          break;
        case "selianka":
          villagers.push(
            makeVillager("тітка Одарка", "selianka", "villager-hit-her", born));
          break;
      }
      notify(oneBlockSaid(item, lang.value), 1400);
      audio.play("place");
      return;
    }
    drop([{ item, count: oneBlockCount(item) }], at);
    notify(oneBlockSaid(item, lang.value), 1400);
    ui.refresh();
    recipes.refresh();
  }

  function breakBlock(): void {
    const hit = aim();
    if (hit === null) return;

    const id = world.getBlock(hit.x, hit.y, hit.z);
    if (id === BlockId.Air) return;

    // The right tool, or nothing happens and it says why. There is no mining
    // *time* in this game, so "the right tool" cannot mean "faster" - it means
    // "at all". Refusing out loud beats Minecraft's silent no-drop: a block
    // that vanishes and yields nothing looks like a bug to anyone who does not
    // already know the convention.
    const needed = requiredTool(id);
    if (needed !== null && itemTool(inventory.get(ui.selectedSlot)?.id ?? 0) !== needed) {
      notify(say(TOOL_REFUSAL[needed], tone.tone, lang.value), 1600);
      return;
    }

    // Sounded here rather than at each of the three ways a block comes out
    // below, and before the block is gone - by the time a door has been
    // removed there is nothing left to ask what it was made of.
    audio.play(soundForBlock(id));

    /*
     * A вибухоблок, and three in ten of them go off.
     *
     * Online this is the server's: `edit` sends the break and the blast comes
     * back as ordinary edits and a `hurt`, the same as a fire eating a wall.
     * Alone, it happens here - the same pure rule, the same hash, so the map
     * plays identically whether or not anybody else is in it.
     */
    if (id === BlockId.Boom) {
      /*
       * `break`, not `edit`.
       *
       * `edit` sends `set` - "this cell is air now" - and the server does
       * exactly that and nothing else. The blast lives in `realm.breakBlock`,
       * which only the `break` message reaches, so online **no вибухоблок
       * ever went off**: every one of them came quietly away like a block of
       * dirt. "Жоден блок не взірвався", and the comment here had said for
       * weeks that the server was handling it.
       */
      world.setBlock(hit.x, hit.y, hit.z, BlockId.Air);
      if (net.online) net.break(hit.x, hit.y, hit.z);
      else if (explodes(hit.x, hit.y, hit.z, world.opts.seed)) {
        blastAt(hit.x, hit.y, hit.z);
      }
      return;
    }

    /*
     * Шопопало-блок: four blows shake something out, and the block stays.
     *
     * The whole map is this one cell, so taking it away would end the game
     * on the first break with everybody falling into the dark. What comes
     * out is decided by the world's seed and by which break this is - the
     * same arithmetic in `core/world/oneBlock.ts` that the server would use,
     * so two people at the same block take the same things out of it in the
     * same order.
     */
    if (id === BlockId.Random) {
      /*
       * Online it is the server's block, not ours.
       *
       * The world's own supply is the world's to open: the server keeps the
       * count, works out what comes out, puts creatures into the world
       * itself and sends back a `gave` for anything you can carry. A page
       * that rolled its own would hand out a different table to every
       * person standing at the same block.
       */
      if (net.online) {
        net.break(hit.x, hit.y, hit.z);
        return;
      }
      const item = oneBlockDrop(world.opts.seed, ++oneBlockBreaks);
      letOutOrGive(item, hit);
      return;
    }

    // A stove comes apart with whatever was in it, the same as a chest -
    // including the fuel that was part-burned, which is simply gone, as it
    // would be.
    if (isStove(id)) {
      const fire = world.furnaces.at(hit.x, hit.y, hit.z);
      const inside = fire === null ? [] : fire.contents();
      const drops = rollDrops(id);
      edit(hit.x, hit.y, hit.z, BlockId.Air);
      drop([
        ...drops,
        ...inside.map((slot) => ({ item: slot.id, count: slot.count })),
      ], hit);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A chest hands back what it held before it comes apart. There are no
    // dropped-item entities, so anything that does not fit in the bag would
    // simply cease to exist - and losing a chestful to one click is not a
    // thing this game should be able to do.
    if (isChest(id)) {
      const held = world.containers.at(hit.x, hit.y, hit.z);
      const contents = held === null ? [] : held.slots.filter((slot) => slot !== null);
      // setBlock clears the container itself, so this must come after reading.
      edit(hit.x, hit.y, hit.z, BlockId.Air);
      drop([
        ...contents.map((slot) => ({ item: slot.id, count: slot.count })),
        { item: PlainItemId.Chest, count: 1 },
      ], hit);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A bed is two blocks and comes up as one thing, same as a door.
    if (isBed(id)) {
      const drops = rollDrops(id);
      removeBed(world, hit.x, hit.y, hit.z);
      drop(drops, hit);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A door is two blocks and comes down as one thing, or half of it is left
    // hanging in the wall.
    if (isDoor(id)) {
      const drops = rollDrops(id);
      removeDoor(world, hit.x, hit.y, hit.z);
      drop(drops, hit);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A block no longer necessarily yields itself: leaves yield nothing, wild
    // wheat yields seeds.
    const drops = rollDrops(id);

    // Not `edit`: breaking has a message of its own, and sending both would
    // say the same thing twice.
    world.setBlock(hit.x, hit.y, hit.z, BlockId.Air);
    net.break(hit.x, hit.y, hit.z);

    // Anything standing on it comes down too, rather than being left hanging.
    const above = world.getBlock(hit.x, hit.y + 1, hit.z);
    if (needsSupport(above)) {
      edit(hit.x, hit.y + 1, hit.z, BlockId.Air);
      drop(rollDrops(above), { x: hit.x, y: hit.y + 1, z: hit.z });
    }

    /*
     * And a смолоскип hung on it, which is beside rather than above.
     *
     * Its facing names the wall it is on, so the one to take down is the
     * neighbour whose facing points back at the block just broken - the
     * torch on the far side of the same wall stays where it is.
     */
    for (const [dx, dz, back] of
      [[1, 0, 3], [-1, 0, 1], [0, 1, 0], [0, -1, 2]] as const) {
      const beside = world.getBlock(hit.x + dx, hit.y, hit.z + dz);
      if (!isWallTorch(beside) || wallTorchFacing(beside) !== back) continue;
      edit(hit.x + dx, hit.y, hit.z + dz, BlockId.Air);
      drop(rollDrops(beside), { x: hit.x + dx, y: hit.y, z: hit.z + dz });
    }

    // Water first: it may have poured into the hole, which changes what is
    // within reach of the farmland around it.
    world.updateWater(hit.x, hit.y, hit.z);
    world.updateLava(hit.x, hit.y, hit.z);
    world.updateOil(hit.x, hit.y, hit.z);
    world.refreshFarmlandAround(hit.x, hit.y, hit.z);
    drop(drops, hit);
    ui.refresh();
    recipes.refresh();
  }

  /**
   * Break soil into furrows.
   *
   * Only from above: hoeing the side of a block makes no sense, and allowing
   * it means ploughing walls. Anything growing on top is knocked off and
   * dropped rather than silently deleted.
   */
  function tryTill(hit: { x: number; y: number; z: number; ny: number }): FarmAction {
    const verdict = canTill(world, hit.x, hit.y, hit.z, hit.ny === 1);
    if (verdict !== "ok") return verdict;

    // Whatever was growing on it is knocked off and kept, not deleted.
    const above = world.getBlock(hit.x, hit.y + 1, hit.z);
    if (above !== BlockId.Air) {
      for (const drop of rollDrops(above)) inventory.add(drop.item, drop.count);
      edit(hit.x, hit.y + 1, hit.z, BlockId.Air);
    }

    edit(
      hit.x, hit.y, hit.z,
      world.isHydrated(hit.x, hit.y, hit.z) ? BlockId.FarmlandWet : BlockId.Farmland,
    );
    return "ok";
  }

  /**
   * Sow seed onto ploughed soil.
   *
   * Only onto farmland, and only from above - seed does not stick to walls.
   */
  function tryPlant(
    hit: { x: number; y: number; z: number; ny: number },
    crop: number,
  ): FarmAction {
    const verdict = canPlant(world, hit.x, hit.y, hit.z, hit.ny === 1);
    if (verdict !== "ok") return verdict;

    edit(hit.x, hit.y + 1, hit.z, crop);
    inventory.takeFromSlot(ui.selectedSlot, 1);
    return "ok";
  }

  /**
   * Right-click.
   *
   * A tool takes precedence over placing: holding a hoe should never drop a
   * block into the world by accident.
   */
  /**
   * Refuse to build into somebody, and say so.
   *
   * Every placement goes through here. The player's own box is already
   * checked inside placeBlock; this is the same courtesy extended to the
   * people who live here, who were otherwise buried to the chest without a
   * word of complaint.
   */
  function blockedByVillager(...cells: readonly [number, number, number][]): boolean {
    for (const [x, y, z] of cells) {
      if (villagerInCell(villagers, x, y, z) === null) continue;
      notify(say("somebody-there", tone.tone, lang.value), 1600);
      return true;
    }
    return false;
  }

  /** Hang a door: two blocks, facing whoever placed it. */
  function tryPlaceDoor(
    hit: { x: number; y: number; z: number; ny: number }, wood: DoorWood,
  ): boolean {
    // Only onto the top of something, like any other standing thing.
    if (hit.ny !== 1) {
      notify(say("not-from-above", tone.tone, lang.value), 1800);
      return false;
    }
    const x = hit.x;
    const y = hit.y + 1;
    const z = hit.z;

    const verdict = canPlaceDoor(world, x, y, z);
    if (verdict !== "ok") {
      notify(say(verdict, tone.tone, lang.value), 1800);
      return false;
    }
    if (blockedByVillager([x, y, z], [x, y + 1, z])) return false;

    const dir = player.lookDirection();
    placeDoor(world, x, y, z, wood, facingFromLook(dir.x, dir.z));
    inventory.takeFromSlot(ui.selectedSlot, 1);
    return true;
  }

  /** Put a stack on the ground in front of the player, thrown. */
  function throwStack(stack: { id: number; count: number }): void {
    const eye = player.eyePosition();
    const look = player.lookDirection();
    pickups.push(makePickup(
      { id: stack.id, count: stack.count },
      { x: eye.x + look.x * 0.6, y: eye.y - 0.2, z: eye.z + look.z * 0.6 },
      // Thrown, not dribbled: it should land a couple of blocks away.
      { x: look.x * 6, y: look.y * 6 + 1.5, z: look.z * 6 },
    ));
  }

  /**
   * Throw away whatever is being pointed at.
   *
   * With the bag open that is the stack on the cursor, or the slot the mouse
   * is over; with it shut it is what is in the hand. One key for the two,
   * because from the player's side it is one thing - "get rid of this" - and
   * which of them is meant is never in doubt.
   */
  function throwHeld(whole: boolean): void {
    if (ui.open && ui.dropUnderCursor(whole)) return;
    const holding = inventory.get(ui.selectedSlot);
    if (holding === null) return;
    const count = whole ? holding.count : 1;
    const taken = inventory.takeFromSlot(ui.selectedSlot, count);
    if (taken === 0) return;
    throwStack({ id: holding.id, count: taken });
    ui.refresh();
    recipes.refresh();
  }

  /** Open a піч: three slots and two gauges above the bag. */
  function openStove(x: number, y: number, z: number): void {
    if (ui.open) ui.close();
    if (skins.open) skins.close();
    openPanel(() => ui.show(null, world.furnaces.open(x, y, z)));
  }

  /** Open a скриня: the same window as the bag, with its row bound on top. */
  function openChest(x: number, y: number, z: number): void {
    if (ui.open) ui.close();
    if (skins.open) skins.close();
    // Through openPanel like every other window, so the pointer is released
    // and the overlay is told - a window opened any other way traps the mouse.
    openPanel(() => ui.show(world.containers.open(x, y, z)));
  }

  /** Loose an arrow along the line of sight, spending one from the bag. */
  function shoot(): void {
    if (!inventory.take(PlainItemId.Arrow, 1)) {
      notify(say("no-arrows", tone.tone, lang.value), 1400);
      return;
    }

    const eye = player.eyePosition();
    const dir = player.lookDirection();
    // Started a little ahead of the eye, or the first step of the segment test
    // finds the block the player is standing in.
    const from = {
      x: eye.x + dir.x * 0.4, y: eye.y + dir.y * 0.4, z: eye.z + dir.z * 0.4,
    };
    arrows.push(makeArrow(from, dir));
    /*
     * And the server flies its own copy of it, at the orcs.
     *
     * A launch, not an impact - the same reasoning as a bolt. "I hit that orc
     * forty blocks away" is a claim nothing at the other end could test; "an
     * arrow left my bow, here, pointing there" is checked against where the
     * server already knows I am standing. Ours is still drawn locally, so the
     * shot leaves the bow on the frame it was fired rather than a snapshot
     * later.
     */
    net.shoot(from, dir);
    audio.play("place", { rate: 1.6, volume: 0.7 });
    ui.refresh();
    recipes.refresh();
  }

  function useHeld(): void {
    const aimed = aim();
    const holdingId = inventory.get(ui.selectedSlot)?.id ?? 0;

    /*
     * Getting into a човен, whatever is in your hand.
     *
     * Before everything else on purpose. A boat is a thing you point at and
     * step into, and a rule that said "only with an empty hand" would mean
     * putting your axe away to cross a lake. The only thing that beats it is
     * a block nearer than the boat, so building on the bank still works.
     */
    /*
     * Getting into a танк, which comes before everything else for the
     * reason the човен does: it is a thing you point at and climb into.
     */
    if (driving === null && tanks.length > 0) {
      const eyeT = player.eyePosition();
      const lookT = player.lookDirection();
      const hitT = pickEntity(
        tanks, eyeT.x, eyeT.y, eyeT.z, lookT.x, lookT.y, lookT.z,
        REACH + 1.5, 2.4, 2.2,
      );
      if (hitT !== null && (aimed === null || aimed.distance > hitT.distance)) {
        const tank = tanks.find((t) => t === hitT.entity);
        if (tank !== undefined && tank.rider === null) {
          driving = tank;
          tank.rider = net.online ? net.joined?.you ?? ALONE : ALONE;
          if (net.online) net.mount(tank.id);
          audio.play("place", { rate: 0.5, volume: 0.7 });
          notify(say("in-tank", tone.tone, lang.value), 2600);
          return;
        }
      }
    }

    /*
     * And putting one down, which is the other half of carrying it.
     *
     * On the ground in front of you rather than in the cell you clicked:
     * three blocks of machine placed into the one-block cell under the
     * crosshair would be a tank standing in a wall.
     */
    if (holdingId === PlainItemId.Tank) {
      if (aimed === null) {
        notify(say("nothing-there", tone.tone, lang.value), 1600);
        return;
      }
      const onto = {
        x: aimed.x + aimed.nx + 0.5,
        y: aimed.y + aimed.ny,
        z: aimed.z + aimed.nz + 0.5,
      };
      const floor = world.groundAt(Math.floor(onto.x), Math.floor(onto.z));
      if (floor < 0) {
        notify(say("nothing-there", tone.tone, lang.value), 1600);
        return;
      }
      const put = makeTank(
        { x: onto.x, y: floor + 1, z: onto.z }, player.yaw);
      tanks.push(put);
      if (net.online) net.deploy(put.x, put.y, put.z, put.yaw);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      audio.play("place", { rate: 0.5 });
      return;
    }

    if (aboard === null) {
      const eyeB = player.eyePosition();
      const lookB = player.lookDirection();
      const hit = pickEntity(
        boats, eyeB.x, eyeB.y, eyeB.z, lookB.x, lookB.y, lookB.z,
        REACH, 1.1, 0.7,
      );
      if (hit !== null && (aimed === null || aimed.distance > hit.distance)) {
        const boat = boats.find((b) => b === hit.entity);
        if (boat !== undefined && boat.rider === null) {
          /*
           * Taken at once and asked about afterwards.
           *
           * The oars answer on the frame the button was pressed rather than
           * a round trip later; if somebody else was already in it, the next
           * snapshot says so and we are put back out - see where the boats
           * are mirrored.
           */
          aboard = boat;
          boat.rider = net.online ? net.joined?.you ?? ALONE : ALONE;
          if (net.online) net.board(boat.id);
          audio.play("place", { rate: 0.9, volume: 0.6 });
          notify(say("aboard", tone.tone, lang.value), 2200);
          return;
        }
      }
    }


    // Water goes in and out of a pail by right-clicking the thing you mean:
    // the lake to fill it, the vat to empty it. No separate verb to learn.
    if (holdingId === PlainItemId.ClayBucket) {
      // Its own ray, not the one everything else aims by - see core/world/
      // scoop.ts. The aiming ray goes straight through water, which is what a
      // spade needs and exactly what a pail must not have.
      const source = waterToScoop(
        (x, y, z) => world.getBlock(x, y, z),
        player.eyePosition(), player.lookDirection(), REACH,
      );
      /*
       * And нафта, which the same pail carries.
       *
       * A second bucket item for oil would be a second thing to craft and a
       * second thing to lose; what comes back from a pool is сира нафта,
       * which the game has had since the shale and which is what a танк is
       * built out of. The pail itself stays in the bag either way.
       */
      if (source === null) {
        const oil = oilToScoop(
          (x, y, z) => world.getBlock(x, y, z),
          player.eyePosition(), player.lookDirection(), REACH,
        );
        if (oil !== null) {
          // The pool loses the cell it gave up: нафта is a pocket, not a
          // sea, and a bottomless one would be a tank factory.
          edit(oil.x, oil.y, oil.z, BlockId.Air);
          world.updateOil(oil.x, oil.y, oil.z);
          if (inventory.add(PlainItemId.CrudeOil, 1) > 0) {
            drop([{ item: PlainItemId.CrudeOil, count: 1 }], oil);
          }
          audio.play("place", { rate: 0.7 });
          ui.refresh();
          recipes.refresh();
          return;
        }
      }
      if (source === null) {
        // Always something, even aiming at open sky. A pail that does nothing
        // and says nothing is indistinguishable from a broken one, which is
        // exactly how this looked before.
        notify(say("nothing-to-fill", tone.tone, lang.value), 1600);
        return;
      }
      inventory.takeFromSlot(ui.selectedSlot, 1);
      /*
       * The lake does not miss one bucketful - but a puddle is not a lake.
       *
       * Water closed over by two of its own kind stays where it is, which
       * is what keeps a shore from being drained a cell at a time. Anything
       * else is a bucketful somebody poured, and it comes back up: leaving
       * it there made a pour permanent and the puddle an endless well.
       * "Якщо вилити воду з відра на землю, її неможливо потім забрати -
       * створюється безкінечне джерело води."
       */
      if (!poolClosesOver((x, y, z) => world.getBlock(x, y, z), source)) {
        edit(source.x, source.y, source.z, BlockId.Air);
        world.updateWater(source.x, source.y, source.z);
        world.updateOil(source.x, source.y, source.z);
        world.refreshFarmlandAround(source.x, source.y, source.z);
      }
      if (inventory.add(PlainItemId.WaterBucket, 1) > 0) {
        inventory.add(PlainItemId.ClayBucket, 1); // no room - give it back
        notify(say("no-space", tone.tone, lang.value), 1600);
      }
      ui.refresh();
      recipes.refresh();
      return;
    }

    if (holdingId === PlainItemId.WaterBucket && aimed !== null) {
      if (world.getBlock(aimed.x, aimed.y, aimed.z) === BlockId.Vat) {
        edit(aimed.x, aimed.y, aimed.z, BlockId.VatFull);
        inventory.takeFromSlot(ui.selectedSlot, 1);
        inventory.add(PlainItemId.ClayBucket, 1);
        notify(say("vat-filled", tone.tone, lang.value), 1600);
        ui.refresh();
        recipes.refresh();
        return;
      }

      // Anywhere else, it goes on the ground as a source and runs from there.
      // This is what carrying water uphill is for: a field too far from the
      // lake gets its own spring at the top of it.
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) {
        notify(say("pour-into-vat", tone.tone, lang.value), 1800);
        return;
      }
      edit(px, py, pz, BlockId.Water);
      world.updateWater(px, py, pz);
      world.updateLava(px, py, pz);
      world.updateOil(px, py, pz);
      world.refreshFarmlandAround(px, py, pz);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      inventory.add(PlainItemId.ClayBucket, 1);
      audio.play("place");
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A chest answers a right-click before anything else it could be doing:
    // holding a block and clicking a chest means "open it", never "build on
    // it", which is the one thing that would make chests infuriating.
    if (aimed !== null && isChest(world.getBlock(aimed.x, aimed.y, aimed.z))) {
      openChest(aimed.x, aimed.y, aimed.z);
      return;
    }

    // A station answers a right-click by opening the bag.
    //
    // Stations here are places rather than containers - you stand near one and
    // the recipes it allows become craftable - but nothing about a чан says
    // so, and the first thing anyone does is right-click it holding the stalks
    // they mean to ret. That did nothing whatever and said nothing either.
    if (aimed !== null) {
      const at = world.getBlock(aimed.x, aimed.y, aimed.z);
      if (at === BlockId.Vat) {
        // An empty vat is not a station yet, and the reason is specific.
        notify(say("vat-empty", tone.tone, lang.value), 2000);
        return;
      }
      if (isStove(at)) {
        openStove(aimed.x, aimed.y, aimed.z);
        return;
      }
      if (at === BlockId.VatFull || at === BlockId.SpinningWheel) {
        toggleInventory();
        return;
      }
    }

    // A bed answers a right-click before anything else: sleep if it is night,
    // and either way this is now where you wake up.
    if (aimed !== null && isBed(world.getBlock(aimed.x, aimed.y, aimed.z))) {
      const spot = wakeSpot(world, aimed.x, aimed.y, aimed.z);
      if (spot !== null) {
        spawn = spot;
        const result = sleep(cycle);
        notify(say(result === "slept" ? "slept" : "spawn-set", tone.tone, lang.value), 2200);
      }
      return;
    }

    // Anything hinged opens on right-click whatever is in hand. Holding a
    // block should not mean building over the door you were walking through.
    if (aimed !== null) {
      const at = world.getBlock(aimed.x, aimed.y, aimed.z);
      // A rushnyk hangs flat and has nothing to open.
      if ((isDoor(at) || isShutter(at) || isGate(at)) && !isRushnyk(at)) {
        togglePanel(world, aimed.x, aimed.y, aimed.z);
        return;
      }
    }

    const holding = inventory.get(ui.selectedSlot);

    // A stove turns to face whoever sets it down, so it cannot go through the
    // ordinary "this item places that block" path.
    // A мазана стіна needs no facing: it grows arms toward whatever it
    // touches, and works that out for itself every time the mesher looks.
    /*
     * The things that place a block with no facing of their own.
     *
     * A table rather than a branch each, because there are four of them now
     * and the branch is identical every time - and because the гранітна
     * стіна shipped with an item, a recipe and no way to put it down at all,
     * which is what a fifth copy of this gets you.
     */
    const plain = holding === null ? undefined : PLAIN_PLACEMENTS.get(holding.id);
    if (plain !== undefined && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;
      if (blockedByVillager([px, py, pz])) return;

      edit(px, py, pz, plain);
      audio.play("place");
      world.updateWater(px, py, pz);
      world.updateLava(px, py, pz);
      world.updateOil(px, py, pz);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A скриня turns to face whoever sets it down, same as a stove.
    if (holding !== null && holding.id === PlainItemId.Chest && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;
      if (blockedByVillager([px, py, pz])) return;

      const look = player.lookDirection();
      edit(px, py, pz, chestId(facingFromLook(look.x, look.z)));
      audio.play("place");
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    const finish = holding === null ? -1 : stoveItemFinish(holding.id);
    if (finish >= 0 && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;

      const dir = player.lookDirection();
      // Same rule as a door: the face nearest the player is the one you see.
      edit(px, py, pz, stoveId(
        finish as StoveFinish, facingFromLook(dir.x, dir.z),
      ));
      world.refreshFarmlandAround(px, py, pz);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    /*
     * The креслярський камінь: a building in a block.
     *
     * Put down like anything else, turned to face you like a stove, and the
     * хата stands up behind it. Nothing is flattened to make room - if there
     * is a tree or a wall in the way it refuses and says so, and the stone
     * stays in your hand.
     */
    const carried = buildingNamed(blueprintBuilding(holdingId));
    if (carried !== undefined && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;

      const dir = player.lookDirection();
      const facing = facingFromLook(dir.x, dir.z);
      /*
       * Collected, then written in one go.
       *
       * A кремль is twenty-one thousand blocks and every one of them through
       * `setBlock` would relight the world around it - fifty seconds of a
       * frozen tab for a building. `setBlocks` writes them all and rebuilds
       * the light once. The хата's four hundred take the same path, because
       * two paths would mean the rare one is the one that rots.
       */
      const pending: { x: number; y: number; z: number; id: number }[] = [];
      // What of the player's own work is standing here, before it goes.
      const built = carried.clears === "everything"
        ? builtInside(carried, facing, px, py, pz,
          (x, y, z) => world.getBlock(x, y, z), isNaturalGround)
        : 0;
      const verdict = raise(
        carried, facing, px, py, pz,
        (x, y, z) => world.getBlock(x, y, z),
        (x, y, z) => world.withinBounds(x, y, z),
        (x, y, z, id) => { pending.push({ x, y, z, id }); },
        isNaturalGround,
      );
      if (verdict !== "ok") {
        notify(say(verdict === "off-the-map" ? "build-off-map" : "build-no-room",
          tone.tone, lang.value), 3200);
        return;
      }
      world.setBlocks(pending);
      // One instruction, never the blocks: see NETWORK.md. Everybody runs the
      // same pure `raise` and lands on the same world.
      net.raise(carried.name, facing, px, py, pz);
      /*
       * And the stone is spent.
       *
       * It used to go down as a block in front of the door, "so a refusal
       * leaves nothing behind" - but a refusal returns above, before
       * anything is written, and what the stone left behind on success was
       * a stone: "використаний креслярський камінь має зникати". The
       * building is what it was for; the item comes off the belt below.
       */
      /*
       * Counted before the writes land, and reported after they do.
       *
       * Only for a building that clears everything - for the others the
       * question cannot arise, since anything built would have refused it.
       */
      if (built > 0) {
        notify(`${say("built-over", tone.tone, lang.value)} ${built}`, 5000);
      }
      audio.play("place");
      notify(say("built", tone.tone, lang.value), 2600);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A rushnyk hangs like a shutter but never swings, so it shares the
    // placement rule and none of the state.
    if (holdingId === PlainItemId.Rushnyk && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      const verdict = canPlaceShutter(world, px, py, pz);
      if (verdict !== "ok") {
        notify(say(verdict === "no-room" ? "rushnyk-occupied" : "rushnyk-wall",
          tone.tone, lang.value), 1800);
        return;
      }
      if (blockedByVillager([px, py, pz])) return;
      /*
       * Hung on the face that was clicked, not on the way the player looks.
       *
       * "Рушник висить в повітрі": the placement rule asked only whether
       * *some* neighbour was solid, and the facing came from the look
       * direction - so a рушник put up in a doorway with the wall on its
       * left came out facing the player with its back to open air.
       */
      const back = facingFromFace(aimed.nx, aimed.ny, aimed.nz);
      if (back === null) {
        notify(say("rushnyk-wall", tone.tone, lang.value), 1800);
        return;
      }
      edit(px, py, pz, rushnykId(back));
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // Shutters hang on a wall face, so they place into the cell the ray
    // pointed at rather than onto a surface like a block.
    const shutterWood = holding === null ? -1 : shutterItemWood(holding.id);
    if (shutterWood >= 0 && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      const verdict = canPlaceShutter(world, px, py, pz);
      if (verdict !== "ok") {
        notify(say(verdict, tone.tone, lang.value), 1800);
        return;
      }
      if (blockedByVillager([px, py, pz])) return;
      // The same rule as the рушник above, and for the same reason: a
      // shutter hangs on the wall you clicked, not on the one you happen to
      // be facing.
      const hinge = facingFromFace(aimed.nx, aimed.ny, aimed.nz);
      if (hinge === null) {
        notify(say("nothing-to-hang-on", tone.tone, lang.value), 1800);
        return;
      }
      placeShutter(world, px, py, pz, shutterWood as DoorWood, hinge);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    // A bed lies down across two cells, so it places like a door rather than
    // like a block.
    if (holding !== null && holding.id === PlainItemId.Bed && aimed !== null) {
      if (aimed.ny !== 1) {
        notify(say("not-from-above", tone.tone, lang.value), 1800);
        return;
      }
      const look = player.lookDirection();
      // Laid away from the player, so you see the head end from where you are.
      const facing = ((facingFromLook(look.x, look.z) + 2) % 4) as 0 | 1 | 2 | 3;
      const verdict = canPlaceBed(world, aimed.x, aimed.y + 1, aimed.z, facing);
      if (verdict !== "ok") {
        notify(say(verdict, tone.tone, lang.value), 1800);
        return;
      }
      const head = headCell(aimed.x, aimed.z, facing);
      if (blockedByVillager(
        [aimed.x, aimed.y + 1, aimed.z], [head.x, aimed.y + 1, head.z],
      )) return;
      placeBed(world, aimed.x, aimed.y + 1, aimed.z, facing);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      ui.refresh();
      recipes.refresh();
      return;
    }

    const wood = holding === null ? -1 : doorItemWood(holding.id);
    if (wood >= 0) {
      if (aimed !== null && tryPlaceDoor(aimed, wood as DoorWood)) {
        ui.refresh();
        recipes.refresh();
      }
      return;
    }
    const use = holding === null ? undefined : itemUse(holding.id);

    // Shooting needs no target: an arrow into open sky is a legitimate shot,
    // so this comes before the "what are you aiming at" check below.
    if (use?.kind === "shoot") {
      shoot();
      return;
    }

    // Nor does a drone: it goes up in front of you, wherever you happen to be
    // looking.
    if (use?.kind === "drone") {
      launchDrone();
      return;
    }

    // And the пульт sits you back behind the eyes of one already up.
    if (use?.kind === "remote") {
      if (takeControls()) {
        audio.play("place", { rate: 1.3 });
        notify(say("drone-up", tone.tone, lang.value), 2000);
      } else {
        notify(say("drone-none", tone.tone, lang.value), 2500);
      }
      return;
    }

    // The rod. The same button throws the line and strikes with it, which is
    // how a вудка works and how everyone expects it to: what it does depends
    // on whether there is already something in the water.
    if (use?.kind === "fish") {
      if (line.status !== "idle") {
        const got = line.pull();
        if (got === "caught") {
          inventory.add(KARAS_DROP, 1);
          audio.play("place", { rate: 0.8 });
          notify(say("caught-fish", tone.tone, lang.value), 2000);
          ui.refresh();
          recipes.refresh();
        } else {
          notify(say("pulled-early", tone.tone, lang.value), 1800);
        }
        return;
      }
      // Casting needs water in front of you. The aiming ray goes through
      // water on purpose so a spade can reach a lake bed, so this asks the
      // world directly rather than asking what was struck.
      const eye2 = player.eyePosition();
      const look2 = player.lookDirection();
      let wet = false;
      for (let d = 1; d <= REACH && !wet; d++) {
        wet = isFluidAt(
          Math.floor(eye2.x + look2.x * d),
          Math.floor(eye2.y + look2.y * d),
          Math.floor(eye2.z + look2.z * d),
        );
      }
      if (!wet) {
        notify(say("no-water", tone.tone, lang.value), 1600);
        return;
      }
      line.cast();
      audio.play("place", { rate: 1.2 });
      notify(say("cast", tone.tone, lang.value), 1500);
      return;
    }

    /*
     * Feeding a dog, which comes before eating the food yourself.
     *
     * The same button and the same meat: what decides is whether there is a
     * собака in front of you. It has to come first or the branch below would
     * have swallowed the сало before anything looked at what you were
     * pointing at - a dog fed by a player at full сила would otherwise be
     * told they were not hungry.
     *
     * Only a hungry-looking stray. Feeding one that is already yours does
     * nothing but lose you the meat, so it is refused and said out loud.
     *
     * A кістка counts as well as meat, and is not food: `tamesDogs` knows
     * what a dog wants, because that is a fact about the item and not about
     * the page holding it. A bone in hand with no dog in front of you does
     * nothing at all - it falls past the eating branch below, which is
     * exactly right.
     */
    if (tamesDogs(holdingId)) {
      const eye3 = player.eyePosition();
      const look3 = player.lookDirection();
      const met = pickEntity(
        beasts, eye3.x, eye3.y, eye3.z, look3.x, look3.y, look3.z,
        REACH, ORC_WIDTH, ORC_HEIGHT,
      );
      const animal = met === null ? null
        : beasts.find((b) => b === met.entity) ?? null;
      if (animal !== null && animal.breed.tameable === true) {
        if (animal.owner !== null) {
          notify(say("already-yours", tone.tone, lang.value), 1600);
          return;
        }
        animal.owner = ALONE;
        animal.vitals.heal(animal.breed.maxHp);
        inventory.takeFromSlot(ui.selectedSlot, 1);
        audio.play("place", { rate: 1.3 });
        notify(say("tamed", tone.tone, lang.value), 2200);
        ui.refresh();
        recipes.refresh();
        return;
      }
    }

    // Eating needs no target either, and it needs a reason: a body already at
    // full сила should keep the сало rather than spend it on nothing.
    if (use?.kind === "eat") {
      if (health.hp >= health.max) {
        notify(say("not-hungry", tone.tone, lang.value), 1600);
        return;
      }
      health.heal(use.heals);
      inventory.takeFromSlot(ui.selectedSlot, 1);
      audio.play("breakGrass", { rate: 0.7 });
      notify(say("ate", tone.tone, lang.value), 1400);
      ui.refresh();
      recipes.refresh();
      return;
    }

    /*
     * Спустити човен на воду.
     *
     * Its own ray rather than the aiming one, for the reason the bucket has
     * its own: the aiming ray goes straight through water so that a spade
     * can reach a lake bed, which means "what am I looking at" is never the
     * water itself. So this walks the look direction and takes the first
     * cell of water it meets.
     */
    if (use?.kind === "launch") {
      const eye4 = player.eyePosition();
      const look4 = player.lookDirection();
      let put: { x: number; z: number } | null = null;
      for (let d = 1; d <= REACH + 1 && put === null; d += 0.5) {
        const x = Math.floor(eye4.x + look4.x * d);
        const y = Math.floor(eye4.y + look4.y * d);
        const z = Math.floor(eye4.z + look4.z * d);
        if (isWater(world.getBlock(x, y, z))) put = { x, z };
      }
      const top = put === null ? null : surfaceAt(put.x + 0.5, put.z + 0.5);
      if (put === null || top === null) {
        notify(say("no-water", tone.tone, lang.value), 1600);
        return;
      }
      /*
       * Online the boat is the server's to make.
       *
       * Not made here as well: the hull that comes back in the next snapshot
       * has the server's id on it, and one made locally would sit beside it
       * as a second boat nobody could get into. A fifth of a second before
       * it appears is the same wait as putting down a block.
       */
      if (net.online) net.launch(put.x, put.z);
      else {
        boats.push(makeBoat(
          { x: put.x + 0.5, y: top + BOAT_DRAFT, z: put.z + 0.5 },
          player.yaw,
        ));
      }
      inventory.takeFromSlot(ui.selectedSlot, 1);
      audio.play("place", { rate: 0.7 });
      notify(say("launched", tone.tone, lang.value), 1800);
      ui.refresh();
      recipes.refresh();
      return;
    }

    /*
     * Кресало: a spark into whatever you are pointing at.
     *
     * Onto the block itself rather than the face in front of it. Fire in
     * this game lives *in* a cell and eats what is there, so lighting the
     * air beside a стіжок would light nothing - and a player aiming at a
     * haystack means the haystack.
     *
     * What refuses is `catchesFire`, not this: strike at stone or at bare
     * earth all day and nothing happens, which is the rule that keeps a
     * кресало from being a way to burn a hill down.
     */
    if (use?.kind === "strike") {
      if (aimed === null) {
        notify(say("nothing-to-light", tone.tone, lang.value), 1500);
        return;
      }
      const target = world.getBlock(aimed.x, aimed.y, aimed.z);
      if (fire.isBurning(aimed.x, aimed.y, aimed.z)) return;
      if (!fire.light(aimed.x, aimed.y, aimed.z, target, false)) {
        notify(say("wont-catch", tone.tone, lang.value), 1600);
        audio.play("breakStone", { rate: 1.9, volume: 0.25 });
        return;
      }
      // Online, the fire that counts is the server's: ours draws the flames,
      // and the blocks it eats come back as edits like anybody else's.
      if (net.online) net.spark(aimed.x, aimed.y, aimed.z);
      audio.play("breakGrass", { rate: 1.6, volume: 0.5 });
      notify(say("lit-fire", tone.tone, lang.value), 1500);
      return;
    }

    /*
     * The требушет is turned like a stove: it throws the way it is pointed.
     */
    /*
     * Сходи, turned to climb away from you.
     *
     * The same placement a требушет gets, and for the same reason: what a
     * placed stair *means* is which way it goes up, and one that faced
     * wherever it happened to be put would make a pile rather than a
     * staircase. Facing 0 is -Z, so looking north and placing puts the high
     * side away from you - which is the way somebody building a staircase is
     * walking.
     */
    /*
     * A хвіртка is placed facing the way you are, like a door and a stair.
     *
     * The тин beside it needs nothing: a fence has no facing, it joins to
     * whatever is there - see `wallConnects`.
     */
    /*
     * Into the void as well - and then turned with its back to the block.
     *
     * "Сходи мали стати навпаки, спиною до блока, а не до повітря." A stair
     * aimed at nothing used to fall through to the plain placement and come
     * out facing whichever way is nought. Placed beside a block, the thing
     * you are extending is that block, so the high side goes against it:
     * the facing is the way from the new cell *to* the block, which is the
     * hit's normal reversed. Only sideways - beside the block you stand on
     * the normal points up, and up is not a way to face.
     */
    const at = aimed ?? aimBeside();
    if ((holdingId === WOOD_STAIRS_BASE || holdingId === STONE_STAIRS_BASE ||
        holdingId === gateId(0, false)) && at !== null) {
      const px = at.x + at.nx;
      const py = at.y + at.ny;
      const pz = at.z + at.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;
      /*
       * Sideways, the stair's back goes against the block it is put against.
       *
       * Whether the side face was clicked or the void beside it was aimed
       * at, the block you are extending is that block, and "сходи мали
       * стати спиною до блока, а не до повітря" is the whole rule. Only on
       * a floor or a ceiling is there no block to lean on, and there it
       * climbs away from you as before.
       */
      const look = player.lookDirection();
      // A stair's back goes to the block, or away from you; a хвіртка turns
      // to face you like a door. Two conventions, so two answers.
      const facing = facingFromLook(look.x, look.z);
      const stairFacing = stairFacingFor(at, look);
      /*
       * Put up under a block, a stair hangs from it.
       *
       * "Якщо ставляться до блока знизу вверх." The face that was clicked
       * says which: the underside of a block has a normal of -1 in y, and
       * nothing else does. A хвіртка is not turned over - it has no up.
       */
      edit(px, py, pz,
        holdingId === WOOD_STAIRS_BASE
          ? stairsForFace(false, stairFacing, at.ny)
          : holdingId === STONE_STAIRS_BASE
          ? stairsForFace(true, stairFacing, at.ny)
          : gateId(facing, false));
      inventory.takeFromSlot(ui.selectedSlot, 1);
      audio.play("place");
      ui.refresh();
      recipes.refresh();
      return;
    }

    if (holdingId === TREBUCHET_BASE && aimed !== null) {
      const px = aimed.x + aimed.nx;
      const py = aimed.y + aimed.ny;
      const pz = aimed.z + aimed.nz;
      if (!isReplaceable(world.getBlock(px, py, pz))) return;
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, px, py, pz,
      )) return;
      const look = player.lookDirection();
      /*
       * Turned to face whoever put it down. "Розверни требушет на 180
       * градусів коли його ставиш."
       *
       * It used to be turned the other way, on the reasoning that an engine
       * throws where it points - which stopped being true the day the throw
       * started following the player's eye. What the facing decides now is
       * only which way the machine *stands*, and standing with its arm
       * towards you is how it reads from where you are putting it.
       */
      edit(px, py, pz, trebuchetId(facingFromLook(look.x, look.z)));
      inventory.takeFromSlot(ui.selectedSlot, 1);
      audio.play("place");
      ui.refresh();
      recipes.refresh();
      return;
    }

    /*
     * Winding, and loosing.
     *
     * "Зроби щоб напрямок снарядів залежив від напрямку погляду гравця і щоб
     * можна було регулювати силу та висоту кидка." Hold the button on the
     * engine with a снаряд in hand: the arm winds while you hold it, and
     * where you are looking when you let go is where it throws - yaw for the
     * direction, pitch for how high it is lobbed.
     *
     * The engine turns to the shot as it goes, so a row of them points where
     * it last threw rather than where it was built.
     */
    if (holdingId === PlainItemId.Firebolt && aimed !== null &&
        isTrebuchet(world.getBlock(aimed.x, aimed.y, aimed.z))) {
      winding = { x: aimed.x, y: aimed.y, z: aimed.z, power: 0 };
      audio.play("breakWood", { rate: 1.8, volume: 0.4 });
      return;
    }

    /*
     * Letting a creature out of the storeroom.
     *
     * Handled before the ploughing and sowing below, which assume by then
     * that whatever `use` is left can only be a tool - the compiler said so
     * the moment "spawn" joined the union, which is the whole reason that
     * union is a union.
     */
    if (use?.kind === "spawn") {
      if (aimed === null) {
        notify(say("nothing-aimed", tone.tone, lang.value), 1200);
        return;
      }
      const def = spawnForItem(holdingId);
      if (def === undefined) return;

      const at = {
        x: aimed.x + aimed.nx, y: aimed.y + aimed.ny, z: aimed.z + aimed.nz,
      };
      const here = world.getBlock(at.x, at.y, at.z);
      const below = world.getBlock(at.x, at.y - 1, at.z);

      if (def.habitat === "water") {
        // A карась goes in water and nowhere else.
        if (!isWater(here)) {
          notify(say("spawn-needs-water", tone.tone, lang.value), 1800);
          return;
        }
      } else {
        // Everything else wants air to stand in and something under it.
        if (isSolid(here) || isWater(here)) {
          notify(say("spawn-occupied", tone.tone, lang.value), 1800);
          return;
        }
        if (!isSolid(below)) {
          notify(say("spawn-needs-ground", tone.tone, lang.value), 1800);
          return;
        }
      }
      // Not inside the player, who would then be standing in a тур.
      if (boxOverlapsVoxel(
        player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH / 2, PLAYER_HEIGHT, at.x, at.y, at.z,
      )) {
        notify(say("spawn-occupied", tone.tone, lang.value), 1800);
        return;
      }

      const middle = { x: at.x + 0.5, y: at.y + 0.5, z: at.z + 0.5 };
      /*
       * Nothing alive is yours to make on somebody else's world.
       *
       * Everything that walks there is the server's, mirrors and nothing
       * else - and one made here would be given a local number, counted from
       * one, the same as the server's. The collision does not look like a
       * collision: the mirror of the server's pig 1 is quietly dragged to
       * wherever this egg was thrown, and the real one dropped on the next
       * snapshot. It was the orc egg only until the herd moved too.
       */
      if (net.online) {
        notify(say("spawn-not-yours", tone.tone, lang.value), 2000);
        return;
      }
      switch (def.kind) {
        case "pig": case "tur": case "kurka": case "sobaka": case "kit":
          beasts.push(makeBeast(def.kind, middle));
          break;
        case "karas":
          fish.push(makeFish(middle));
          break;
        case "orc":
          orcs.push(makeOrc(middle));
          break;
        case "selianyn":
          villagers.push(makeVillager("дядько Гриць", "selianyn", "villager-hit", middle));
          break;
        case "selianka":
          villagers.push(
            makeVillager("тітка Одарка", "selianka", "villager-hit-her", middle));
          break;
      }
      inventory.takeFromSlot(ui.selectedSlot, 1);
      audio.play("place");
      notify(say("spawned", tone.tone, lang.value), 1200);
      ui.refresh();
      recipes.refresh();
      return;
    }

    if (use !== undefined) {
      const hit = aimed;
      if (hit === null) {
        notify(say("nothing-aimed", tone.tone, lang.value), 1200);
        return;
      }

      // Everything below wants a block under the crosshair; the uses that do
      // not have already returned.
      if (use.kind !== "till" && use.kind !== "plant") return;
      const verdict = use.kind === "till" ? tryTill(hit) : tryPlant(hit, use.crop);
      if (verdict === "ok") {
        ui.refresh();
        recipes.refresh();
      } else {
        // Say why. A tool that silently does nothing reads as a broken game.
        notify(say(verdict, tone.tone, lang.value), 1800);
      }
      // A tool or a seed never falls back to placing a block: holding a hoe
      // should not drop one into the world by accident.
      return;
    }
    placeBlock();
  }

  /**
   * Aimed at nothing: the empty cell along the ray that touches something.
   *
   * "Гравець стоїть на блоці, попереду пустота, він може поставити блок
   * впритул до свого блока просто цілячись в пустоту перед блоком." Asked
   * only when `aim` found nothing, so a wall is still a wall. The cell has
   * to be one a block may go in at all - not inside us, not on top of a
   * villager - and one that is not is walked past rather than refused.
   */
  function aimBeside() {
    const eye = player.eyePosition();
    const dir = player.lookDirection();
    return raycastBeside(
      eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, REACH, isTargetableAt,
      (x, y, z) => isReplaceable(world.getBlock(x, y, z)) &&
        !boxOverlapsVoxel(
          player.position.x, player.position.y, player.position.z,
          PLAYER_WIDTH / 2, PLAYER_HEIGHT, x, y, z) &&
        !blockedByVillager([x, y, z]),
    );
  }

  function placeBlock(): void {
    const hit = aim() ?? aimBeside();
    if (hit === null) return;

    // The new block goes in the empty cell against the face that was hit.
    const px = hit.x + hit.nx;
    const py = hit.y + hit.ny;
    const pz = hit.z + hit.nz;

    // Refuse to place inside ourselves - otherwise you can seal yourself in.
    if (boxOverlapsVoxel(
      player.position.x, player.position.y, player.position.z,
      PLAYER_WIDTH / 2, PLAYER_HEIGHT,
      px, py, pz,
    )) return;

    if (!isReplaceable(world.getBlock(px, py, pz))) return;
    if (blockedByVillager([px, py, pz])) return;

    // You can only place what you are actually holding, and only if it is
    // something that places a block at all - seeds and tools are not.
    const holding = inventory.get(ui.selectedSlot);
    if (holding === null) return;
    let block = placedBlock(holding.id);
    if (block === BlockId.Air) return;

    /*
     * A смолоскип put against a wall hangs on it.
     *
     * Which wall is the face that was clicked: the torch goes in the empty
     * cell, and the block it was aimed at is behind it. Only against a solid
     * one - a torch on the side of a bush is a torch on nothing - and only
     * sideways: aimed at a floor or a ceiling it is the plain standing
     * смолоскип it always was.
     */
    if (block === BlockId.Torch && hit.ny === 0 &&
      isSolid(world.getBlock(hit.x, hit.y, hit.z))) {
      const facing: DoorFacing =
        hit.nz < 0 ? 2 : hit.nz > 0 ? 0 : hit.nx < 0 ? 1 : 3;
      block = wallTorchId(facing);
    }

    /*
     * And a колода put against a side lies down along it.
     *
     * "Зроби щоб колоди можливо було ставити горизонтально." The face
     * settles it, the way it settles which way up a stair goes: the top or
     * the bottom of something and it stands, a side and it lies along that
     * side's axis. Laying one on the ground is a click on the side of a
     * neighbour rather than on the ground itself, which is the gesture every
     * other game of this kind uses for the same thing.
     */
    if (UPRIGHT_LOGS.includes(block)) {
      block = logForFace(block, hit.nx, hit.ny, hit.nz);
    }

    // Not `edit`: placing has a message of its own, with a check of its own
    // that a bare `set` deliberately does not make.
    world.setBlock(px, py, pz, block);
    /*
     * Told to the server as well as done here.
     *
     * Done locally first so the block appears under your hand rather than a
     * round trip later - and then confirmed, because the server is the
     * authority and may refuse it. When it does, its own snapshot of that
     * chunk puts things back.
     */
    net.place(px, py, pz, block);
    audio.play("place");
    // A block dropped into a stream dams it, so the flow has to be redrawn
    // from its sources - not only when a block is broken.
    world.updateWater(px, py, pz);
    world.updateLava(px, py, pz);
    world.updateOil(px, py, pz);
    world.refreshFarmlandAround(px, py, pz);
    inventory.takeFromSlot(ui.selectedSlot, 1);
    ui.refresh();
    recipes.refresh();
  }

  input.onHotbar = (slot) => ui.select(slot);
  input.onScroll = (delta) => ui.scrollSelection(delta);

  /** Whether there is already an adventure to write over. See below. */
  let hasAdventure = false;

  /** What the bag looked like last time it was sent. See below. */
  let bagMark = 0;



  /** Seconds left of the blow being swung, or nought. */
  let strikeLeft = 0;

  /**
   * Digging: the button is down, and this is how far into the block it is.
   *
   * "Hits count only if you hold the button" - so the count lives here,
   * beside the button, and not on the block. Let go and it is lost.
   */
  const digging = new Digging();
  let mining = false;
  /** Seconds to the next swing at nothing. See `dig`. */
  let empty = 0;

  /**
   * Say so, once, when what is held cannot work the block at all.
   *
   * The refusal is older than durability - a pick is not *faster* on stone,
   * it is the only thing that works - and it has to be said on the press
   * rather than when the blows run out, or the player would hold the button
   * on a rock for three seconds to be told it was never going to work.
   */
  function refuseIfWrongTool(): void {
    const hit = aim();
    if (hit === null) return;
    const id = world.getBlock(hit.x, hit.y, hit.z);
    if (id === BlockId.Air) return;
    const held = inventory.get(ui.selectedSlot)?.id ?? 0;

    /*
     * Three refusals, and they are not the same refusal.
     *
     * Wrong kind of tool - a hand on stone - is the old one. A block nothing
     * breaks is the незламний блок, and saying "you need a pick" about it
     * would send somebody to make one for nothing. And адамантій is the one
     * block that asks about the *tier*: "його можна копати тільки залізним
     * кайлом", so a wooden pick is the right tool and still not enough.
     */
    if (blowsToBreak(id, held, creativeHere()) > 0) return;
    mining = false;

    const needed = requiredTool(id);
    const strong = requiredStrength(id);
    if (needed !== null && itemTool(held) !== needed) {
      notify(say(TOOL_REFUSAL[needed], tone.tone, lang.value), 1600);
      return;
    }
    if (strong > 0) {
      notify(say(strong >= 4 ? "need-adamant" : "need-iron-pick",
        tone.tone, lang.value), 1800);
      return;
    }
    notify(say("unbreakable", tone.tone, lang.value), 1600);
  }

  /**
   * One tick of holding the button down on a block.
   *
   * The pacing is `BLOW_SECONDS`; how many blows the block wants is the
   * table in core/block/breaking.ts, read every tick so that swapping to a
   * pick halfway through a stone counts from here on.
   */
  /**
   * Whether blocks fall at one blow here. Only alone: a world with other
   * people in it plays by one rule for everybody, and a switch in one
   * browser is not a rule anybody else agreed to.
   */
  /**
   * Flying, on «Сучасна війна», is for Pavlo alone: "літати на цій мапі
   * ніхто не має вміти, крім мене". A war where anybody can fly over the
   * wall is not a war. By the remembered name, which in a shared game is
   * the name the world knows you by.
   */
  function mayFly(): boolean {
    return world.opts.terrain !== "war" || savedName() === "Pavlo";
  }

  function creativeHere(): boolean {
    return creative.value && joined === null;
  }

  function dig(dt: number): void {
    // Not while flying the drone: the button means "drop a снаряд" there,
    // and the crosshair the player is watching is the drone's, not the one
    // over the body they left standing in a field.
    const at = mining && input.locked && !atControls ? aim() : null;
    const id = at === null
      ? BlockId.Air : world.getBlock(at.x, at.y, at.z);
    const held = inventory.get(ui.selectedSlot)?.id ?? 0;
    const wanted = requiredTool(id);
    const allowed = wanted === null || itemTool(held) === wanted;
    const needed = at === null || id === BlockId.Air || !allowed
      ? 0 : blowsToBreak(id, held, creativeHere());

    /*
     * A swing at thin air is still a swing.
     *
     * Holding the button at the sky, at water, or at a rock you have no pick
     * for moves the arm on the same beat as digging does - a hand that goes
     * still the moment it has nothing to bite on looks like the game stopped
     * listening. Nothing is dug and nothing is counted against a block.
     */
    if (mining && needed === 0) {
      empty -= dt;
      if (empty <= 0) {
        empty += BLOW_SECONDS;
        strikeLeft = STRIKE_SECONDS;
        blows++;
      }
    } else {
      empty = 0;
    }

    const swung = digging.swing(dt, mining, at, needed);
    if (swung.blows > 0) {
      // The arm goes on swinging while the button is held: a hand that
      // moves once and then rests on a block looks like the game stopped.
      strikeLeft = STRIKE_SECONDS;
      blows += swung.blows;
      // Quieter and higher than the block coming apart - this is the sound
      // of working at it, and the break itself is still the loud one.
      audio.play(soundForBlock(id), { volume: 0.35, rate: 1.35 });
    }
    if (swung.done) breakBlock();
  }
  /**
   * How many blows have been swung, ever.
   *
   * What goes over the wire, rather than the timer above: fifteen snapshots a
   * second cannot describe a blow that lasts 0.28 of one, but a number that
   * goes up says *a blow started* and the body at the other end swings on its
   * own clock from there.
   */
  let blows = 0;

  let speedIndex = 0;
  input.onAction = (action) => {
    if ((action === "timeSpeed" || action === "skipToNight") && joined !== null) {
      /*
       * Not with other people either. The world's clock is the host's and
       * goes out to everybody; a pause pressed in one window stopped only
       * that window's copy, said "(paused)" in the readout, and night came
       * anyway: "настала ніч, хоча час на паузі".
       */
      cycle.timeScale = 1;
      notify(uiText("net-shared-time", lang.value), 2600);
      return;
    }
    if (action === "timeSpeed") {
      // Not on the hill: five minutes is one round, and a sun that can be
      // moved is a race somebody can darken.
      if (world.opts.terrain === "hill") return;
      speedIndex = (speedIndex + 1) % TIME_SPEEDS.length;
      cycle.timeScale = TIME_SPEEDS[speedIndex]!;
    } else if (action === "skipToNight") {
      if (world.opts.terrain === "hill") return;
      // Jump to just before midnight - quickest way to check the moon.
      cycle.t = 0.96;
    } else if (action === "toggleFly") {
      // Not while looking through the drone: it would put your own body into
      // free flight while you were not there to see where it went.
      if (atControls) return;
      if (player.mode === "walk" && !mayFly()) {
        notify(uiText("no-flying-here", lang.value), 2000);
        return;
      }
      player.toggleFly();
    } else if (action === "placeEnd") {
      // Letting go of the right button. Only the требушет cares, and it
      // cares a great deal: this is the throw.
      looseTrebuchet();
    } else if (action === "landDrone") {
      leaveControls();
    } else if (action === "break" && atControls) {
      /*
       * Aimed at the crosshair, rather than let go over the side.
       *
       * "Дрон дуже погано попадає, його граната летить далеко від приціла."
       * It used to leave with the drone's own speed and a nudge downwards,
       * which put it under the drone and had nothing to do with where the
       * camera was pointing - you had to lead a target by eye through a lens
       * with a crosshair drawn in the middle of it.
       *
       * Now the ray under the crosshair finds what you are looking at and
       * `aimedThrow` works out the arc that lands on it. Nothing is asked of
       * the player's arithmetic; what is asked is that they point at the hut.
       */
      if (drone.dropOne()) {
        const eye = drone.eyePosition();
        const look = drone.lookDirection();
        const seen = raycastVoxels(eye.x, eye.y, eye.z,
          look.x, look.y, look.z, DRONE_SIGHT, isSolidAt);
        // Looking at nothing but sky: it goes where it is pointing, and
        // wherever that lands is where it lands.
        const target = seen === null
          ? {
            x: eye.x + look.x * DRONE_SIGHT,
            y: eye.y + look.y * DRONE_SIGHT,
            z: eye.z + look.z * DRONE_SIGHT,
          }
          : { x: seen.x + 0.5, y: seen.y + 1, z: seen.z + 0.5 };
        const from = { x: drone.x, y: drone.y - 0.3, z: drone.z };
        const velocity = aimedThrow(from, target);
        throwBolt(from, velocity ?? { x: drone.vx, y: -2, z: drone.vz });
        audio.play("breakWood", { rate: 0.6 });
      } else if (drone.load <= 0) {
        notify(say("drone-empty", tone.tone, lang.value), 2000);
      }
    } else if (action === "break") {
      /*
       * A blow at something alive lands now; a block is worked at.
       *
       * Blocks have durability - see core/block/breaking.ts - so pressing
       * the button no longer takes one out of the world. It starts digging,
       * and the digging carries on for as long as the button is held.
       */
      /*
       * In a танк the button is the gun, and nothing else.
       *
       * Not digging, not swinging: a driver leaning out to punch a tree
       * would be a strange thing for a machine to allow, and the crosshair
       * in a tank means "put the next shell there".
       */
      if (driving !== null) { fireShell(driving); return; }
      if (strikeSomething()) {
        // The blow is started whether or not it lands on anything. A swing
        // at thin air is still a swing, and a hand that only moves when you
        // connect teaches you nothing about your reach.
        strikeLeft = STRIKE_SECONDS;
        // And counted, because that is what travels: a number that goes up
        // says a blow started, once. See `blows` in the protocol.
        blows++;
      } else {
        // The swing for a dug block is counted by `dig`, on the beat it
        // lands: counting one here as well would send two swings for one
        // press and the arm would judder on the first frame of every hole.
        mining = true;
        refuseIfWrongTool();
      }
    } else if (action === "breakEnd") {
      mining = false;
    } else if (action === "place") {
      useHeld();
    } else if (action === "dropItem") {
      throwHeld(false);
    } else if (action === "dropStack") {
      throwHeld(true);
    } else if (action === "toggleInventory") {
      toggleInventory();
    } else if (action === "toggleSkins") {
      toggleSkins();
    } else if (action === "toggleRecipes") {
      toggleRecipeBook();
    } else if (action === "toggleCamera") {
      const i = CAMERA_MODES.indexOf(renderer.cameraMode);
      renderer.cameraMode = CAMERA_MODES[(i + 1) % CAMERA_MODES.length]!;
    } else if (action === "save") {
      void saveGame("manual");
    } else if (action === "exportSave") {
      exportSave();
    } else if (action === "importSave") {
      void importSave();
    } else if (action === "escape") {
      // Escape always leaves, rather than peeling off one layer at a time.
      if (ui.open) ui.close();
      else if (skins.open) skins.close();
    }
  };

  /** What the selected hotbar slot is holding, for the HUD. */
  function describeHeld(): string {
    const held = inventory.get(ui.selectedSlot);
    if (held === null) return `— [${ui.selectedSlot + 1}]`;
    return `${itemLabel(held.id)} ×${held.count}  [${ui.selectedSlot + 1}]`;
  }

  // ---- crops -------------------------------------------------------------
  let growthTimer = 0;

  // ---- loop --------------------------------------------------------------
  let last = performance.now();
  let accumulator = 0;
  let frames = 0;
  let fpsTimer = 0;
  let fps = 0;

  function frame(now: number): void {
    requestAnimationFrame(frame);

    const elapsed = Math.min((now - last) / 1000, 0.25);
    last = now;

    /*
     * Paused: the world stands still, the screen does not.
     *
     * Whenever the pause screen is up - which is whenever the game does not
     * have the pointer and no panel is open - nothing in the world moves. The
     * hour stops, the fires stop, the raid clock stops and the orcs stop where
     * they are. Everything still *draws*, so you can look at where you were
     * while you decide.
     *
     * Tied to the screen rather than to a key of its own, because the screen
     * already exists and already means "not playing right now". Opening the
     * bag is deliberately not a pause: rummaging while a тур walks up to you
     * is a decision, and one this game should let a player make badly.
     */
    // An ungrown world is paused by definition: there is no ground to stand
    // on, and letting the player fall through it into the void while they are
    // still reading the map picker would be a strange way to begin.
    const paused = !worldRunning();
    // Nobody arrives in the air where flying is not allowed - not from a
    // save, not from a key pressed on another map.
    if (player.mode === "fly" && !mayFly()) player.mode = "walk";
    // Dropped rather than saved: coming back from a pause must not run the
    // world forward through everything that would have happened.
    if (paused) accumulator = 0;
    else accumulator += elapsed;

    // Mouse look is applied once per frame, not per tick - it is already a
    // delta, so ticking it would multiply the sensitivity by the tick count.
    const [dx, dy] = input.consumeLook();
    // The mouse turns whatever you are looking through.
    if (dx !== 0 || dy !== 0) (atControls ? drone : player).look(dx, dy);

    /*
     * Digging, once a frame rather than once a tick.
     *
     * It is paced in real seconds and it is about a button being held, not
     * about the world: a tab that comes back after a minute away should not
     * find that it has quietly dug through a hill. `elapsed` is already
     * capped at a quarter second for exactly that reason.
     */
    if (paused) { mining = false; digging.stop(); } else dig(elapsed);

    let ticks = 0;
    while (!paused && accumulator >= TICK_DT && ticks < MAX_TICKS_PER_FRAME) {
      // Running water carries you. Applied before the step rather than after,
      // so the current is something you move through rather than something
      // that teleports you once the move is done.
      const current = world.flowAt(
        Math.floor(player.position.x),
        Math.floor(player.position.y),
        Math.floor(player.position.z),
      );
      player.velocity.x += current.x * TICK_DT;
      player.velocity.z += current.z * TICK_DT;

      /*
       * Your body does not move while your eyes are elsewhere.
       *
       * It is still stepped - it still falls, still stands in water, still
       * gets hit - it simply takes no orders. That is the whole price of the
       * drone: you are standing in a field, and the field carries on.
       */
      /*
       * And nobody moves while everybody is still arriving.
       *
       * Taken away at the orders rather than at the body, the same as the
       * drone: gravity still applies, water still holds you up, a вибухоблок
       * still throws you. What is taken away is the walking.
       */
      const listening = (input.locked || input.touching) && !atControls &&
        !frozen() && overScreen.hidden;
      if (driving !== null) {
        /*
         * In a танк, the tank is where you are.
         *
         * The same arrangement as a човен and written for the same reason -
         * see core/entity/tank.ts. The keys pull the levers, the machine
         * decides where that leaves it, and the driver is put there. No
         * walking physics runs at all, which is what stops the two of them
         * arguing about one position.
         */
        const levers: Levers = listening ? {
          forward: input.state.forward, back: input.state.back,
          left: input.state.left, right: input.state.right,
        } : { forward: false, back: false, left: false, right: false };
        const wasYaw = driving.yaw;
        const moved = driveTank(driving, levers, TICK_DT, {
          floorAt: (x, z) => {
            const ground = world.groundAt(Math.floor(x), Math.floor(z));
            return ground < 0 ? null : ground + 1;
          },
        });
        if (moved) {
          trackNoise += TICK_DT;
          if (trackNoise > 0.35) {
            trackNoise = 0;
            audio.play("breakStone", { rate: 0.35, volume: 0.2 });
          }
        }
        player.position.x = driving.x;
        player.position.y = driving.y + TANK_FLOOR;
        player.position.z = driving.z;
        player.velocity.x = 0;
        player.velocity.y = 0;
        player.velocity.z = 0;
        player.inWater = false;
        player.submerged = false;
        // Only the heading goes on the wire: where the tank is arrives with
        // the driver's own `move`, exactly as a boat's does.
        if (net.online && Math.abs(driving.yaw - wasYaw) > 0.002) {
          net.steer(driving.yaw);
        }
        // Sneak is how you get out, and there has to be ground to get out
        // onto - the same rule a boat has about the bank.
        if (listening && input.state.sneak) {
          const off = stepAshore({ ...driving, way: 0, rider: null }, (x, z) =>
            footroom({
              groundAt: (a, b) => world.groundAt(a, b),
              blockAt: (a, b, c) => world.getBlock(a, b, c),
              fluidAt: (a, b, c) => isFluidAt(a, b, c),
              inside: (a, b) => world.withinBounds(a, 1, b),
            }, { x, z }, BlockId.Air));
          if (off !== null) {
            if (net.online) net.dismount();
            driving.rider = null;
            driving = null;
            player.position.x = off.x;
            player.position.y = off.y;
            player.position.z = off.z;
          }
        }
      } else if (aboard !== null) {
        /*
         * In a boat, the boat is where you are.
         *
         * The walking physics is not run at all - see core/entity/boat.ts.
         * What the keys do instead is pull on the oars, and the player is
         * put wherever that leaves the hull. Gravity, stepping up and
         * swimming are all beside the point on open water, and running any
         * of them alongside this would be two things arguing about one
         * position.
         */
        const oars: Oars = listening ? {
          forward: input.state.forward, back: input.state.back,
          left: input.state.left, right: input.state.right,
        } : { forward: false, back: false, left: false, right: false };
        const heldYaw = aboard.yaw;
        if (rowBoat(aboard, oars, TICK_DT, water)) {
          rowSound += TICK_DT;
          if (rowSound > 0.75) {
            rowSound = 0;
            audio.play("breakGrass", { rate: 0.5, volume: 0.25 });
          }
        }
        player.position.x = aboard.x;
        player.position.y = aboard.y;
        player.position.z = aboard.z;
        player.velocity.x = 0;
        player.velocity.y = 0;
        player.velocity.z = 0;
        /*
         * And not swimming, whatever we were doing a moment ago.
         *
         * These are set by `player.update`, which is exactly what is not
         * being run - so somebody who swam out to a boat and climbed in
         * would still be marked as being in the water, with the swimming
         * speed and the underwater tint to go with it, for as long as they
         * stayed aboard.
         */
        player.inWater = false;
        player.submerged = false;
        /*
         * And the hull's heading, when it has changed.
         *
         * Only the heading: where the boat is arrives with the ordinary
         * `move`, because while somebody is in a boat the boat is where they
         * are. Sending it every tick would be the same number fifteen times
         * a second for a boat sitting still.
         */
        if (net.online && Math.abs(aboard.yaw - heldYaw) > 0.002) {
          net.row(aboard.yaw);
        }
        // Sneak is how you get out of it, and it needs somewhere to get out
        // onto - stepping into the lake is not getting out of a boat.
        if (listening && input.state.sneak) {
          const ashore = stepAshore(aboard, (x, z) =>
            footroom({
              groundAt: (a, b) => world.groundAt(a, b),
              blockAt: (a, b, c) => world.getBlock(a, b, c),
              fluidAt: (a, b, c) => isFluidAt(a, b, c),
              inside: (a, b) => world.withinBounds(a, 1, b),
            }, { x, z }, BlockId.Air));
          if (ashore === null) {
            notify(say("no-bank", tone.tone, lang.value), 1600);
          } else {
            if (net.online) net.ashore();
            aboard.rider = null;
            aboard = null;
            player.position.x = ashore.x;
            player.position.y = ashore.y;
            player.position.z = ashore.z;
          }
        }
      } else {
        player.update(
          listening ? input.state : EMPTY_INPUT,
          TICK_DT, isSolidAt, isWadeAt, fillAt,
        );
      }
      if (player.position.y < VOID_Y) respawn();

      // The raids. Counted in real seconds off the fixed tick, so a paused
      // day or a sped-up sky does not move the half hour.
      const standing = world.standingLairs().length;
      if (standing < lairsLeft) {
        lairsLeft = standing;
        audio.play("breakStone");
        notify(say(standing === 0 ? "raids-over" : "lair-broken", tone.tone, lang.value), 3000);
      }
      if (!net.online) peaceLeft = Math.max(0, peaceLeft - TICK_DT);
      switch (net.online ? "none" : raid.step(TICK_DT, standing)) {
        case "warning":
          audio.play("breakWood", { rate: 0.4 });
          notify(say("raid-warning", tone.tone, lang.value), 4000);
          break;
        case "raid": {
          // Trimmed to what the island will hold. See MAX_ORCS.
          landRaid(raid.waveSize(standing, orcs.length, orcCap));
          audio.play("breakStone", { rate: 0.5 });
          notify(say("raid-begins", tone.tone, lang.value), 4000);
          break;
        }
        case "none":
          break;
      }

      /*
       * The herd and the карасі, when they are ours to walk.
       *
       * Online they are the server's, the same as the orcs - everybody had
       * their own fourteen pigs in their own places, and a тур one player
       * shot went on grazing on the other screen. Nothing either of them does
       * can hurt anybody, which is why this waited; it is still two people
       * looking at one field and seeing different animals in it.
       */
      if (!net.online) {
        /*
         * Who the dogs are walking beside, and what is on them.
         *
         * One person, alone; the nearest орк within a few blocks is what
         * they are taken to be fighting. No memory of who started it - see
         * GUARD_REACH.
         */
        let foe: { id: number; x: number; y: number; z: number } | null = null;
        let nearestFoe = GUARD_REACH;
        for (const orc of orcs) {
          const away = Math.hypot(orc.x - player.position.x, orc.z - player.position.z);
          if (away < nearestFoe) {
            nearestFoe = away;
            foe = { id: orc.id, x: orc.x, y: orc.y, z: orc.z };
          }
        }
        const herd = stepBeasts(
          beasts, TICK_DT, isSolidAt, isFluidAt, Math.random,
          [{
            id: ALONE, x: player.position.x, y: player.position.y,
            z: player.position.z, foe,
          }],
        );
        for (const bite of herd.bites) {
          const bitten = orcs.find((o) => o.id === bite.foe);
          if (bitten === undefined) continue;
          if (bitten.vitals.hurt(bite.damage) > 0) {
            audio.play("breakWood", { rate: 1.5, volume: 0.5 });
          }
        }
        for (const fallen of herd.fallen) {
          drop(rollBeastDrop(fallen), {
            x: Math.floor(fallen.x), y: Math.floor(fallen.y), z: Math.floor(fallen.z),
          });
          audio.play("breakGrass", { rate: 0.5 });
        }
        for (const caught of stepFish(fish, TICK_DT, isFluidAt)) {
          drop([{ item: KARAS_DROP, count: 1 }], {
            x: Math.floor(caught.x), y: Math.floor(caught.y), z: Math.floor(caught.z),
          });
        }
      }

      // The line, if one is out. Ticked on the fixed step so the wait is the
      // same length whatever the frame rate is doing.
      switch (line.step(TICK_DT)) {
        case "bite":
          audio.play("place", { rate: 1.6 });
          notify(say("bite", tone.tone, lang.value), 1500);
          break;
        case "lost":
          notify(say("fish-lost", tone.tone, lang.value), 1500);
          break;
        case "none":
          break;
      }

      // The orcs come for whoever is alive. Their blows go through the same
      // grace period as everything else, so two of them cannot both land in
      // the same instant.
      /*
       * The lairs keep sending, five minutes apart.
       *
       * Between raids rather than instead of them: a fortress left alone
       * fills back up to a garrison and stops, and a room you cleared stays
       * clear for a few minutes rather than forever. The ceiling is counted
       * per lair, which is what makes breaking one worth the trouble.
       */
      if (!net.online) {
        for (const sent of lairClock.step(TICK_DT, world.standingLairs(),
          (lair: { x: number; y: number; z: number }) => orcs.filter((o) =>
            o.from !== undefined &&
            o.from.x === lair.x && o.from.y === lair.y && o.from.z === lair.z).length)) {
          if (orcs.length >= orcCap) break;
          orcs.push(makeOrc(
            world.findSpawn(sent.at.x + 2, sent.at.z + 2),
            orcs.length % 3 === 2 ? "archer" : "sabre",
            sent.at,
          ));
        }
      }

      /*
       * The orcs, and who is running them.
       *
       * Alone, this machine does: the clocks above, the pathing here, the
       * blows below. On somebody else's world none of that happens here at
       * all - the raid is the server's, and this reads its snapshot instead.
       * That was the largest thing on the list of what was not shared and the
       * most visible: two people in one world fought two different wars, one
       * standing in the middle of a wave while the other saw an empty field.
       */
      const turn = net.online
        ? { blows: [], shots: [], hurls: [], fallen: [] }
        : stepOrcs(orcs, TICK_DT, isSolidAt, huntable([{
          id: 0, ...player.position, flying: player.mode === "fly",
        }]), marchOn());
      for (const blow of turn.blows) {
        // Armour stands between the axe and the body. Falls are not reduced:
        // a кольчуга does nothing about the ground, and pretending otherwise
        // would turn armour into a licence to jump off things.
        if (health.hurt(damageAfterArmour(blow.damage, armourPoints())) <= 0) continue;
        audio.play("breakWood", { rate: 0.6 });
        notify(say("orc-hit", tone.tone, lang.value), 1200);
      }
      /*
       * The archers' arrows fly in their own list.
       *
       * Two flights rather than one with an owner on each arrow: an orc's
       * arrow must never hit an orc and the player's must never hit the
       * player, and keeping them apart is one array instead of a rule inside
       * the flight code that somebody has to remember.
       */
      for (const shot of turn.shots) {
        orcArrows.push(makeArrow(shot.from, shot.dir));
        audio.play("breakGrass", { rate: 1.4, volume: 0.5 });
      }
      /*
       * And the cold fire, which is his and nobody else's.
       *
       * Into the same list of firebolts the требушет uses, flagged cold: one
       * flight model, one set of impacts, and the flag is what says whether
       * what lands sets anything alight.
       */
      for (const hurl of turn.hurls) {
        firebolts.push(makeFirebolt(hurl.from, hurl.dir, COLD_SPEED, true));
        audio.play("breakGlass", { rate: 0.7, volume: 0.6 });
      }
      /*
       * The bolts, and what they set alight.
       *
       * A bolt is fierce: it takes anything that is not untouched ground,
       * which is what lets a fortress of brick burn at all. What happens
       * after that is the fire's business.
       */
      /*
       * And the Змії, who breathe rather than swing.
       *
       * Their breath goes into the same list the требушет's shots do, hot:
       * "запали один блок в який попадає вогнем" is what a hot bolt already
       * does, and a second kind of fire would be a second thing to get
       * right.
       */
      for (const breath of stepDragons(dragons, [player.position], TICK_DT)) {
        const dx = breath.at.x - breath.from.x;
        const dy = breath.at.y - breath.from.y;
        const dz = breath.at.z - breath.from.z;
        const len = Math.hypot(dx, dy, dz) || 1;
        firebolts.push(makeFirebolt(breath.from,
          { x: dx / len, y: dy / len, z: dz / len }, BOLT_SPEED, false, true));
        audio.play("breakGrass", { rate: 0.5, volume: 0.9 });
      }

      /*
       * Снаряди in the air.
       *
       * Flown on this page as well as on the server: a shell is the one
       * thing in the game that has to look right on the frame it is fired,
       * and a round trip before the gun does anything would make aiming
       * impossible. What the server says about what it hit is what counts,
       * and it arrives with the next snapshot.
       */
      for (const hit of stepShells(shells, TICK_DT, isSolidAt, tanks)) {
        if (hit.tank !== null) {
          /*
           * "Танк можна розбити тільки пострілами з іншого танку за 5
           * попадань." Counted here so the hull darkens on the frame it is
           * struck; online, the server keeps the count that decides.
           */
          if (shellStruck(hit.tank)) {
            const at = { x: hit.tank.x, y: hit.tank.y, z: hit.tank.z };
            const gone = tanks.indexOf(hit.tank);
            if (gone >= 0) tanks.splice(gone, 1);
            tankBlast(at);
          } else {
            audio.play("breakStone", { rate: 0.7, volume: 0.8 });
          }
          continue;
        }
        // The ground: a bang and nothing else. A shell that dug craters
        // would be a shovel that fires, and the wall between two armies is
        // the thing this map is about.
        audio.play("breakStone", { rate: 0.5, volume: 0.7 });
      }

      for (const impact of stepFirebolts(firebolts, TICK_DT, isSolidAt)) {
        /*
         * Cold fire burns whoever it catches and leaves the timber standing.
         *
         * Which is why his own замок is still there after a fight in it, and
         * why this is a weapon rather than a siege: the трeбушет's bolt is a
         * stone in burning смола and what it is *for* is the fire it starts.
         */
        if (impact.bolt.cold) {
          const near = Math.hypot(
            impact.x + 0.5 - player.position.x,
            impact.y + 0.5 - player.position.y,
            impact.z + 0.5 - player.position.z);
          if (near < 2.5) health.hurt(COLD_DAMAGE);
          audio.play("breakGlass", { rate: 1.4, volume: 0.7 });
          continue;
        }
        /*
         * And it burns whoever it lands on, which it never used to.
         *
         * A Змій's breath is a hot bolt, and "наносить шкоди на 3 серця коли
         * без броні" is six points - which is what `BOLT_DAMAGE` has always
         * been. The требушет's own shots get the same treatment, because a
         * burning stone landing at your feet not hurting you was never right
         * either; it was simply never a thing anybody stood next to.
         */
        const near = Math.hypot(
          impact.x + 0.5 - player.position.x,
          impact.y + 0.5 - player.position.y,
          impact.z + 0.5 - player.position.z);
        if (near < 2.5) health.hurt(BREATH_DAMAGE);
        const struck = world.getBlock(impact.x, impact.y, impact.z);
        if (fire.light(impact.x, impact.y, impact.z, struck, true)) {
          audio.play("breakGrass", { rate: 0.6 });
        }
      }
      // Burning, spreading, and eating what it stands on.
      const burnt = fire.step(TICK_DT, (x, y, z) => world.getBlock(x, y, z));
      /*
       * What the fire eats is the server's to say, when there is one.
       *
       * The flames are still drawn from this blaze - they have to be, or a
       * fire would be invisible until something fell out of it - but the
       * blocks it takes come back as ordinary edits, the same as anybody
       * else's digging. Two clients each burning their own hole in the same
       * fortress was the one way this world could look actively wrong rather
       * than merely incomplete.
       */
      if (!net.online) {
        for (const cell of burnt.consumed) {
          world.setBlock(cell.x, cell.y, cell.z, BlockId.Air);
        }
      }
      if (burnt.caught.length > 0) audio.play("breakGrass", { rate: 1.8, volume: 0.3 });

      /*
       * And it burns whoever is standing in it.
       *
       * "Вогонь повинен вбивати та запалювати людей тварин." Everything that
       * walks is asked the same question of its own box, and whoever is in
       * the flames catches - and keeps burning for a few seconds after they
       * run out of them, which is what makes a burning house something to
       * flee rather than something to walk through.
       *
       * Water puts it out, so a свиня alight can save itself in the river
       * and so can you.
       */
      const alight = (x: number, y: number, z: number, w: number, h: number) =>
        fire.touching(x, y, z, w, h);
      if (alight(player.position.x, player.position.y, player.position.z,
        PLAYER_WIDTH, PLAYER_HEIGHT)) burning.light("player");
      for (const orc of orcs) {
        if (alight(orc.x, orc.y, orc.z, ORC_WIDTH, ORC_HEIGHT)) {
          burning.light(`orc:${orc.id}`);
        }
      }
      for (const animal of beasts) {
        if (alight(animal.x, animal.y, animal.z,
          animal.breed.width, animal.breed.height)) {
          burning.light(`beast:${animal.id}`);
        }
      }
      // A ducking is a ducking: waist-deep is enough for anybody.
      if (isFluidAt(Math.floor(player.position.x),
        Math.floor(player.position.y + 1), Math.floor(player.position.z))) {
        burning.out("player");
      }
      for (const orc of orcs) {
        if (isFluidAt(Math.floor(orc.x), Math.floor(orc.y), Math.floor(orc.z))) {
          burning.out(`orc:${orc.id}`);
        }
      }
      for (const animal of beasts) {
        if (isFluidAt(Math.floor(animal.x), Math.floor(animal.y),
          Math.floor(animal.z))) burning.out(`beast:${animal.id}`);
      }

      /*
       * The bite itself.
       *
       * No armour in it: a кольчуга is no help against being on fire, and a
       * шкіряний куртка is part of the problem. Whoever it kills is taken
       * away by their own `step` on the next tick - which is also what drops
       * what they were carrying, so a roast свиня leaves сало.
       */
      for (const who of burning.step(TICK_DT)) {
        if (who === "player") {
          if (health.hurt(SCORCH_DAMAGE) > 0) {
            notify(say("burnt", tone.tone, lang.value), 1200);
            audio.play("breakGrass", { rate: 1.9, volume: 0.4 });
          }
          continue;
        }
        if (who.startsWith("orc:")) {
          const id = Number(who.slice(4));
          orcs.find((o) => o.id === id)?.vitals.hurt(SCORCH_DAMAGE);
          continue;
        }
        const id = Number(who.slice(6));
        const animal = beasts.find((b) => b.id === id);
        if (animal !== undefined && animal.vitals.hurt(SCORCH_DAMAGE) > 0) {
          audio.play("breakGrass", { rate: 0.7, volume: 0.4 });
        }
      }

      // What they were carrying stays where they fell.
      for (const dead of turn.fallen) {
        drop(rollOrcDrops(dead.breed), {
          x: Math.floor(dead.x), y: Math.floor(dead.y), z: Math.floor(dead.z),
        });
      }

      health.step(TICK_DT);
      /*
       * Лава burns.
       *
       * Asked of the feet and of the chest, so that wading through a shallow
       * pool hurts as much as swimming a deep one - there is no such thing
       * as standing safely in лава up to the knee. The grace period does the
       * rest of the work: a blow is offered every tick and lands twice a
       * second, which is the difference between a mistake and an execution.
       */
      if (isLava(world.getBlock(
        Math.floor(player.position.x),
        Math.floor(player.position.y),
        Math.floor(player.position.z),
      )) || isLava(world.getBlock(
        Math.floor(player.position.x),
        Math.floor(player.position.y + 1),
        Math.floor(player.position.z),
      ))) {
        if (health.hurt(damageAfterArmour(LAVA_DAMAGE, armourPoints())) > 0) {
          notify(say("burnt", tone.tone, lang.value), 1600);
          audio.play("breakStone", { rate: 1.6, volume: 0.5 });
        }
      }
      // The landing speed is set for exactly one tick, so this reads each
      // arrival once without having to remember anything itself.
      if (player.landingSpeed > 0) {
        const hurt = health.hurt(fallDamage(player.landingSpeed));
        if (hurt > 0) {
          notify(say("fell-hard", tone.tone, lang.value), 1600);
          audio.play("breakStone", { rate: 0.7 });
        }
      }
      if (health.dead) {
        /*
         * And, in a match, who did it.
         *
         * A player's сила is this page's - see the protocol - so the server
         * has no way of knowing anybody has died unless it is told, and the
         * table of kills and deaths is the whole end of «Сучасна війна».
         */
        if (warState !== null && net.online) net.died(struckBy);
        struckBy = 0;
        die();
      }
      cycle.update(TICK_DT);

      // Every fire, every tick, watched or not: a furnace you have to stand
      // over is a furnace nobody uses.
      //
      // A stove that catches or goes out becomes a different block - that is
      // the only thing the mesher can see, so the fire in its mouth has to be
      // in the block id. Turned rather than replaced, so it keeps its facing,
      // its finish and everything in its slots.
      /*
       * And the water, which now takes time to get where it is going.
       *
       * Two blocks a second, and лава one. On the fixed tick rather than the
       * frame, because how far a fluid has run is world state and not a
       * matter of how fast this machine draws.
       */
      world.stepFluids(TICK_DT);

      for (const change of world.furnaces.step(TICK_DT)) {
        const here = world.getBlock(change.x, change.y, change.z);
        const want = withStoveLit(here, change.lit);
        if (want !== here) edit(change.x, change.y, change.z, want);
      }

      growthTimer += TICK_DT;
      if (growthTimer >= GROWTH_PASS_SECONDS) {
        growthTimer = 0;
        growCrops(world);
        // Паростки run on the same clock but far slower - see SAPLING_CHANCE.
        growSaplings(world);
        // And bare earth under the open sky greens over, which is the other
        // half of a spade of turf being earth in your hands.
        spreadGrass(world);
      }

      accumulator -= TICK_DT;
      ticks++;
    }
    if (ticks === MAX_TICKS_PER_FRAME) accumulator = 0; // give up on the backlog

    // What the player can craft depends on what they are standing near. Only
    // while a panel is open - nothing else looks at it.
    if (ui.open) {
      const p = player.position;
      ui.setStations(stationsNear(world, p.x, p.y, p.z));
      // The window is looking at a live object, so it has to be repainted as
      // the fire burns down rather than only when something is clicked.
      ui.refreshStove();
    }

    // Villagers, arrows and dropped things run on wall-clock seconds rather
    // than on the fixed tick, so stopping the tick alone would leave an arrow
    // in flight while the world it is flying through stood still.
    if (!paused) {
      if (!net.online) {
        /*
         * A day in the village.
         *
         * "Нехай селяни вночі сплять а вдень працюють, вирощують пшеницю."
         * Night sends everybody to bed and morning sends the grown-ups out to
         * the plot; the children play in the lane either way.
         *
         * Doors are not solid to them. A villager who could not open a door
         * was a villager who lived his whole life in one room - and having
         * them work the latch properly is the wrong fix, because the latch is
         * the player's verb. They walk through, and the door swings as they
         * pass, which is what it looks like from outside.
         */
        const openToFolk = (x: number, y: number, z: number): boolean => {
          const id = world.getBlock(x, y, z);
          if (isDoor(id) || isGate(id)) return false;
          return isSolid(id);
        };
        const turn = stepVillagers(villagers, elapsed, openToFolk, isFluidAt,
          cycle.phase === "night" || cycle.phase === "dusk");
        for (const v of villagers) {
          // The door they are standing in, swung open behind them.
          const at = world.getBlock(
            Math.floor(v.x), Math.floor(v.y), Math.floor(v.z));
          if ((isDoor(at) && !doorState(at).open) || isGate(at)) {
            togglePanel(world, Math.floor(v.x), Math.floor(v.y), Math.floor(v.z));
          }
        }
        /*
         * And what a day's work comes to.
         *
         * One crop, one stage, somewhere in the plot they are standing over.
         * The garden grows on its own as well - everything sown does - and
         * this is what makes a village's fields visibly better kept than a
         * patch of the same seeds left alone.
         */
        for (const worker of turn.tended) {
          const at = tendable(worker);
          if (at === null) continue;
          const next = nextCropStage(world.getBlock(at.x, at.y, at.z));
          if (next !== 0) world.setBlock(at.x, at.y, at.z, next);
        }
      } else {
        tickVillagers(villagers, elapsed);
      }
      if (stepPickups(pickups, elapsed, isSolidAt, player.position, inventory) > 0) {
        ui.refresh();
        recipes.refresh();
      }
      // Cheap, and it keeps a mined seam from becoming a hundred entities.
      mergePickups(pickups);
      // An arrow in somebody's ribs gets the same answer as a fist: he turns
      // round and says his piece.
      // Arrows hit anything alive. A villager has something to say about it; an
      // orc just takes it.
      /*
       * The orcs are not in this list when online.
       *
       * The server flew its own copy of this arrow and has already said what
       * it hit; hurting an orc here as well would be a second opinion about a
       * number that has one owner - and the mirror's hit points are not the
       * ones that decide whether it falls.
       */
      for (const hit of stepArrows(
        arrows, elapsed, isSolidAt,
        // Nothing alive is ours online: the server flew its own copy of this
        // arrow at all of them and has already said what it hit.
        net.online ? [] : [...villagers, ...orcs, ...beasts],
      )) {
        if (hit.target.vitals.hurt(ARROW_DAMAGE) <= 0) continue;
        // An arrow out of the dark sends it away from the archer, not from the
        // arrow: what it runs from is whoever it can work out is there.
        const shot = beasts.find((b) => b === hit.target);
        if (shot !== undefined) spook(shot, player.position.x, player.position.z);
        const villager = villagers.find((v) => v === hit.target);
        if (villager !== undefined) {
          faceToward(villager, player.position.x, player.position.z);
          villager.speaking = SPEECH_SECONDS;
          sayAloud(villager.line);
        }
      }
      /*
       * And theirs, against one target only.
       *
       * The player's box, standing where the player stands. Armour counts, as
       * it does for a sabre: an arrow that ignored a кольчуга would make the
       * archers the reason never to wear one.
       */
      const shotAt = {
        x: player.position.x, y: player.position.y, z: player.position.z,
        vitals: health,
      };
      // Nobody to hit, online: the server flew these and said `hurt` if one
      // of them landed. These are here to be seen.
      for (const hit of stepArrows(orcArrows, elapsed, isSolidAt,
        net.online ? [] : [shotAt])) {
        void hit;
        if (health.hurt(damageAfterArmour(ARROW_DAMAGE, armourPoints())) <= 0) {
          continue;
        }
        audio.play("breakWood", { rate: 0.6 });
        notify(say("orc-hit", tone.tone, lang.value), 1200);
      }
    }

    /*
     * Flying, on wall-clock seconds.
     *
     * Not on the fixed tick: the battery is five real minutes, and a paused
     * world must not stop it - putting the drone up and walking away would
     * otherwise be free reconnaissance for as long as you liked.
     */
    if (droneAloft) {
      // Nobody at the controls is nobody at the controls: it holds its
      // station and spends its battery just the same.
      const flightInput: DroneInput = atControls && input.locked ? {
        forward: input.state.forward, back: input.state.back,
        left: input.state.left, right: input.state.right,
        up: input.state.jump, down: input.state.sneak || input.state.sprint,
      } : NO_DRONE_INPUT;
      drone.step(flightInput, elapsed, isSolidAt, droneBounds);
      if (!drone.flying) landDrone("flat");
    }
    /*
     * Your body is drawn while you are not in it.
     *
     * Looking down and finding yourself standing in a field is half of what
     * makes this a decision. The drone itself is not drawn: the camera is
     * inside it, and the first attempt filled the screen with the inside of
     * its own shell. Other people do see it - it rides on this body's own
     * snapshot - which is why it is only *this* screen that leaves it out.
     */
    renderer.showOwnBody = atControls;
    // And the machine itself, on the frames when you are not inside it.
    renderer.syncOwnDrone(droneAloft && !atControls
      ? { x: drone.x, y: drone.y, z: drone.z, yaw: drone.yaw }
      : null);
    /*
     * Through the drone there is only the one view.
     *
     * The first/third-person switch is about your own body; pulling the
     * camera back from a drone would put it behind a machine nobody draws.
     * The player's choice is remembered and handed back when they land.
     */
    const ownView = renderer.cameraMode;
    if (atControls) renderer.cameraMode = "first";
    renderer.syncCamera(atControls ? drone : player, isSolidAt, elapsed);
    renderer.cameraMode = ownView;
    /*
     * What is in the hand, and how far through a blow it is.
     *
     * Said every frame rather than when the belt changes: the selected slot
     * moves for a dozen reasons - the wheel, the number keys, the bag, a
     * stack running out under a pick - and one of them would eventually not
     * tell the renderer.
     */
    renderer.setHeld(inventory.get(ui.selectedSlot)?.id ?? 0, tone.tone);
    // And the shield, off its own slot. Costs nothing on the frames where
    // it has not changed, like the held thing.
    renderer.setShield(worn.get(ARMOUR_SLOTS.indexOf("shield"))?.id ?? 0, tone.tone);
    strikeLeft = Math.max(0, strikeLeft - elapsed);
    // Said on the page as well as drawn, so what is outside the canvas - a
    // hint, a check - can tell a crouch from a walk without a hook into her.
    if (document.body.dataset.crouch !== (player.crouching ? "1" : "")) {
      document.body.dataset.crouch = player.crouching ? "1" : "";
    }
    renderer.syncPlayerModel(
      player, elapsed, strikeProgress(strikeLeft), health.glow,
    );
    net.move(player.position, player.yaw, player.pitch,
      droneAloft
        ? { x: drone.x, y: drone.y, z: drone.z, yaw: drone.yaw }
        : undefined,
      // Their hand travels with their body: it changes with it, and one that
      // arrived on a different beat would be holding last frame's axe.
      inventory.get(ui.selectedSlot)?.id ?? 0, blows,
      player.mode === "fly");

    /*
     * The book's nudge, every frame and whoever you are playing with.
     *
     * Unconditional, unlike the bag below it: somebody playing alone is the
     * one who most needs telling that the book has a page for the thing they
     * have just dug up.
     */
    watchTheBag();

    /*
     * And the whole bag, when it has changed.
     *
     * Compared by a cheap running sum rather than by remembering the last
     * one: a bag is forty slots and this runs every frame, and the point is
     * only to notice that something moved. The connection does the rest of
     * the rationing - see `bag` there.
     */
    if (net.online) {
      let mark = 0;
      for (const slot of [...inventory.slots, ...worn.slots]) {
        mark = (mark * 31 + (slot === null ? 0 : slot.id * 977 + slot.count)) | 0;
      }
      if (mark !== bagMark) {
        bagMark = mark;
        net.bag({ slots: [...inventory.slots], worn: [...worn.slots] });
      }
    }
    /*
     * The server's orcs, brought in line before they are drawn.
     *
     * Out here with the other players rather than on the fixed tick, and for
     * the same reason they are: `paused` is a local fiction on somebody
     * else's world. The server goes on ticking whether or not you have the
     * menu open, and a raid that froze while you looked at your bag - then
     * jumped when you closed it - would be a lie told in two directions.
     * Nothing here simulates anything; this is the drawing of what arrived.
     */
    if (net.online) { mirrorOrcs(net.orcs); mirrorHerd(); }
    /*
     * The same for everybody else, drawn a snapshot behind.
     *
     * Done here rather than in the renderer because it is the same
     * arithmetic the orcs use and there should be one of it. What the
     * renderer gets is a list of places, already smoothed.
     */
    const drawnAt = performance.now() / 1000;
    /*
     * Where everybody else is *drawn*, kept for the swing to aim at.
     *
     * The same numbers, not the raw snapshot: what you hit has to be what
     * you can see, and the snapshot is up to a fifth of a second behind the
     * body on the screen.
     */
    seenPlayers.length = 0;
    renderer.syncPlayers(net.players.map((p) => {
      const trail = trailFor(`player:${p.id}`);
      trail.push({ x: p.x, y: p.y, z: p.z, yaw: p.yaw, pitch: p.pitch },
        net.arrivedAt);
      const where = trail.at(drawnAt);
      seenPlayers.push({ id: p.id, x: where.x, y: where.y, z: where.z });
      return { ...p, ...where, speed: trail.speed };
    }), elapsed);
    // Bodies nobody has described for a while are gone; so are their trails.
    if (trails.size > 200) {
      const live = new Set([
        ...net.players.map((p) => `player:${p.id}`),
        ...net.orcs.map((o) => `orc:${o.id}`),
      ]);
      for (const key of trails.keys()) if (!live.has(key)) trails.delete(key);
    }
    renderer.syncVillagers(villagers);
    renderer.syncOrcs(orcs, elapsed);
    renderer.syncBeasts(beasts);
    renderer.syncDragons(dragons);
    renderer.syncFish(fish);
    renderer.syncBoats(boats);
    renderer.syncTanks(tanks);
    renderer.syncArrows([...arrows, ...orcArrows]);
    renderer.syncFirebolts(firebolts);
    renderer.syncFire(fire.burning());
    /*
     * And the flames on whoever is alight.
     *
     * Built here rather than kept as a list, because the bodies move every
     * frame and the book only knows their names. The player is left out on
     * purpose: you never see yourself, and a burning cube around the camera
     * is an orange screen.
     */
    const lit: {
      key: string; x: number; y: number; z: number;
      width: number; height: number;
    }[] = [];
    for (const orc of orcs) {
      if (burning.isAlight(`orc:${orc.id}`)) {
        lit.push({
          key: `orc:${orc.id}`, x: orc.x, y: orc.y, z: orc.z,
          width: ORC_WIDTH, height: ORC_HEIGHT,
        });
      }
    }
    for (const animal of beasts) {
      if (burning.isAlight(`beast:${animal.id}`)) {
        lit.push({
          key: `beast:${animal.id}`, x: animal.x, y: animal.y, z: animal.z,
          width: animal.breed.width, height: animal.breed.height,
        });
      }
    }
    renderer.syncBodyFire(lit);
    renderer.syncPickups(pickups);
    // Every frame, not only on a hit: сила mends on its own, so a bar that
    // moved only when something struck would sit wrong most of the time.
    vitals.update(
      health.hp, health.max, armourPoints(), reliefPercent(armourPoints()),
    );
    /*
     * The country ahead, grown as it is walked into.
     *
     * Only the adventure does this: the кремль and the hill are made whole
     * the moment they are asked for, and both are small enough that it costs
     * a third of a second. An adventure has no such moment - a column is
     * grown when somebody comes near it, a couple per frame, so walking into
     * new country costs a frame here and there instead of a freeze.
     */
    /*
     * «Один блок» has a Змій too, and the once-a-second block below is the
     * adventure's alone - so he has a clock of his own here. The first
     * version of the island put him in `restockDragons` and forgot that
     * nothing on this map ever called it: "ти забув додати самого змія".
     */
    if (world.opts.terrain === "one") {
      dragonClock += elapsed;
      if (dragonClock > 1) {
        dragonClock = 0;
        restockDragons();
      }
    }
    if (world.opts.terrain === "adventure") {
      world.ensureAround(player.position.x, player.position.z, GROW_REACH);
      /*
       * And let go of what is far behind.
       *
       * Not every frame: walking the length of the map would otherwise hold
       * every column ever passed, and sweeping the list is work in itself.
       * Once a second is often enough for something measured in minutes of
       * walking.
       */
      forgetClock += elapsed;
      if (forgetClock > 1) {
        forgetClock = 0;
        renderer.dropChunks(world.forgetBeyond(
          [{ x: player.position.x, z: player.position.z }], FORGET_REACH));
        // And the life moves with the country: on the same clock, because
        // what has just been thrown away is exactly what nothing should be
        // left standing in.
        restockLife();
        restockDragons();
        // And the villages: who lives in a хата is worked out from the хата,
        // so the people come back exactly as they were left.
        restockFolk();
      }
    }
    /*
     * Where the round is, where somebody climbing can see it.
     *
     * Drawn every frame rather than stepped with the world: the clock stops
     * while the pause screen is up and so does everything in here, but the
     * banner still has to say what it last said rather than vanish. It was a
     * line in the readout, which is the corner nobody looks at while they
     * are digging at the block under somebody's feet.
     */
    /*
     * The round runs whether or not anybody is playing.
     *
     * It is the match clock, not part of the world: the minute everybody has
     * to gather in is a minute of real time, and a player still on the menu
     * is a player who is late rather than a player who has stopped the
     * match. Online the server's clock is the only one that counts and this
     * is left alone - two machines counting separately are two machines
     * disagreeing about when it ends.
     */
    if (grown && world.opts.terrain === "hill" && !net.online && overScreen.hidden) {
      round.clock += elapsed;
      if (round.clock >= roundFor) {
        round.clock = 0;
        /*
         * Alone, whoever is high enough has won it, and nobody has if they
         * are not. The same rule the server uses - being on the hill at all
         * is what `KING_MIN_Y` is for, and standing on the floor at the
         * spawn is not being on the hill.
         */
        showRoundOver(player.position.y >= KING_MIN_Y ? savedName() : "");
      }
      const now = roundAt(round.clock, gatherFor, roundFor - gatherFor);
      round.phase = now.phase;
      round.left = now.left;
    }

    /*
     * A finished round shows itself out.
     *
     * Counted in real seconds off the frame, the same as the round itself:
     * nothing in the world is moving behind this screen, so a clock that
     * stepped with the world would never reach nought.
     */
    if (!overScreen.hidden) {
      overLeft = Math.max(0, overLeft - elapsed);
      overSoon.textContent =
        `${uiText("round-soon", lang.value)} ${Math.ceil(overLeft)}`;
      if (overLeft === 0) void leaveRound();
    }

    if (grown && world.opts.terrain === "hill") {
      roundBanner.hidden = false;
      roundBanner.classList.toggle("gather", round.phase === "gather");
      roundWhat.textContent = uiText(
        round.phase === "gather" ? "round-gather" : "round-climb", lang.value);
      roundLeft.textContent = clock(Math.ceil(round.left));
    } else {
      roundBanner.hidden = true;
    }

    // Rebuilds only chunks marked dirty - a no-op on frames with no edits.
    // Still synchronous, so a rebuild costs a few ms; workers land in phase 1.
    renderer.syncChunks(world);
    reportWhenMeshed();
    /*
     * The outline, and the cracks in what is being dug.
     *
     * The progress only counts when the cracked block is the one under the
     * crosshair: look away mid-dig and `Digging` has already forgotten it,
     * but the frame that draws the change must not show the old cracks on
     * the new block for even one frame.
     */
    const aimed = input.locked ? aim() : null;
    const worked = digging.cell;
    const into = aimed !== null && worked !== null &&
      worked.x === aimed.x && worked.y === aimed.y && worked.z === aimed.z
      ? digging.progress : 0;
    renderer.setHighlight(aimed, into);
    renderer.updateEnvironment(cycle);
    // Must follow updateEnvironment, which resets the fog and tint it overrides.
    renderer.applyUnderwater(player.submerged);
    // The view widens a little while running, so the speed is something you
    // see and not only something the numbers know about.
    renderer.easeFieldOfView(player.running, elapsed);
    renderer.render();
    // After syncCamera, so the bubble sits over him in this frame's view.
    updateSpeech();
    updateNameTags();
    /*
     * The music stops with the world.
     *
     * Paused means paused: the hour stops, the fires stop, the orcs stop -
     * and a tune playing on over a stopped world is the one thing still
     * insisting the game is happening. Suspended rather than cut, so the
     * track carries on from the bar it was in when escape was pressed.
     */
    if (paused !== musicPaused) {
      musicPaused = paused;
      if (paused) audio.suspend();
      else audio.unlock();
    }
    if (!paused) stepMusic(elapsed);
    // The arm comes back while the button is held, on wall-clock seconds:
    // winding is something the player is doing, not something the world is.
    if (!paused) windTrebuchet(elapsed);

    frames++;
    fpsTimer += elapsed;
    if (fpsTimer >= 0.5) {
      fps = Math.round(frames / fpsTimer);
      frames = 0;
      fpsTimer = 0;

      const p = player.position;
      const speed = cycle.timeScale === 0 ? "paused" : `${cycle.timeScale}x`;
      const stance = player.mode === "fly"
        ? "fly"
        : player.inWater ? "вода"
        : (player.onGround ? "ground" : "air");
      hud.textContent =
        `${fps} fps\n` +
        `xyz  ${p.x.toFixed(1)}  ${p.y.toFixed(1)}  ${p.z.toFixed(1)}\n` +
        /*
         * The seed the world grew from.
         *
         * It is written into the save and nowhere else - the key it arrived
         * on is read once and cleared, so that a new кремль does not inherit
         * an adventure's country - which meant that the one question you
         * cannot answer about a world you are standing in is "which world is
         * this?". Anything sited from the seed rather than from where you
         * walked - the замок is the first of them - can only be found again
         * by somebody who knows this number.
         */
        `сід  ${world.opts.seed}\n` +
        (droneAloft
          ? `дрон  ${Math.floor(drone.charge / 60)}:` +
            `${String(Math.floor(drone.charge % 60)).padStart(2, "0")}  ` +
            `снарядів ${drone.load}  ` +
            `${drone.x.toFixed(0)} ${drone.y.toFixed(0)} ${drone.z.toFixed(0)}\n`
          : "") +
        `час  ${cycle.formatClock()}  ${cycle.phase}  (${speed})\n` +
        // What the light actually is where you are standing and where you are
        // looking. On screen rather than in a console because "the torch does
        // nothing" and "this cave is already full of daylight" look identical
        // from the inside, and only these two numbers tell them apart.
        `світло  тут ${world.light.sky(
          Math.floor(p.x), Math.floor(p.y), Math.floor(p.z),
        )}/${world.light.block(
          Math.floor(p.x), Math.floor(p.y), Math.floor(p.z),
        )}` + (() => {
          const lookingAt = aim();
          if (lookingAt === null) return "  (небо/смолоскип)\n";
          const lx = lookingAt.x + lookingAt.nx;
          const ly = lookingAt.y + lookingAt.ny;
          const lz = lookingAt.z + lookingAt.nz;
          return `  ціль ${world.light.sky(lx, ly, lz)}/` +
            `${world.light.block(lx, ly, lz)}  (небо/смолоскип)\n`;
        })() +
        `mode ${stance}  ${CAMERA_LABEL[renderer.cameraMode]}\n` +
        // Not "козак": you can be a селянка now, and a line that says
        // козак over a woman in a плахта is a line that is wrong.
        `хто ${skins.selected.name}\n` +
        /*
         * No clock online: the raid runs on the server, and the one here has
         * been stopped since we joined. A stopped clock read as a countdown
         * would be worse than no countdown.
         *
         * And no line at all on a map that never had lairs: "лігва
         * зруйновані" over a cube nobody ever defended is a sentence about a
         * war that did not happen.
         */
        (world.opts.kremlin === false
          ? ""
          : lairsLeft > 0
            ? `наліт ${net.online ? "(сервер)" : raid.format()}  ` +
              `лігва ${lairsLeft}  орків ${orcs.length}\n`
            : `лігва зруйновані  орків ${orcs.length}\n`) +
        // Counted here because a world with nothing alive in it looks exactly
        // like a world with everything alive in it until you go looking, and
        // for a while that is what this one was: the herd, the карасі and the
        // кремль's garrison were all put out before the terrain existed.
        `звірів ${beasts.length}  риби ${fish.length}  зміїв ${dragons.length}\n` +
        /*
         * And what the adventure has that no other map does.
         *
         * On screen rather than in a console for the same reason the light
         * numbers are: "there are no вовкулаки in this wood" and "вовкулаки
         * are never placed at all" look identical from the inside. It is
         * also what the browser check reads to know the country grew
         * something to live in it.
         */
        (world.opts.terrain === "adventure"
          ? `човнів ${boats.length}  ` +
            `вовкулак ${orcs.filter((o) => o.breed.kind === "vovkulaka").length}  ` +
            `мавок ${orcs.filter((o) => o.breed.kind === "mavka" || o.breed.kind === "mavkaNight").length}  ` +
            /*
             * And the замок's garrison, for the same reason as the rest of
             * this line: so a check can see it. `manTheCastle` was written,
             * tested and shipped while sitting in a branch this map never
             * takes, and nothing could tell - the замок is six hundred
             * blocks away and no check has ever walked there. A number on
             * the screen is a thing a browser can read from the doorstep.
             */
            `сторожі ${orcs.filter((o) =>
              CASTLE_FOES.includes(o.breed.kind)).length}\n`
          : "") +
        // The hill, and only the hill: on every other map there is nothing
        // to be king of and the line would be a puzzle.
        (world.opts.terrain === "hill"
          ? `${round.phase === "gather" ? "збір" : "до кінця"} ` +
            `${clock(Math.ceil(round.left))}  ` +
            `король ${crown.id === 0 ? "—" : crown.name}\n`
          : "") +
        `тримає ${describeHeld()}\n` +
        (performance.now() < saveNoticeUntil ? `${saveNotice}\n` : "") +
        `chunks ${renderer.stats.chunks}` +
        (renderer.stats.meshing > 0 ? `  (+${renderer.stats.meshing} meshing)` : "") + "\n" +
        `tris   ${renderer.stats.triangles.toLocaleString()}`;
    }
  }

  // A join that was refused on the way in: the panel opens where it was
  // pressed, with the reason standing on it. Last of all, once every panel
  // it touches exists - any earlier and it was a ReferenceError, twice.
  if (joinTrouble !== null) {
    showNetwork(true);
    network.openTrouble(joinTrouble);
  }
  requestAnimationFrame(frame);
}

void main();
